// src/pages/SchoolPaymentsPage.jsx

import React, { useEffect, useState, useContext, useCallback, useRef } from 'react';
import moment from 'moment';
import 'moment/locale/es'; // Importar locale español
import {
    Typography,
    Box,
    CircularProgress,
    Card,
    CardContent,
    Button,
    Menu,
    Paper,
    TablePagination,
    Grid,
    Chip,
    Snackbar,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Tabs,
    Tab,
    Switch,
    Tooltip,
    ToggleButton,
    ToggleButtonGroup,
    Checkbox,
    FormControlLabel
} from '@mui/material';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { IconButton, Collapse } from '@mui/material';
// Recharts (used by PaymentsManagement analysis)
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    ResponsiveContainer,
    Cell,
    Tooltip as RechartsTooltip,
    Legend
} from 'recharts';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowBack, School as SchoolIcon, CalendarToday, InfoOutlined, People, ChevronLeft, ChevronRight } from '@mui/icons-material';
import DownloadIcon from '@mui/icons-material/GetApp';
import { getCurrentDate, getCurrentDateSync } from '../hooks/useCurrentDate';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import styled from 'styled-components';
import tw from 'twin.macro';
import { AuthContext } from '../context/AuthProvider';
import api from '../utils/axiosConfig';
import useRegisterPageRefresh from '../hooks/useRegisterPageRefresh';
import PaymentFilters from '../components/PaymentFilters';
import PaymentTable from '../components/PaymentTable';
import ManagePaymentsModal from '../components/ManagePaymentsModal';
import ManagePeriodsModal from '../components/modals/ManagePeriodsModal';
import CreateSchoolPeriodModal from '../components/modals/CreateSchoolPeriodModal';
import CreditRefundModal from '../components/modals/CreditRefundModal';
import PermissionGuard from '../components/PermissionGuard';
import usePermissions from '../hooks/usePermissions';
import ExtraordinaryPaymentSection from '../components/ExtraordinaryPaymentSection';
import ReceiptsPane from '../components/ReceiptsPane';
import { getCicloEscolarYear } from '../services/cicloEscolarService';

moment.locale('es'); // Configurar moment en español
const PageContainer = styled.div`
    ${tw`bg-gray-50 min-h-screen w-full`}
    padding: 2rem;
    max-width: 1400px;
    margin: 0 auto;

    @media (max-width: 640px) {
        padding: 1rem;
    }
`;

const HeaderCard = styled(Card)`
    ${tw`mb-6 shadow-lg`}
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border-radius: 12px;
    padding: 8px 0;
`;

const SectionCard = styled(Card)`
    ${tw`mb-4`}
    border-radius: 10px;
    box-shadow: 0 1px 4px rgba(0,0,0,0.06);
`;

const ChipsRow = styled(Box)`
    ${tw`mb-3`}
    display: flex;
    gap: 12px;
    align-items: center;
    flex-wrap: wrap;
`;

// summary is rendered inline as a horizontal chip row

const getReceiptDisplayDateValue = (receipt) => receipt?.displayDate || receipt?.date || receipt?.createdAt || receipt?.uploadedAt || '';

// Formateadores de presentación: el backend ya calcula TODAS las métricas financieras.
// `null`/`undefined` => "N/A" (p. ej. divisiones por cero resueltas en el backend).
const formatMoneyOrNA = (value) => (value === null || value === undefined)
    ? 'N/A'
    : `Q ${Number(value).toLocaleString('es-GT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const formatPercentOrNA = (value) => (value === null || value === undefined)
    ? 'N/A'
    : `${Number(value).toFixed(1)}%`;

const ENROLLMENT_STATUS_LABEL = {
    PENDIENTE: { label: 'Pendiente', color: 'warning' },
    PARCIAL: { label: 'Parcial', color: 'info' },
    PAGADO: { label: 'Pagado', color: 'success' },
};

const SchoolPaymentsPage = () => {
    const { hasPermission, hasAnyPermission } = usePermissions();
    const puedeRecalcular = hasPermission('pagos-recalcular');
    useContext(AuthContext); // keep context hook for future auth-based features
    const navigate = useNavigate();
    const location = useLocation();
    const { cicloEscolarId: routeCicloEscolarId, schoolId } = useParams();

    const [school, setSchool] = useState(location.state?.school || null);
    const currentCicloEscolarId = routeCicloEscolarId || location.state?.cicloEscolarId || location.state?.school?.cicloEscolarId || school?.cicloEscolarId || '';
    const stateCycleYear = getCicloEscolarYear(location.state?.cicloEscolar) || getCicloEscolarYear(location.state?.school?.cicloEscolar) || getCicloEscolarYear(location.state?.school?.CicloEscolar);
    const schoolCycleYear = getCicloEscolarYear(school?.cicloEscolar) || getCicloEscolarYear(school?.CicloEscolar);
    const currentCycleYear = Number(stateCycleYear || schoolCycleYear || moment().year());
    const currentCycleLabel = stateCycleYear || schoolCycleYear || String(currentCycleYear || '');
    const buildPaymentParams = useCallback((extra = {}) => ({
        schoolId,
        ...(currentCicloEscolarId ? { cicloEscolarId: currentCicloEscolarId } : {}),
        ...extra
    }), [schoolId, currentCicloEscolarId]);
    const [allSchools, setAllSchools] = useState([]);
    // paymentsAll stores the full dataset fetched from server. filtered is the client-side filtered pageable set.
    const [paymentsAll, setPaymentsAll] = useState([]);
    // (paymentsAll is the complete dataset; avoid relying on deprecated global `payments` identifier)
    const [filtered, setFiltered] = useState([]);
    // Sorting (moved to parent so sorting applies to full filtered set before pagination)
    const [orderBy, setOrderBy] = useState('familyLastName');
    const [order, setOrder] = useState('asc');
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [autoDebitFilter, setAutoDebitFilter] = useState('');
    const [serviceStatusFilter, setServiceStatusFilter] = useState('');
    const [showInactive, setShowInactive] = useState(false);
    const [showDeleted, setShowDeleted] = useState(false);
    const [countersView, setCountersView] = useState('payment'); // 'payment' o 'service'
    useEffect(() => {
        if (statusFilter) setShowDeleted(false);
    }, [statusFilter]);
    useEffect(() => {
        if (serviceStatusFilter === 'INACTIVE') setShowInactive(false);
    }, [serviceStatusFilter]);
    // client-side pagination state (we fetch all data once and paginate locally)
    const [page, setPage] = useState(0); // UI page (0-based)
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [totalPayments, setTotalPayments] = useState(0);
    // Total families authoritative (includes active + inactive). Fetched independently
    // so it doesn't change when table filters/switches are toggled.
    const [totalFamiliesCount, setTotalFamiliesCount] = useState(null);
    const [totalPaidCount, setTotalPaidCount] = useState(0);
    const [totalMoraCount, setTotalMoraCount] = useState(0);
    const [totalPendingCount, setTotalPendingCount] = useState(0);
    const [totalInactiveCount, setTotalInactiveCount] = useState(0);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    // Loading indicator for initial data fetch
    const [loading, setLoading] = useState(true);
    // Analysis data (short version of PaymentsManagementPage analysis)
    const [analysisData, setAnalysisData] = useState(null);
    const [combinedEarnings, setCombinedEarnings] = useState([]);
    // Métricas financieras V2: el backend realiza TODOS los cálculos; el frontend sólo renderiza.
    const [metricsData, setMetricsData] = useState(null);
    const [metricsLoading, setMetricsLoading] = useState(false);
    const [metricsBasis, setMetricsBasis] = useState('caja'); // 'caja' | 'devengado'
    const [selectedPeriod, setSelectedPeriod] = useState(() => getCurrentDateSync().format('YYYY-MM'));
    const [todayPeriod, setTodayPeriod] = useState(() => getCurrentDateSync().format('YYYY-MM'));
    const didInitPeriodRef = useRef(false);
    // collapsed states: initially both collapsed
    const [collapsedAnalysis, setCollapsedAnalysis] = useState(true);
    const [collapsedExtra, setCollapsedExtra] = useState(true);

    // Indicators derived from analysisData/combinedEarnings
    // IMPORTANTE: Solo contar usuarios activos (state !== 0)
    // V2: Estados de pago son CONFIRMADO, ADELANTADO, PENDIENTE, PARCIAL, MORA
    // El estado INACTIVO es exclusivo del serviceStatus (estado del servicio), no del pago
    // Usar exclusivamente los totales globales que provee el backend.
    // Si el backend no provee estos totales, mostrar 0 (no caer a conteos locales).
    const serverPaymentTotals = analysisData?.paymentStateTotalsAll ?? null;
    const serverServiceTotals = analysisData?.serviceStatusTotalsAll ?? null;

    const eliminadoCount = serverPaymentTotals?.ELIMINADO ?? serverPaymentTotals?.DELETED ?? 0;

    // Total de familias (conteo operativo; las métricas financieras vienen del backend).

    // ====== Métricas financieras (calculadas 100% en el backend; aquí sólo se leen) ======
    // Regla del sistema: ninguna fórmula financiera vive en el frontend.
    const fin = metricsData?.metrics || null;
    // Selector Caja/Devengado para las métricas que exponen ambas bases.
    const finByBasis = (obj) => {
        if (!obj || typeof obj !== 'object') return null;
        return metricsBasis === 'devengado' ? obj.devengado : obj.caja;
    };
    const familiasActivas = fin ? fin.familiasActivas : null;

    // Contadores de estado del servicio (autoridad: backend)
    const serviceActiveCount = serverServiceTotals?.ACTIVE ?? 0;
    const servicePausedCount = serverServiceTotals?.PAUSED ?? 0;
    const serviceSuspendedCount = serverServiceTotals?.SUSPENDED ?? 0;
    const serviceInactiveCount = serverServiceTotals?.INACTIVE ?? 0;

    // Totales autoritativos que provee el backend (si faltan, mostrar 0)

    const displayPaid = serverPaymentTotals?.PAGADO ?? serverPaymentTotals?.CONFIRMADO ?? 0;
    const displayAdelantado = serverPaymentTotals?.ADELANTADO ?? 0;
    const displayParcial = serverPaymentTotals?.PARCIAL ?? 0;
    const displayPendiente = serverPaymentTotals?.PENDIENTE ?? 0;
    const displayMora = serverPaymentTotals?.MORA ?? 0;

    useEffect(() => {
        (async () => {
            setLoading(true);
            // Clear stale metrics when school changes
            setMetricsData(null);
            // If the current `school` is missing or does not match the requested `schoolId`, fetch it.
            const currentSchool = (!school || String(school.id) !== String(schoolId)) ? await fetchSchool() : school;
            // Colegio excluido del flujo de pagos: no se piden pagos, análisis ni débitos (el backend respondería 409).
            if (currentSchool?.paymentsEnabled === false) {
                setLoading(false);
                return;
            }
            // Avoid school-wide mora recalculation on page load. Opening the register modal
            // refreshes only the selected family's payment with /payments/:id/recalc.
            // Trigger auto-debits for this school as a fire-and-forget operation.
            // Exige pagos-procesar-debitos-automaticos: sin la llave no se dispara, porque antes daba un 403 silencioso en cada visita a la pantalla.
            if (hasPermission('pagos-procesar-debitos-automaticos')) {
                api.post('/payments/process-auto-debits', { schoolId, cicloEscolarId: currentCicloEscolarId || '' }).catch(err => {
                    console.error('Background auto-debits failed to start', err);
                });
            }
            // load all payments once (client-side pagination/filtering)
            await fetchAllPayments(statusFilter, search);
            // fetch analysis after payments loaded
            await fetchPaymentsAnalysis(schoolId);
            setLoading(false);
            // Nothing to load here: penalty is per-school and fetchSchool() populates it.
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [schoolId]);

    // keep analysis updated when paymentsAll change (best-effort)
    useEffect(() => {
        fetchPaymentsAnalysis(schoolId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paymentsAll]);

    // Re-fetch analysis totals when visibility toggles change so backend totals
    // reflect the same filtered population shown in the table.
    useEffect(() => {
        fetchPaymentsAnalysis(schoolId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showInactive, showDeleted]);

    // Métricas financieras: el backend hace todos los cálculos; el frontend sólo consume el endpoint.
    const fetchMetrics = useCallback(async (period) => {
        if (!schoolId || !currentCicloEscolarId || !period) return;
        setMetricsLoading(true);
        try {
            const res = await api.get('/payments/metrics', {
                params: { schoolId, cicloEscolarId: currentCicloEscolarId, period }
            });
            setMetricsData(res.data || null);
        } catch (err) {
            console.error('fetchMetrics error', err);
            setMetricsData(null);
        } finally {
            setMetricsLoading(false);
        }
    }, [schoolId, currentCicloEscolarId]);

    // Refetch metrics whenever the selected period changes (or school/cycle resolves).
    useEffect(() => {
        if (selectedPeriod && schoolId && currentCicloEscolarId) {
            fetchMetrics(selectedPeriod);
        } else {
            setMetricsData(null);
        }
    }, [selectedPeriod, fetchMetrics, schoolId, currentCicloEscolarId]);

    // Resolve the accurate "current month" (supports simulated dates) once on mount.
    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const d = await getCurrentDate();
                if (!active) return;
                const p = d.format('YYYY-MM');
                setTodayPeriod(p);
                if (!didInitPeriodRef.current) {
                    didInitPeriodRef.current = true;
                    setSelectedPeriod(p);
                }
            } catch (e) {
                console.debug('[metrics] no se pudo resolver la fecha actual; se usa el período por defecto', e);
            }
        })();
        return () => { active = false; };
    }, []);

    // Period navigation bounds: from the school year start month up to the current month.
    const minPeriod = school?.schoolYearStart ? moment(school.schoolYearStart).format('YYYY-MM') : null;
    const shiftSelectedPeriod = useCallback((delta) => {
        setSelectedPeriod((prev) => {
            const next = moment(`${prev}-01`).add(delta, 'month').format('YYYY-MM');
            if (minPeriod && next < minPeriod) return prev;
            if (next > todayPeriod) return prev;
            return next;
        });
    }, [minPeriod, todayPeriod]);

    const fetchSchool = async () => {
        try {
            const res = await api.get(`/schools/${schoolId}`);
            const fetched = res.data.school || null;
            setSchool(fetched);
            return fetched;
        } catch (err) {
            console.error('fetchSchool', err);
        }
    };

    const fetchAllSchools = useCallback(async () => {
        try {
            const res = await api.get('/schools', { params: { ...(currentCicloEscolarId ? { cicloEscolarId: currentCicloEscolarId } : {}), includeArchived: true } });
            const schools = res.data.schools || [];
            setAllSchools(schools);
            return schools;
        } catch (err) {
            console.error('fetchAllSchools', err);
            return [];
        }
    }, [currentCicloEscolarId]);

    // Fetch all payments for the school in one request and store locally.
    // Tries a single large request first and falls back to iterative paging if backend enforces pagination.
    const fetchAllPayments = async (status = '', q = '') => {
        if (!schoolId) return;
        try {
            const st = status ? String(status).toUpperCase().trim() : '';
            const qq = q ? String(q).trim() : '';
            const params = buildPaymentParams({ page: 1, limit: 10000 });

            // MORA y PENDIENTE son valores de finalStatus → filtrar en el servidor.
            // PAGADO (== CONFIRMADO) e INACTIVO (= serviceStatus INACTIVE)
            // se manejan solo en cliente cuando corresponde.
            const SERVER_FINAL_STATUS = new Set(['MORA', 'PENDIENTE', 'PARCIAL', 'CONFIRMADO', 'ADELANTADO', 'ELIMINADO']);
            if (st && SERVER_FINAL_STATUS.has(st)) params.finalStatus = st;
            // PAGADO e INACTIVO: no enviar filtro al servidor, el cliente filtra
            if (qq) params.search = qq;
            // include auto-debit filter if set: 'yes'|'no'
            if (autoDebitFilter === 'yes') params.autoDebit = true;
            if (autoDebitFilter === 'no') params.autoDebit = false;
            const res = await api.get('/payments', { params });
            let arr = res.data.payments || res.data.rows || [];
            let total = typeof res.data.totalCount === 'number'
                ? res.data.totalCount
                : (typeof res.data.count === 'number' ? res.data.count : (res.data.total || arr.length));

            // If the backend reported more records than returned, fetch iteratively
            if (total && Array.isArray(arr) && arr.length < total) {
                const per = Math.max(arr.length || 500, 500);
                const pages = Math.ceil(Number(total) / per);
                const all = [];
                for (let p = 0; p < pages; p++) {
                    const serverFilter = st && SERVER_FINAL_STATUS.has(st) ? { finalStatus: st } : {};
                    const r = await api.get('/payments', { params: buildPaymentParams({ page: p + 1, limit: per, ...serverFilter, ...(qq ? { search: qq } : {}) }) });
                    const part = r.data.payments || r.data.rows || [];
                    if (Array.isArray(part) && part.length > 0) all.push(...part);
                }
                if (all.length > 0) arr = all;
            }

            setPaymentsAll(arr);
            setFiltered(arr);
            setTotalPayments(total || (Array.isArray(arr) ? arr.length : 0));
            
            // IMPORTANTE: Excluir usuarios inactivos de los conteos de PAGADO/PENDIENTE/MORA
            const isUserActive = (pmt) => Number(pmt.User?.state) !== 0;
            const activePayments = arr.filter(isUserActive);
            const inactivePayments = arr.filter(pmt => !isUserActive(pmt));
            
            const paidPayments = activePayments.filter(pmt => ((pmt.finalStatus || '').toUpperCase() === 'CONFIRMADO'));
            setTotalPaidCount(typeof res.data.totalPaidCount === 'number' ? res.data.totalPaidCount : paidPayments.length);
            setTotalPendingCount(typeof res.data.totalPendingCount === 'number' ? res.data.totalPendingCount : activePayments.filter(pmt => (pmt.finalStatus||'').toUpperCase() === 'PENDIENTE').length);
            setTotalMoraCount(typeof res.data.totalMoraCount === 'number' ? res.data.totalMoraCount : activePayments.filter(pmt => (pmt.finalStatus||'').toUpperCase() === 'MORA').length);
            setTotalInactiveCount(typeof res.data.totalInactiveCount === 'number' ? res.data.totalInactiveCount : inactivePayments.length);
            // Do not reset pagination here; preserve current page.
            // Page resets are handled only when filters/search change.
        } catch (err) {
            console.error('Error cargando pagos:', err);
            if (err.response?.status === 403) {
                setSnackbar({ open: true, message: `Sin permisos: ${err.response?.data?.message || 'No autorizado'}`, severity: 'error' });
            } else {
            setSnackbar({ open: true, message: 'Error cargando pagos', severity: 'error' });
            }
        }
    };

    // Small analysis fetch: tries backend endpoint /payments/analysis or derives simple metrics
    const fetchPaymentsAnalysis = async (schId) => {
        if (!schId || !hasPermission('pagos-ver-analisis')) return;
        try {
            // Pedir al backend los totales respetando los toggles de la UI.
            // Si `showInactive` o `showDeleted` están activos, incluimos esas familias;
            // de lo contrario pedimos que el backend excluya INACTIVE/DELETED.
            const excludeInactiveParam = !(showInactive || showDeleted);
            const res = await api.get('/payments/analysis', { params: buildPaymentParams({ schoolId: schId, excludeInactive: excludeInactiveParam }) });
            // expected shape: { statusDistribution: [...], monthlyEarnings: [...] }
            const data = res.data || null;
            // Keep ELIMINADO in statusDistribution so the counter chip reads the correct value.
            // Only exclude it from totalPayments (active-family count).
            const originalDist = Array.isArray(data?.statusDistribution) ? data.statusDistribution : [];
            const eliminatedEntry = originalDist.find(s => (s.finalStatus || '').toUpperCase() === 'ELIMINADO');
            const eliminatedCount = eliminatedEntry?.count || 0;
            const sanitized = {
                ...data,
                statusDistribution: originalDist,
                totalPayments: Math.max(0, (data?.totalPayments || 0) - eliminatedCount)
            };
            setAnalysisData(sanitized);
            setCombinedEarnings(Array.isArray(data?.monthlyEarnings) ? data.monthlyEarnings : []);
        } catch (e) {
            // fallback: derive from current payments (best-effort)
            // IMPORTANTE: Separar familias con servicio inactivo (serviceStatus=INACTIVE)
            try {
                const isServiceInactive = (p) => (p.serviceStatus || p.User?.FamilyDetail?.serviceStatus) === 'INACTIVE';
                const activePayments = (paymentsAll || []).filter(p => !isServiceInactive(p));
                const inactivePayments = (paymentsAll || []).filter(isServiceInactive);
                
                // V2: Estados de pago son CONFIRMADO, ADELANTADO, PENDIENTE, PARCIAL, MORA
                // EN_PROCESO ya no es un finalStatus (ahora es "boleta pendiente de
                // registro"); en este fallback offline no hay datos de boletas
                // disponibles, así que no se puede derivar y se omite del cálculo local.
                const confirmado = activePayments.filter(p => (p.finalStatus||'').toUpperCase() === 'CONFIRMADO').length;
                const adelantado = activePayments.filter(p => (p.finalStatus||'').toUpperCase() === 'ADELANTADO').length;
                const mora = activePayments.filter(p => (p.finalStatus||'').toUpperCase() === 'MORA').length;
                const parcial = activePayments.filter(p => (p.finalStatus||'').toUpperCase() === 'PARCIAL').length;
                const pend = activePayments.filter(p => (p.finalStatus||'').toUpperCase() === 'PENDIENTE').length;
                const inactivo = inactivePayments.length;

                const derived = { statusDistribution: [
                    { finalStatus: 'CONFIRMADO', count: confirmado },
                    { finalStatus: 'ADELANTADO', count: adelantado },
                    { finalStatus: 'MORA', count: mora },
                    { finalStatus: 'PARCIAL', count: parcial },
                    { finalStatus: 'PENDIENTE', count: pend },
                    { finalStatus: 'INACTIVO', count: inactivo }
                ] };
                setAnalysisData(derived);
                // build a tiny combinedEarnings for last 6 months with zeros
                const now = moment();
                const months = [];
                for (let i = 5; i >= 0; i--) {
                    const m = moment(now).subtract(i, 'months');
                    months.push({ year: m.year(), month: m.month() + 1, total: 0 });
                }
                setCombinedEarnings(months);
            } catch (e2) {
                // ignore
            }
        }
    };

    // reference analysisData to avoid lint warnings when it's only set by fetch
    React.useEffect(() => {
        // noop: keep analysisData referenced for potential future effects
    }, [analysisData]);

    const fetchExtraordinaryEarnings = async () => {
        if (!hasPermission('pagos-ver-analisis-extraordinarios')) return [];
        try {
            const res = await api.get('/payments/extraordinary/analysis', { params: buildPaymentParams() });
            return res.data.monthlyEarnings || [];
        } catch (error) {
            console.error('fetchExtraordinaryEarnings error', error);
            return [];
        }
    };

    useEffect(() => {
        (async () => {
            const extra = await fetchExtraordinaryEarnings();
            const normal = analysisData?.monthlyEarnings || [];
            const combined = combineEarnings(normal, extra);
            setCombinedEarnings(combined);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [analysisData]);

    const combineEarnings = (normalEarnings, extraEarnings) => {
        const map = {};
        (normalEarnings || []).forEach(item => {
            const key = `${item.year}-${item.month}`;
            map[key] = (map[key] || 0) + (item.total || 0);
        });
        (extraEarnings || []).forEach(item => {
            const key = `${item.year}-${item.month}`;
            map[key] = (map[key] || 0) + (item.total || 0);
        });
        const combined = Object.keys(map).map(key => {
            const [year, month] = key.split('-').map(Number);
            return { year, month, total: map[key] };
        });
        combined.sort((a, b) => (a.year - b.year) || (a.month - b.month));
        return combined;
    };

    // Register global refresh after helper fetch functions are defined
    useRegisterPageRefresh(async () => {
        setLoading(true);
        try {
            await fetchAllPayments();
            await fetchPaymentsAnalysis(schoolId);
        } finally {
            setLoading(false);
        }
    }, [fetchAllPayments, fetchPaymentsAnalysis, schoolId]);

    const fetchTotalFamiliesCount = useCallback(async (/* schId unused */) => {
        try {
            if (!Array.isArray(paymentsAll) || paymentsAll.length === 0) {
                setTotalFamiliesCount(0);
                return;
            }
            const famMap = new Map();
            paymentsAll.forEach(p => {
                const fam = p?.User?.FamilyDetail || p?.FamilyDetail || null;
                const familyId = fam?.id || fam?.familyId || p?.familyDetailId || p?.familyId || p?.userId || p?.User?.id || null;
                if (!familyId) return;
                const serviceStatus = (fam?.serviceStatus || p?.serviceStatus || '').toString().toUpperCase();
                const familyDeletedFlag = fam?.deleted || fam?.isDeleted || !!fam?.deletedAt || !!p?.deleted || !!p?.deletedAt || false;
                const deleted = familyDeletedFlag || serviceStatus === 'DELETED' || serviceStatus === 'ELIMINADO';
                if (deleted) return;
                famMap.set(String(familyId), true);
            });
            setTotalFamiliesCount(famMap.size);
        } catch (err) {
            console.error('fetchTotalFamiliesCount error', err);
            setTotalFamiliesCount(0);
        }
    }, [paymentsAll]);

    useEffect(() => {
        if (schoolId) fetchTotalFamiliesCount(schoolId);
    }, [schoolId, fetchTotalFamiliesCount]);

    // Extraordinary payments are handled by the shared ExtraordinaryPaymentSection component

    // When using server-side filtering/pagination, request server for filtered pages.
    // `filtered` will mirror `payments` (the current page returned by server).
    // Reset to first page only when filter inputs change (not when data refreshes).
    useEffect(() => {
        setPage(0);
    }, [search, statusFilter, autoDebitFilter, showDeleted, showInactive, serviceStatusFilter]);

    useEffect(() => {
        // Apply client-side filtering of the already-fetched dataset
        try {
            const st = statusFilter ? String(statusFilter).toUpperCase().trim() : '';
            const qq = search ? String(search).toLowerCase().trim() : '';
            // Allow including inactive families when explicitly requested or when
            // filtering by payment states that the backend counts across all
            // service statuses (e.g., PAGADO). This makes the table filter
            // consistent with backend `paymentStateTotals` used for the chips.
            const allowInactive = !!showInactive || serviceStatusFilter === 'INACTIVE' || st === 'INACTIVO' || st === 'PAGADO';
            const allowDeleted = !!showDeleted || serviceStatusFilter === 'ELIMINADO';
            const arr = (paymentsAll || []).filter(p => {
                // Determinar si la familia tiene servicio inactivo (serviceStatus = INACTIVE)
                const isServiceInactive = (p.serviceStatus || p.User?.FamilyDetail?.serviceStatus) === 'INACTIVE';
                const isDeleted = !!p.User?.FamilyDetail?.deleted || !!p.User?.FamilyDetail?.isDeleted || !!p.User?.FamilyDetail?.deletedAt || !!p.deleted || !!p.deletedAt;

                // Por defecto, NO mostrar inactivas. Solo mostrarlas si el toggle está activo
                // o si el usuario filtró explícitamente por Estado del Servicio = INACTIVE.
                if (isServiceInactive && !allowInactive) return false;

                if (st) {
                    if (st === 'INACTIVO') {
                        // Filtrar solo familias con servicio inactivo
                        if (!isServiceInactive) return false;
                    } else if (st === 'ELIMINADO') {
                        // Filtrar solo eliminados
                        if (!isDeleted) return false;
                    } else {
                        // Para otros estados: filtrar por finalStatus.
                        // Excepción: cuando el usuario pidió `PAGADO`, incluir
                        // familias eliminadas/inactivas para coincidir con los
                        // totales autoritativos del backend (las chips).
                        if (st !== 'PAGADO' && isDeleted && !allowDeleted) return false;
                        const s = (p.finalStatus || '').toUpperCase();

                        // Interpretar `PAGADO` como `CONFIRMADO` solamente (ADELANTADO es separado)
                        if (st === 'PAGADO') {
                            if (s !== 'CONFIRMADO') return false;
                        } else {
                            if (s !== st) return false;
                        }
                    }
                } else {
                    // No se seleccionó estado: aplicar reglas por defecto
                    if (isDeleted && !allowDeleted) return false;

                    const s = (p.finalStatus || '').toUpperCase();
                    const defaultAllowed = ['CONFIRMADO', 'ADELANTADO', 'PENDIENTE', 'MORA', 'PARCIAL'];
                    if (!(defaultAllowed.includes(s) || (allowDeleted && s === 'ELIMINADO') || (allowInactive && isServiceInactive))) return false;
                }
                if (qq) {
                    const familyLast = (p.User?.FamilyDetail?.familyLastName || p.User?.familyLastName || '').toLowerCase();
                    if (!familyLast.includes(qq)) return false;
                }
                if (autoDebitFilter === 'yes') {
                    const auto = !!(p.automaticDebit || p.User?.FamilyDetail?.automaticDebit || p.User?.FamilyDetail?.autoDebit);
                    if (!auto) return false;
                } else if (autoDebitFilter === 'no') {
                    const auto = !!(p.automaticDebit || p.User?.FamilyDetail?.automaticDebit || p.User?.FamilyDetail?.autoDebit);
                    if (auto) return false;
                }
                // Filtrado por estado del servicio
                if (serviceStatusFilter) {
                    const paymentServiceStatusRaw = p.serviceStatus || p.User?.FamilyDetail?.serviceStatus || '';
                    const paymentServiceStatus = (paymentServiceStatusRaw || '').toString().toUpperCase();
                    const paymentIsDeleted = !!p.User?.FamilyDetail?.deleted || paymentServiceStatus === 'DELETED' || paymentServiceStatus === 'ELIMINADO';
                    if (serviceStatusFilter === 'ELIMINADO') {
                        if (!paymentIsDeleted) return false;
                    } else {
                        if (paymentServiceStatus !== serviceStatusFilter) return false;
                    }
                }
                return true;
            });
            setFiltered(arr);
            setTotalPayments(Array.isArray(arr) ? arr.length : 0);

            // Clamp current page if results shrink after refresh
            const total = Array.isArray(arr) ? arr.length : 0;
            const maxPage = Math.max(0, Math.ceil(total / Math.max(1, Number(rowsPerPage || 1))) - 1);
            setPage((prev) => Math.min(prev, maxPage));
        } catch (e) {
            console.error('filtering error', e);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, statusFilter, paymentsAll, autoDebitFilter, showDeleted, showInactive, serviceStatusFilter, rowsPerPage]);

    const handleBack = () => {
        if (!currentCicloEscolarId) {
            navigate(-1);
            return;
        }

        const cicloEscolar = school?.cicloEscolar || school?.CicloEscolar || location.state?.cicloEscolar || null;
        navigate(`/admin/escuelas/ciclo/${currentCicloEscolarId}/${schoolId}`, { state: { school, cicloEscolarId: currentCicloEscolarId, cicloEscolar } });
    };

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
        // reset to first page when sorting changes
        setPage(0);
    };

    // Client-side pagination: slice the filtered array for the current page
    // Sorting helpers (parent-level)
    const getValueForKey = (p, key) => {
        try {
            if (key === 'familyLastName') return (p.User?.FamilyDetail?.familyLastName || p.User?.familyLastName || '').toString().toLowerCase();
            if (key === 'students') return p.User?.FamilyDetail?.studentsCount || p.studentCount || 0;
            if (key === 'routeType') return (p.User?.FamilyDetail?.routeType || '').toString().toLowerCase();
            if (key === 'lastPayment') return p.lastPaymentDate || p.lastPaidDate || p.lastPayment || '';
            if (key === 'discount') {
                const fd = p.User?.FamilyDetail || {};
                const isPercent = !!(fd.discountIsPercent || p.discountIsPercent);
                const pctRaw = fd.specialFeePercentage ?? p.appliedFamilyDiscountPercent ?? null;
                if (isPercent && pctRaw !== null && typeof pctRaw !== 'undefined' && !Number.isNaN(Number(pctRaw))) {
                    const monthlyFee = Number(p.monthlyFee || 0);
                    return Math.round(monthlyFee * Number(pctRaw) * 100) / 100;
                }
                return Number(fd.specialFee ?? p.specialFee ?? 0) || 0;
            }
            if (key === 'invoice') return !!(p.User?.FamilyDetail?.requiresInvoice || p.requiresInvoice) ? 1 : 0;
            if (key === 'status') return (p.finalStatus || '').toString().toLowerCase();
            if (key === 'serviceStatus') return (p.serviceStatus || p.User?.FamilyDetail?.serviceStatus || '').toString().toLowerCase();
        } catch (e) {
            console.log('[SchoolPaymentsPage getValueKey]',e);
            return '';
        }
        return '';
    };

    const stableSort = (array, comparator) => {
        const stabilized = array.map((el, index) => [el, index]);
        stabilized.sort((a, b) => {
            const orderRes = comparator(a[0], b[0]);
            if (orderRes !== 0) return orderRes;
            return a[1] - b[1];
        });
        return stabilized.map(el => el[0]);
    };

    const sortedFiltered = React.useMemo(() => {
        if (!Array.isArray(filtered)) return [];
        const comparator = (a, b) => {
            const va = getValueForKey(a, orderBy);
            const vb = getValueForKey(b, orderBy);
            if (va < vb) return order === 'asc' ? -1 : 1;
            if (va > vb) return order === 'asc' ? 1 : -1;
            return 0;
        };
        return stableSort(filtered.slice(), comparator);
    }, [filtered, order, orderBy]);

    // Keep pagination count tied to the current filtered dataset (toggles included)
    const paginationCount = Array.isArray(sortedFiltered) ? sortedFiltered.length : 0;

    const pageSlice = React.useMemo(() => {
        const start = page * rowsPerPage;
        return (sortedFiltered || []).slice(start, start + rowsPerPage);
    }, [sortedFiltered, page, rowsPerPage]);

    // Dialog states
    const [openRegisterDialog, setOpenRegisterDialog] = useState(false);
    const [paymentTab, setPaymentTab] = useState(0); // 0 = Pago de Tarifa, 1 = Pago de Mora, 2 = Inscripción
    const [registerPaymentTarget, setRegisterPaymentTarget] = useState(null);
    const [registerAmount, setRegisterAmount] = useState('');
    const [registerPaymentExtra, setRegisterPaymentExtra] = useState({
        paymentDate: moment().format('YYYY-MM-DD'),
        numeroBoleta: '',
        extraordinaryDiscount: 0,
        bankAccountNumber: ''
    });
    // (extraordinary quick-register removed; use ExtraordinaryPaymentSection)

    // Payment histories loading flag (for receipts pane inside Registrar Pago dialog)
    const [regHistLoading, setRegHistLoading] = useState(false);
    const [regHistPage] = useState(0);
    const [regHistLimit] = useState(10);
    if (!global.__paymentHistCache) global.__paymentHistCache = new Map();
    const regHistCacheRef = React.useRef(global.__paymentHistCache);
    // Helper to invalidate all cached payment-history entries for a given userId
    const invalidatePaymentHistCacheForUser = useCallback((uid) => {
        try {
            if (!uid || !regHistCacheRef.current) return;
            const prefix = `${uid}:`;
            for (const k of Array.from(regHistCacheRef.current.keys())) {
                if (k.startsWith(prefix)) regHistCacheRef.current.delete(k);
            }
        } catch (e) {
            console.warn('Error invalidating payment history cache for user', uid, e);
        }
    }, []);

    // Helper to invalidate all cached payment-history entries for a given paymentId
    const invalidatePaymentHistCacheForPaymentId = useCallback((paymentId) => {
        try {
            if (!paymentId || !regHistCacheRef.current) return;
            const prefix = `${paymentId}:`;
            for (const k of Array.from(regHistCacheRef.current.keys())) {
                if (k.startsWith(prefix)) regHistCacheRef.current.delete(k);
            }
        } catch (e) {
            console.warn('Error invalidating payment history cache for paymentId', paymentId, e);
        }
    }, []);

    // Composite invalidation: some UI caches history by userId, other by paymentId
    const invalidatePaymentHistCache = useCallback((paymentLike) => {
        try {
            const uid = paymentLike?.User?.id || paymentLike?.userId || null;
            const pid = paymentLike?.id || paymentLike?.paymentId || null;
            if (uid) invalidatePaymentHistCacheForUser(uid);
            if (pid) invalidatePaymentHistCacheForPaymentId(pid);
        } catch (e) {
            console.warn('Error invalidating payment history cache', e);
        }
    }, [invalidatePaymentHistCacheForPaymentId, invalidatePaymentHistCacheForUser]);
    const [uploadedReceipts, setUploadedReceipts] = useState([]);
    const [uploadedReceiptsLoading, setUploadedReceiptsLoading] = useState(false);
    const [receiptUploadLoading, setReceiptUploadLoading] = useState(false);
    const [selectedReceipt, setSelectedReceipt] = useState(null);
    // "Marcar boleta abierta como registrada" al confirmar un pago. Desmarcado
    // por defecto: el admin decide explícitamente si la boleta que tiene
    // abierta corresponde al pago que está registrando.
    const [markReceiptAsRegistered, setMarkReceiptAsRegistered] = useState(false);
    useEffect(() => {
        setMarkReceiptAsRegistered(false);
    }, [selectedReceipt?.id]);
    const [receiptZoom, setReceiptZoom] = useState(1);
    // Month filter for boletas (format: YYYY-MM)
    const [boletaMonth, setBoletaMonth] = useState('');
    // Extra actions: create school-wide period
    const [openExtraAnchorEl, setOpenExtraAnchorEl] = useState(null);
    const openExtraMenu = Boolean(openExtraAnchorEl);
    const [openCreateSchoolPeriodModal, setOpenCreateSchoolPeriodModal] = useState(false);
    const [openCreditRefundModal, setOpenCreditRefundModal] = useState(false);

    // derive month options only from uploaded receipts (Boletas should show uploaded files only)
    const boletaMonthOptions = React.useMemo(() => {
        const setMonths = new Set();
        const pushDate = (d) => {
            if (!d) return;
            try {
                const m = moment.parseZone(d).format('YYYY-MM');
                setMonths.add(m);
            } catch (e) { /* ignore */ }
        };
        (uploadedReceipts || []).forEach(r => pushDate(getReceiptDisplayDateValue(r)));
        const arr = Array.from(setMonths).sort().reverse();
        return arr;
    }, [uploadedReceipts]);

    const filteredUploadedReceipts = React.useMemo(() => {
        if (!boletaMonth) return uploadedReceipts || [];
        return (uploadedReceipts || []).filter(r => {
            const d = getReceiptDisplayDateValue(r);
            if (!d) return false;
            return moment.parseZone(d).format('YYYY-MM') === boletaMonth;
        });
    }, [uploadedReceipts, boletaMonth]);

    const getRegisterReceiptUserId = useCallback(() => (
        registerPaymentTarget?.User?.id ||
        registerPaymentTarget?.user?.id ||
        registerPaymentTarget?.userId ||
        null
    ), [registerPaymentTarget]);

    const handleUploadRegisterReceipt = useCallback(async (file, displayDate) => {
        const userId = getRegisterReceiptUserId();
        if (!userId) {
            setSnackbar({ open: true, message: 'No se encontro la familia para subir la boleta', severity: 'error' });
            return;
        }

        if (!file || !['image/jpeg', 'image/png'].includes(file.type)) {
            setSnackbar({ open: true, message: 'Solo se permite subir una imagen JPG o PNG.', severity: 'warning' });
            return;
        }

        try {
            setReceiptUploadLoading(true);
            const form = new FormData();
            form.append('receipt', file);
            if (displayDate) form.append('displayDate', displayDate);
            if (registerPaymentTarget?.id) form.append('paymentId', registerPaymentTarget.id);
            if (registerPaymentTarget?.schoolId) form.append('schoolId', registerPaymentTarget.schoolId);
            if (registerPaymentTarget?.cicloEscolarId) form.append('cicloEscolarId', registerPaymentTarget.cicloEscolarId);
            if (registerPaymentTarget?.User?.FamilyDetail?.id) form.append('familyDetailId', registerPaymentTarget.User.FamilyDetail.id);

            const res = await api.post(`/parents/${userId}/receipts`, form, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            const receipt = res.data?.receipt;
            if (receipt) {
                setUploadedReceipts(prev => [receipt, ...(prev || []).filter(r => r.id !== receipt.id)]);
            } else {
                const receiptsRes = await api.get(`/parents/${userId}/receipts`);
                setUploadedReceipts(receiptsRes.data?.receipts || []);
            }

            setBoletaMonth('');
            setSnackbar({ open: true, message: 'Boleta subida correctamente', severity: 'success' });
        } catch (err) {
            console.error('Error subiendo boleta administrativa:', err);
            setSnackbar({ open: true, message: err.response?.data?.message || 'Error al subir la boleta', severity: 'error' });
        } finally {
            setReceiptUploadLoading(false);
        }
    }, [getRegisterReceiptUserId, registerPaymentTarget]);

    const handleChangeReceiptStatus = useCallback(async (receiptId, status, reason) => {
        try {
            const res = await api.patch(`/parents/receipts/${receiptId}/status`, { status, reason });
            const updated = res.data.receipt;
            setUploadedReceipts(prev => (prev || []).map(r => (r.id === receiptId ? updated : r)));
            setSelectedReceipt(prev => (prev && prev.id === receiptId ? updated : prev));
        } catch (err) {
            console.error('Error changing receipt status', receiptId, err);
            setSnackbar({ open: true, message: err.response?.data?.message || 'Error al cambiar el estado de la boleta', severity: 'error' });
        }
    }, []);

    const [openReceiptDialog, setOpenReceiptDialog] = useState(false);
    const [receiptTarget, setReceiptTarget] = useState(null);
    const [receiptNumberDraft, setReceiptNumberDraft] = useState('');
    
    // Pay Penalty dialog states
    const [openPayPenaltyDialog, setOpenPayPenaltyDialog] = useState(false);
    const [payPenaltyAmount, setPayPenaltyAmount] = useState('');
    const [payPenaltyBoleta, setPayPenaltyBoleta] = useState('');
    const [payPenaltyAccount, setPayPenaltyAccount] = useState('');
    const [payPenaltyDate, setPayPenaltyDate] = useState(moment().format('YYYY-MM-DD'));
    const [payPenaltyUseCredit, setPayPenaltyUseCredit] = useState(false);
    const [payPenaltyBoletaAmount, setPayPenaltyBoletaAmount] = useState('');

    // Exonerate/Discount Penalty dialog states
    const [openDiscountPenaltyDialog, setOpenDiscountPenaltyDialog] = useState(false);
    const [discountPenaltyAmount, setDiscountPenaltyAmount] = useState('');
    const [discountPenaltyType, setDiscountPenaltyType] = useState('DISCOUNT');
    const [discountPenaltyNotes, setDiscountPenaltyNotes] = useState('');

    // Tab de Inscripción de Ciclo (Tab Panel 2 del modal de registro de pago)
    const [enrollmentPayment, setEnrollmentPayment] = useState(null);
    const [payEnrollmentAmount, setPayEnrollmentAmount] = useState('');
    const [payEnrollmentDiscount, setPayEnrollmentDiscount] = useState('');
    const [payEnrollmentDate, setPayEnrollmentDate] = useState(moment().format('YYYY-MM-DD'));
    const [payEnrollmentReceiptNumber, setPayEnrollmentReceiptNumber] = useState('');
    const [payEnrollmentNotes, setPayEnrollmentNotes] = useState('');
    const [enrollmentSaving, setEnrollmentSaving] = useState(false);
    
    // Exonerate toggle
    const [isExonerating, setIsExonerating] = useState(false);
    const [exonerateAmount, setExonerateAmount] = useState('');
    
    // New: receipt preview flow triggered from notification
    const [previewReceipt, setPreviewReceipt] = useState(null); // object { id, fileUrl, userId }
    // guard to avoid processing the same openRegister/showReceipt query multiple times
    const processedOpenRegisterRef = useRef(new Set());

    // Notas (editor)
    const [openNotesDialog, setOpenNotesDialog] = useState(false);
    const [notesTarget, setNotesTarget] = useState(null);
    const [notesDraft, setNotesDraft] = useState('');

    const [openEmailDialog, setOpenEmailDialog] = useState(false);
    const [emailTarget, setEmailTarget] = useState(null);
    const [emailSubject, setEmailSubject] = useState('');
    const [emailMessage, setEmailMessage] = useState('');

    // Diálogo para auto débito
    const [openAutoDebitDialog, setOpenAutoDebitDialog] = useState(false);
    const [autoDebitPayload, setAutoDebitPayload] = useState(null);

    // guard to avoid concurrent loads / re-entrancy when opening the register dialog
    const openRegisterInProgressRef = useRef(false);
    const handleOpenRegister = useCallback((payment) => {
        // prevent concurrent opens for the same flow
        if (openRegisterInProgressRef.current) return;
        openRegisterInProgressRef.current = true;

        console.log('DEBUG handleOpenRegister received payment:', {
            id: payment?.id,
            userId: payment?.userId,
            penaltyDue: payment?.penaltyDue,
            penaltyDue_type: typeof payment?.penaltyDue,
            keys: payment ? Object.keys(payment).slice(0, 10) : []
        });
        
        setRegisterPaymentTarget(payment);
        // clear any previously selected receipt when opening for a new target
        setSelectedReceipt(null);
        setReceiptZoom(1);
        setRegisterAmount('');
        setRegisterPaymentExtra({
            paymentDate: moment().format('YYYY-MM-DD'),
            numeroBoleta: payment.receiptNumber || '',
            extraordinaryDiscount: 0,
            bankAccountNumber: payment.bankAccountNumber || (school ? school.bankAccount : '') || ''
        });
        
        // Inicializar campos de la pestaña de mora con los mismos valores por defecto
        setPayPenaltyDate(moment().format('YYYY-MM-DD'));
        setPayPenaltyBoleta(payment.receiptNumber || '');
        setPayPenaltyAccount(payment.bankAccountNumber || (school ? school.bankAccount : '') || '');
        setPayPenaltyAmount('');
        setPayPenaltyUseCredit(false);
        setPayPenaltyBoletaAmount('');
        setDiscountPenaltyAmount('');
        setIsExonerating(false);
        setExonerateAmount('');

        // Inicializar campos de la pestaña de Inscripción con los mismos valores por defecto
        setEnrollmentPayment(null);
        setPayEnrollmentAmount('');
        setPayEnrollmentDiscount('');
        setPayEnrollmentDate(moment().format('YYYY-MM-DD'));
        setPayEnrollmentReceiptNumber(payment.receiptNumber || '');
        setPayEnrollmentNotes('');

    // Do not open dialog immediately. We'll open it after initial loads complete so the UI shows data-ready state.

        // load uploaded receipts and histories for this user's family (payment.User.id)
        (async () => {
            try {
                const userId = payment?.User?.id;
                setUploadedReceiptsLoading(true);
                setRegHistLoading(true);
                if (!userId) {
                    // No userId: clear receipts and loading flags and exit
                    setUploadedReceipts([]);
                    setRegHistLoading(false);
                    setUploadedReceiptsLoading(false);
                    openRegisterInProgressRef.current = false;
                    return;
                }

                // ensure schools list is loaded only when opening register (needed for account select)
                let resolvedSchools = allSchools;
                if (!resolvedSchools || resolvedSchools.length === 0) {
                    try { resolvedSchools = await fetchAllSchools() || []; } catch (e) { resolvedSchools = []; }
                }

                // Re-set bankAccountNumber using the payment's actual school so that navigating
                // from a notification to a different school always picks the correct account.
                const paymentSchoolFromList = payment.schoolId ? resolvedSchools.find(s => s.id === payment.schoolId) : null;
                const correctBankAccount = payment.bankAccountNumber || paymentSchoolFromList?.bankAccount || (school ? school.bankAccount : '') || '';
                setRegisterPaymentExtra(prev => ({ ...prev, bankAccountNumber: correctBankAccount }));
                setPayPenaltyAccount(correctBankAccount);

                // CRÍTICO: Recalcular mora primero para asegurar datos actualizados
                let updatedPayment = payment;
                if (payment?.id) {
                    try {
                        if (puedeRecalcular) {
                            console.log('[handleOpenRegister] Recalculating payment before opening dialog...');
                            const recalcResponse = await api.post(`/payments/${payment.id}/recalc`);
                            console.log('[handleOpenRegister] Recalc response:', recalcResponse.data);
                        }
                        
                        // Obtener el payment actualizado desde el backend
                        const paymentResponse = await api.get(`/payments/${payment.id}`);
                        // El endpoint retorna { payment, currentPenalty, currentStatus, statusDetails }
                        updatedPayment = paymentResponse.data.payment;
                        console.log('[handleOpenRegister] Updated payment fetched:', updatedPayment);
                        
                        // Actualizar el target con los datos frescos
                        setRegisterPaymentTarget(updatedPayment);
                    } catch (e) {
                        console.error('[handleOpenRegister] Error recalculating/fetching payment:', e);
                        // Continue with original payment data if recalc fails
                    }
                }

                const cacheKey = `${userId}:${regHistPage}:${regHistLimit}`;
                const now = Date.now();
                const cached = regHistCacheRef.current.get(cacheKey);

                // build promises: receipts always requested, history only if not cached
                const receiptsPromise = api.get(`/parents/${userId}/receipts`).catch(e => ({ data: { receipts: [] } }));
                const histPromise = cached && (now - cached.ts) < 1000 * 60 * 5
                    ? Promise.resolve({ data: { histories: cached.data || [] }, __fromCache: true })
                    : api.get('/payments/paymenthistory', { params: { userId, page: regHistPage, limit: regHistLimit } }).catch(e => ({ data: { histories: [] } }));
                const cicloEscolarId = updatedPayment?.cicloEscolarId || payment?.cicloEscolarId || null;
                const enrollmentPromise = api.get(`/enrollment-payments/by-user/${userId}`, { params: cicloEscolarId ? { cicloEscolarId } : {} })
                    .catch(e => ({ data: { enrollmentPayment: null } }));

                const [receiptsRes, histRes, enrollmentRes] = await Promise.all([receiptsPromise, histPromise, enrollmentPromise]);

                // receipts
                setUploadedReceipts(receiptsRes.data?.receipts || []);
                setUploadedReceiptsLoading(false);

                // histories (we store in cache but don't keep them in component state currently)
                const histories = histRes.data?.histories || [];
                if (!histRes.__fromCache) regHistCacheRef.current.set(cacheKey, { ts: now, data: histories });
                setRegHistLoading(false);

                // inscripción de ciclo
                const ep = enrollmentRes.data?.enrollmentPayment || null;
                setEnrollmentPayment(ep);
                setPayEnrollmentAmount(ep ? String(ep.amountDue) : '');

                // open dialog now that initial data is ready
                setOpenRegisterDialog(true);
                // done
                openRegisterInProgressRef.current = false;
            } catch (err) {
                console.error('Error loading register histories', err);
                // ensure flags are cleared and modal still opens so user can retry / see error state
                setRegHistLoading(false);
                setUploadedReceiptsLoading(false);
                setUploadedReceipts([]);
                setOpenRegisterDialog(true);
                openRegisterInProgressRef.current = false;
            }
        })();
    }, [allSchools, fetchAllSchools, regHistPage, regHistLimit, school, puedeRecalcular]);

    // Handle query params after handleOpenRegister is defined
    useEffect(() => {
        const qs = new URLSearchParams(location.search);
        const openRegister = qs.get('openRegister') === 'true' || qs.get('openRegister') === '1';
        const userIdFromQuery = qs.get('userId');
        const receiptIdFromQuery = qs.get('receiptId');
        if (!openRegister || !userIdFromQuery || !schoolId) return;
        // avoid handling the same query repeatedly (some navigation flows re-emit location.search)
        const key = `openRegister:${location.search}`;
        if (processedOpenRegisterRef.current.has(key)) return;
        processedOpenRegisterRef.current.add(key);

        (async () => {
            try {
                // Load payments for this school (attempt larger limit to find the user's payment)
                const res = await api.get('/payments', { params: buildPaymentParams({ page: 1, limit: 1000 }) });
                const paymentsArr = res.data.payments || res.data.rows || [];
                const found = paymentsArr.find(p => String(p.User?.id) === String(userIdFromQuery) || String(p.userId) === String(userIdFromQuery));
                if (found) {
                    // open register dialog for this payment
                    handleOpenRegister(found);
                    // If a specific receiptId was provided, try to preload it into selectedReceipt
                    if (receiptIdFromQuery) {
                        try {
                            const recRes = await api.get(`/parents/${userIdFromQuery}/receipts`);
                            const recs = recRes.data.receipts || [];
                            const matched = recs.find(r => String(r.id) === String(receiptIdFromQuery));
                            if (matched) setSelectedReceipt(matched);
                        } catch (e) {
                            // ignore receipt preload errors
                        }
                    }
                }
            } catch (e) {
                console.error('Error handling openRegister query', e);
            }
        })();
    }, [location.search, schoolId, buildPaymentParams, handleOpenRegister]);

    // If navigation provided a payment object in location.state, open the register dialog immediately
    // NOTE: The payment from notification may not have full data. handleOpenRegister will fetch it from the query params path instead.
    useEffect(() => {
        const navPayment = location.state && location.state.payment ? location.state.payment : null;
        if (navPayment) {
            // Skip this effect if we also have openRegister query param - let the query param handler deal with it
            const qs = new URLSearchParams(location.search);
            const hasOpenRegisterParam = qs.get('openRegister') === 'true' || qs.get('openRegister') === '1';
            if (hasOpenRegisterParam) return;
            
            try {
                // directly open with provided payment object
                // avoid multiple opens when navigation state re-triggers
                const key = `navPayment:${location.search || navPayment?.id || navPayment?.User?.id || ''}`;
                if (!processedOpenRegisterRef.current.has(key)) {
                    processedOpenRegisterRef.current.add(key);
                    handleOpenRegister(navPayment);
                }
            } catch (e) {
                // ignore
            }
        }
    }, [location.state, location.search, handleOpenRegister]);

    // New: Show receipt preview when navigation/query indicates showReceipt
    useEffect(() => {
        const qs = new URLSearchParams(location.search);
        const showReceipt = qs.get('showReceipt') === 'true' || qs.get('showReceipt') === '1';
        const receiptId = qs.get('receiptId') || (location.state && location.state.receiptId) || null;
        const navPayment = location.state && location.state.payment ? location.state.payment : null;
        if (!showReceipt || !receiptId) return;

        (async () => {
            try {
                // fetch receipt info (fileUrl) for preview only
                const res = await api.get(`/parents/${navPayment?.User?.id || navPayment?.userId || qs.get('userId')}/receipts`);
                const recs = res.data.receipts || [];
                const found = recs.find(r => String(r.id) === String(receiptId));
                if (found) {
                    setPreviewReceipt(found);
                } else {
                    // No receipt found: clear any preview and do not auto-open Register
                    setPreviewReceipt(null);
                }
            } catch (e) {
                console.error('Error fetching receipt for preview', e);
                setPreviewReceipt(null);
            }
        })();
    }, [location.search, location.state, handleOpenRegister]);
    const handleConfirmRegister = async () => {
        if (!registerPaymentTarget) return;
        try {
            // Usar endpoint pay-tariff del nuevo sistema
            await api.post(`/payments/pay-tariff`, {
                paymentId: registerPaymentTarget.id,
                amount: registerAmount,
                realPaymentDate: registerPaymentExtra.paymentDate,
                receiptNumber: registerPaymentExtra.numeroBoleta,
                bankAccount: registerPaymentExtra.bankAccountNumber,
                extraordinaryDiscount: registerPaymentExtra.extraordinaryDiscount || 0,
                notes: registerPaymentExtra.extraordinaryDiscount > 0
                    ? `Descuento extraordinario: Q${registerPaymentExtra.extraordinaryDiscount}`
                    : undefined,
                ...(selectedReceipt?.status === 'PENDIENTE' ? {
                    receiptId: selectedReceipt.id,
                    markReceiptRegistered: markReceiptAsRegistered
                } : {})
            });
            
            setSnackbar({ open: true, message: 'Pago registrado exitosamente', severity: 'success' });
            setOpenRegisterDialog(false);
            
            // Invalidate cached payment histories (some caches are by userId, others by paymentId)
            invalidatePaymentHistCache(registerPaymentTarget);
            
            // refresh full dataset after mutation
            fetchAllPayments(statusFilter, search);
        } catch (err) {
            console.error(err);
            const errorMsg = err?.response?.data?.error || 'Error registrando pago';
            setSnackbar({ open: true, message: errorMsg, severity: 'error' });
        }
    };

    const handleOpenReceipt = useCallback(async (payment) => {
        setReceiptTarget(payment);
        setReceiptNumberDraft(payment.receiptNumber || '');
        setOpenReceiptDialog(true);
    }, []);
    const handleSaveReceipt = async () => {
        if (!receiptTarget) return;
        try {
            await api.put(`/payments/${receiptTarget.id}/receipt-number`, { receiptNumber: receiptNumberDraft });
            setSnackbar({ open: true, message: 'Número de recibo actualizado', severity: 'success' });
            setOpenReceiptDialog(false);
            // Invalidate cache for this user so modal will fetch fresh histories
            invalidatePaymentHistCacheForUser(receiptTarget?.User?.id || receiptTarget?.userId);
            fetchAllPayments(statusFilter, search);
        } catch (err) {
            console.error(err);
            setSnackbar({ open: true, message: 'Error actualizando recibo', severity: 'error' });
        }
    };

    const handleOpenEmail = useCallback((payment) => {
        setEmailTarget(payment);
        setEmailSubject('');
        setEmailMessage('');
        setOpenEmailDialog(true);
    }, []);
    const handleSendEmail = async () => {
        if (!emailTarget) return;
        try {
            await api.post(`/payments/${emailTarget.id}/sendEmail`, { subject: emailSubject, message: emailMessage });
            setSnackbar({ open: true, message: 'Correo enviado', severity: 'success' });
            setOpenEmailDialog(false);
        } catch (err) {
            console.error(err);
            setSnackbar({ open: true, message: 'Error al enviar correo', severity: 'error' });
        }
    };

    // Open Manage Payments modal for a given family/payment
    const [openManageModal, setOpenManageModal] = useState(false);
    const [manageTarget, setManageTarget] = useState(null);
    const handleManagePayments = useCallback(async (payment) => {
        setManageTarget(payment);
        setOpenManageModal(true);
    }, []);

    // Open Manage Periods modal for a given family/payment
    const [openPeriodsModal, setOpenPeriodsModal] = useState(false);
    const [periodsTarget, setPeriodsTarget] = useState(null);
    const handleManagePeriods = useCallback((payment) => {
        setPeriodsTarget(payment);
        setOpenPeriodsModal(true);
    }, []);

    // Download payment history as a presentable PDF (jsPDF + autotable)
    const handleDownloadHistory = useCallback(async (payment) => {
        try {
            const userId = payment?.User?.id || payment?.userId || null;
            if (!userId) {
                setSnackbar({ open: true, message: 'Usuario no encontrado para generar reporte', severity: 'error' });
                return;
            }

            // Intentar traer el detalle completo del pago (incluye `periods`) para construir la tabla por MES.
            let paymentDetail = null;
            let familyAccount = null;
            try {
                const detailRes = await api.get(`/payments/by-user/${userId}`, { params: payment?.id ? { paymentId: payment.id } : {} });
                paymentDetail = detailRes?.data?.payment || null;
                familyAccount = detailRes?.data?.familyAccount || null;

                if (!Array.isArray(familyAccount?.payments) || familyAccount.payments.length === 0) {
                    const accountRes = await api.get(`/payments/family-account/${userId}`);
                    familyAccount = accountRes?.data?.familyAccount || familyAccount;
                }
            } catch (e) {
                paymentDetail = null;
                familyAccount = null;
            }

            // Cargo de inscripción del ciclo (saldo independiente de la mensualidad)
            let enrollmentPaymentForPdf = null;
            try {
                const cicloEscolarId = payment?.cicloEscolarId || null;
                const enrollmentRes = await api.get(`/enrollment-payments/by-user/${userId}`, {
                    params: cicloEscolarId ? { cicloEscolarId } : {}
                });
                enrollmentPaymentForPdf = enrollmentRes?.data?.enrollmentPayment || null;
            } catch (e) {
                enrollmentPaymentForPdf = null;
            }

            const attemptFetch = async (params) => {
                try {
                    const r = await api.get('/payments/paymenthistory', { params });
                    return r;
                } catch (e) {
                    console.error('[handleDownloadHistory] fetch error', e && e.response ? e.response.data || e.response : e);
                    throw e;
                }
            };

            // Intentar obtener transacciones del endpoint V2
            let res;
            let transactions = [];
            let histories = []; // Declarar la variable histories
            let noTransactions = false;
            
            try {
                // Primero intentar con el endpoint V2 de transacciones
                res = await api.get(`/payments/${payment.id}/history`);
                transactions = res.data.transactions || [];
            } catch (txError) {
                console.warn('No se pudo obtener transacciones V2, intentando con historial antiguo:', txError);
                // Fallback al endpoint antiguo de historial
                res = await attemptFetch({ userId, page: 0, limit: 200 });
                histories = res.data.histories || res.data || [];
            }

            // Si obtuvimos transacciones V2, usar esas
            if (transactions.length > 0) {
                // Procesar transacciones V2
                // No necesitamos paginación iterativa ya que el endpoint devuelve todas
            } else {
                // Si no, usar el sistema antiguo de histories
                const totalReported = res?.data?.totalRecords ?? res?.data?.totalCount ?? res?.data?.count ?? null;

                if ((Array.isArray(histories) && histories.length === 0) && totalReported && Number(totalReported) > 0) {
                    try {
                        const per = 200;
                        const pages = Math.ceil(Number(totalReported) / per);
                        const all = [];
                        for (let p = 0; p < pages; p++) {
                            const r2 = await attemptFetch({ userId, page: p, limit: per });
                            const part = r2.data.histories || r2.data || [];
                            if (Array.isArray(part) && part.length > 0) all.push(...part);
                        }
                        if (all.length > 0) histories = all;
                    } catch (e) {
                        console.warn('[handleDownloadHistory] iterative fetch failed', e);
                    }
                }

                if (!histories || histories.length === 0) {
                    const paymentId = payment?.id || null;
                    if (paymentId) {
                        try {
                            const res2 = await attemptFetch({ paymentId, page: 0, limit: 1000 });
                            const fallbackHist = res2.data.histories || res2.data || [];
                                        if (Array.isArray(fallbackHist) && fallbackHist.length > 0) {
                                            histories = fallbackHist;
                                        } else {
                                            // No hay historial encontrado: continuar y generar PDF indicando ausencia de transacciones
                                            histories = [];
                                            noTransactions = true;
                                        }
                        } catch (e) {
                            console.error('[handleDownloadHistory] fallback fetch error', e && e.response ? e.response.data || e.response : e);
                            const status = e && e.response && e.response.status;
                            const respData = e && e.response && e.response.data;
                            // If API indicates not found or no content, treat as no transactions
                            if (status === 404 || status === 204 || (respData && typeof respData === 'string' && /not found|no history|no transactions/i.test(respData))) {
                                // Treat as no transactions and continue to generate a PDF with a placeholder
                                histories = [];
                                noTransactions = true;
                            } else {
                                // Otherwise, generic error when generating PDF
                                setSnackbar({ open: true, message: 'No es posible generar el PDF por un error consultando historial', severity: 'error' });
                                return;
                            }
                        }
                    } else {
                        // No paymentId available: treat as no transactions and continue
                        histories = [];
                        noTransactions = true;
                    }
                }
            }

            // Helper: fetch image and convert to dataURL
            const fetchImageDataUrl = async (url) => {
                try {
                    const resp = await fetch(url);
                    const blob = await resp.blob();
                    return await new Promise((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onloadend = () => resolve(reader.result);
                        reader.onerror = reject;
                        reader.readAsDataURL(blob);
                    });
                } catch (e) {
                    return null;
                }
            };

            // Build PDF
            const doc = new jsPDF({ unit: 'pt', format: 'a4' });
            const pageWidth = doc.internal.pageSize.width;
            const pageHeight = doc.internal.pageSize.height;

            const computeColumnWidths = (specWidths, availableWidth) => {
                const totalSpec = specWidths.reduce((s, v) => s + (Number(v) || 0), 0) || 1;
                const scaled = specWidths.map((w) => Math.floor((Number(w) || 0) * (availableWidth / totalSpec)));
                const sumScaled = scaled.reduce((s, v) => s + v, 0);
                if (sumScaled < availableWidth && scaled.length > 0) {
                    // add remainder to last column
                    scaled[scaled.length - 1] += (availableWidth - sumScaled);
                }
                return scaled;
            };

            // Colores
            const GREEN = [146, 208, 80];
            const GRID = [0, 0, 0];

            const familyPayment = payment || {};
            const periodsPayment = paymentDetail || payment || {};

            // Try to load logo from public folder
            const logoUrl = '/logo.png';
            const logoData = await fetchImageDataUrl(logoUrl);
            const headerTopY = 36;
            let logoDrawnH = 0;
            let logoDrawnW = 0;
            if (logoData) {
                const maxLogoW = 120;
                const maxLogoH = 80;
                try {
                    const props = doc.getImageProperties(logoData);
                    const imgW = Number(props?.width || 0) || 1;
                    const imgH = Number(props?.height || 0) || 1;
                    const scale = Math.min(maxLogoW / imgW, maxLogoH / imgH);
                    const drawW = imgW * scale;
                    const drawH = imgH * scale;
                    logoDrawnH = drawH;
                    logoDrawnW = drawW;
                    doc.addImage(logoData, props?.fileType || 'PNG', 40, headerTopY, drawW, drawH);
                } catch (e) {
                    // Fallback: draw without distortion by using a rectangular box closer to typical logo proportions
                    const fallbackW = 120;
                    const fallbackH = 60;
                    logoDrawnH = fallbackH;
                    logoDrawnW = fallbackW;
                    doc.addImage(logoData, 'PNG', 40, headerTopY, fallbackW, fallbackH);
                }
            }

            // Header center X: center text within the available space to the right of the logo
            const headerLeftX = 40;
            const headerRightX = pageWidth - 40;
            const headerContentLeftX = logoDrawnW > 0 ? (headerLeftX + logoDrawnW + 14) : headerLeftX;
            const headerCenterX = (headerContentLeftX + headerRightX) / 2;

            // Title centered
            doc.setFontSize(16);
            doc.setFont(undefined, 'bold');
            doc.text('Estado de cuenta', headerCenterX, headerTopY + 30, { align: 'center' });
            doc.setFontSize(9);
            doc.setFont(undefined, 'normal');
            doc.text(`Generado: ${moment().format('YYYY-MM-DD HH:mm')}`, headerCenterX, headerTopY + 46, { align: 'center' });

            // Summary block
            let cursorY = headerTopY + Math.max(logoDrawnH || 0, 60) + 20;

            const familyLastName = familyPayment?.User?.FamilyDetail?.familyLastName
                || familyPayment?.User?.familyLastName
                || familyPayment?.user?.FamilyDetail?.familyLastName
                || paymentDetail?.User?.FamilyDetail?.familyLastName
                || paymentDetail?.User?.familyLastName
                || paymentDetail?.user?.FamilyDetail?.familyLastName
                || '';

            const studentCount = Number(
                familyPayment?.User?.FamilyDetail?.studentsCount
                || familyPayment?.user?.FamilyDetail?.studentsCount
                || familyPayment?.studentCount
                || 0
            );

            // Prefer paymentDetail (by-user endpoint) which returns Students filtered to active only
            const studentsArr = Array.isArray(periodsPayment?.User?.FamilyDetail?.Students)
                ? periodsPayment.User.FamilyDetail.Students
                : (Array.isArray(periodsPayment?.user?.FamilyDetail?.Students)
                    ? periodsPayment.user.FamilyDetail.Students
                    : []);

            const routeType = familyPayment?.User?.FamilyDetail?.routeType
                || familyPayment?.user?.FamilyDetail?.routeType
                || '';

            // Tarifa / descuento: preferir valores del payment; fallback a primer período si existe
            const periodsRaw = Array.isArray(periodsPayment?.periods)
                ? periodsPayment.periods
                : [];

            const firstPeriod = periodsRaw && periodsRaw.length > 0 ? periodsRaw.slice().sort((a, b) => String(a?.period || '').localeCompare(String(b?.period || '')))[0] : null;

            const monthlyTariff = Number(periodsPayment?.monthlyFee ?? periodsPayment?.monthlyFeeAmount ?? firstPeriod?.originalAmount ?? 0) || 0;
            const familyDiscount = Number(
                familyPayment?.User?.FamilyDetail?.specialFee
                || familyPayment?.user?.FamilyDetail?.specialFee
                || periodsPayment?.specialDiscount
                || periodsPayment?.specialFee
                || firstPeriod?.discountApplied
                || 0
            ) || 0;

            const monthlyNet = Number(periodsPayment?.netMonthlyFee ?? (monthlyTariff - familyDiscount)) || 0;

            // Tarifa base (por estudiante) para el tipo de ruta.
            // Preferir derivarla desde el total mensual / cant. estudiantes; fallback a tarifas de School si están disponibles.
            const safeStudentCount = Math.max(1, Number(studentCount || 1));
            const schoolObj = familyPayment?.School || familyPayment?.school || periodsPayment?.School || periodsPayment?.school || null;
            const routeUpper = String(routeType || '').toUpperCase().trim();
            const schoolBaseFee = (() => {
                const complete = Number(schoolObj?.transportFeeComplete ?? 0) || 0;
                const half = Number(schoolObj?.transportFeeHalf ?? 0) || 0;
                if (!complete && !half) return 0;
                if (routeUpper.includes('COMPLETA') || routeUpper.includes('COMPLETO')) return complete;
                return half || complete;
            })();
            const baseTariff = (monthlyTariff > 0 && safeStudentCount > 0)
                ? (monthlyTariff / safeStudentCount)
                : (schoolBaseFee || 0);

            const rawServiceStatus = (
                familyPayment?.serviceStatus
                || familyPayment?.User?.FamilyDetail?.serviceStatus
                || familyPayment?.user?.FamilyDetail?.serviceStatus
                || ''
            ).toString().toUpperCase().trim();

            const serviceStatusLabel = {
                ACTIVE: 'ACTIVO',
                PAUSED: 'PAUSADO',
                SUSPENDED: 'SUSPENDIDO',
                INACTIVE: 'INACTIVO',
                DELETED: 'ELIMINADO'
            }[rawServiceStatus] || (rawServiceStatus || '-');

            const rawFinalStatus = (familyPayment?.finalStatus || '').toString().toUpperCase().trim();
            const paymentStatusLabel = rawFinalStatus === 'CONFIRMADO' ? 'PAGADO' : (rawFinalStatus || '-');

            // Left summary
            doc.setFontSize(11);
            doc.setFont(undefined, 'bold');
            doc.text('Apellidos de Familia:', 40, cursorY);
            doc.setFont(undefined, 'normal');
            doc.text(String(familyLastName || '-'), 160, cursorY);

            doc.setFont(undefined, 'bold');
            doc.text('Cant. Hijos:', 40, cursorY + 18);
            doc.setFont(undefined, 'normal');
            doc.text(String(studentCount || 0), 160, cursorY + 18);

            // Right summary as a table (distinct style; no title/header)
            const rightLabelX = 340;
            const rightStartY = cursorY;
            const familyPercentRaw = Number(familyPayment?.User?.FamilyDetail?.specialFeePercentage ?? familyPayment?.user?.FamilyDetail?.specialFeePercentage ?? null);
            const familyPercentFlag = !!(familyPayment?.User?.FamilyDetail?.discountIsPercent || familyPayment?.user?.FamilyDetail?.discountIsPercent);
            const discountPct = familyPercentFlag && familyPercentRaw ? Math.round(familyPercentRaw * 100) : (monthlyTariff > 0 && familyDiscount > 0 ? Math.round((familyDiscount / monthlyTariff) * 100) : 0);
            const discountLabel = discountPct > 0 ? `Descuento (${discountPct}%)` : 'Descuento:';
            const summaryRows = [
                ['Tipo de Ruta:', String(routeType || '-')],
                ['Tarifa base:', `Q${baseTariff.toFixed(2)}`],
                [`Tarifa (${studentCount || 0} ${Number(studentCount || 0) === 1 ? 'estudiante' : 'estudiantes'}):`, `Q${monthlyTariff.toFixed(2)}`],
                [discountLabel, `Q${familyDiscount.toFixed(2)}`],
                ['Cuota mensual:', `Q${monthlyNet.toFixed(2)}`],
                ['Estado de Pago:', String(paymentStatusLabel || '-')],
                ['Estado del Servicio:', String(serviceStatusLabel || '-')],
            ];

            let summaryTableFinalY = rightStartY;
            // compute available width for this mini-table
            const summaryAvailable = Math.floor(pageWidth - rightLabelX - 40);
            const summaryColWidths = computeColumnWidths([120, 95], summaryAvailable);
            autoTable(doc, {
                startY: rightStartY - 2,
                margin: { left: rightLabelX, right: 40 },
                body: summaryRows,
                showHead: 'never',
                theme: 'grid',
                styles: { fontSize: 10, textColor: 0, cellPadding: 3.2, lineColor: GRID, lineWidth: 0.8 },
                columnStyles: {
                    0: { fontStyle: 'bold', fillColor: [245, 245, 245], cellWidth: summaryColWidths[0] },
                    1: { halign: 'right', cellWidth: summaryColWidths[1] }
                }
            });
            summaryTableFinalY = (doc.lastAutoTable && typeof doc.lastAutoTable.finalY !== 'undefined')
                ? doc.lastAutoTable.finalY
                : summaryTableFinalY;

            // Children table (verde)
            const childTableBody = (studentsArr || []).map(s => {
                const full = (s.fullName || s.name || '').toString().trim() || '-';
                const grade = (s.grade || s.gradeName || s.level || '').toString().trim() || '-';
                return [full, grade];
            });

            const childTableStartY = Math.max(cursorY + 42);

            // child table: compute widths fitting between left/right margins
            const childAvailable = Math.floor(pageWidth - 40 - 40);
            const childColWidths = computeColumnWidths([130, 60], Math.min(200, childAvailable));
            autoTable(doc, {
                startY: childTableStartY,
                margin: { left: 40, right: 40 },
                tableWidth: Math.min(200, childAvailable),
                head: [
                    [{ content: 'HIJOS', colSpan: 2, styles: { halign: 'center', fontStyle: 'bold', fontSize: 10, textColor: 0, fillColor: GREEN, lineColor: GRID, lineWidth: 1 } }],
                    ['Nombre', 'Grado']
                ],
                body: childTableBody.length > 0 ? childTableBody : [['-', '-']],
                styles: { fontSize: 9, textColor: 0, cellPadding: 4, lineColor: GRID, lineWidth: 1 },
                headStyles: { fillColor: GREEN, textColor: 0, halign: 'center', lineColor: GRID, lineWidth: 1 },
                columnStyles: { 0: { cellWidth: childColWidths[0] }, 1: { cellWidth: childColWidths[1], halign: 'center' } },
                theme: 'grid',
            });

            const childrenTableFinalY = (doc.lastAutoTable && typeof doc.lastAutoTable.finalY !== 'undefined')
                ? doc.lastAutoTable.finalY
                : childTableStartY + 120;
            cursorY = Math.max(summaryTableFinalY, childrenTableFinalY) + 30;

            // Start the periods table below both summary + children tables

            // Helpers for new sections
            const monthNamesUpper = ['ENERO','FEBRERO','MARZO','ABRIL','MAYO','JUNIO','JULIO','AGOSTO','SEPTIEMBRE','OCTUBRE','NOVIEMBRE','DICIEMBRE'];
            const monthAbbrLower = ['ene','feb','mar','abr','may','jun','jul','ago','sep','oct','nov','dic'];

            const ensureSpace = (requiredHeight) => {
                const bottomMargin = 40;
                if (cursorY + requiredHeight > pageHeight - bottomMargin) {
                    doc.addPage();
                    cursorY = 52;
                }
            };

            const formatDateDMY = (d) => {
                if (!d) return 'N/A';
                const m = moment.parseZone(d);
                return m.isValid() ? m.format('DD/MM/YYYY') : 'N/A';
            };

            const moneyCard = (v) => {
                const num = Number(v || 0);
                return `Q ${num.toFixed(2)}`;
            };

            const money = (v) => {
                const num = Number(v || 0);
                return `Q ${num.toFixed(2)}`;
            };

            const monthNamesTitle = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
            const formatPeriodName = (period, periodName) => {
                // Prefer formatting from the `period` value (YYYY-MM). Fallback to periodName or raw.
                const value = String(period || '').trim();
                const match = /^(\d{4})-(\d{2})/.exec(value);
                if (match) {
                    const year = match[1];
                    const monthIndex = Number(match[2]) - 1;
                    const month = monthNamesTitle[monthIndex] || match[2];
                    return `${month} ${year}`;
                }
                const name = (periodName || '').toString().trim();
                return name || value || '-';
            };

            const resolveTotals = () => {
                const totals = familyAccount?.totals || null;
                if (totals) return totals;

                const summaryArr = Array.isArray(periodsPayment?.periodsSummary) ? periodsPayment.periodsSummary : [];
                const rawArr = Array.isArray(periodsPayment?.periods) ? periodsPayment.periods : [];
                const periodsForCalc = summaryArr.length > 0 ? summaryArr : rawArr;

                const balanceDue = (periodsForCalc || []).reduce((sum, p) => sum + (Number(p?.amountDue || 0) || 0), 0);
                const penaltyDue = (periodsForCalc || []).reduce((sum, p) => sum + (Number(p?.penaltyDue || 0) || 0), 0);
                const creditBalance = Number(periodsPayment?.creditBalance || payment?.creditBalance || 0) || 0;
                const unpaidPeriodsCount = (periodsForCalc || []).reduce((sum, p) => sum + ((Number(p?.amountDue || 0) || 0) > 0 ? 1 : 0), 0);

                return { balanceDue, penaltyDue, creditBalance, unpaidPeriodsCount };
            };

            const totals = resolveTotals();

            const drawStatusBadge = (xRight, y, status) => {
                const normalized = String(status || '').toUpperCase().trim() || 'PENDIENTE';
                const label = (normalized === 'CONFIRMADO' || normalized === 'PAGADO') ? 'PAGADO' : normalized;
                const padX = 10;
                const h = 16;
                doc.setFontSize(9);
                doc.setFont(undefined, 'bold');
                const textW = Number(doc.getTextWidth(label)) || 0;
                const safePadX = Number(padX) || 10;
                const w = Math.max(54, textW + safePadX * 2);
                const x = Number(xRight) - w;

                let fill = [230, 230, 230];
                let textColor = [0, 0, 0];
                if (label === 'MORA') {
                    fill = [244, 67, 54];
                    textColor = [255, 255, 255];
                } else if (label === 'PAGADO') {
                    fill = GREEN;
                    textColor = [0, 0, 0];
                }

                doc.setDrawColor(...GRID);
                doc.setLineWidth(0.8);
                doc.setFillColor(...fill);
                const rrX = Number.isFinite(Number(x)) ? Number(x) : 40;
                const rrY = Number.isFinite(Number(y - h + 3)) ? Number(y - h + 3) : Number(y);
                const rrW = Number.isFinite(Number(w)) && Number(w) > 0 ? Number(w) : 54;
                const rrH = Number.isFinite(Number(h)) && Number(h) > 0 ? Number(h) : 16;
                try {
                    doc.roundedRect(rrX, rrY, rrW, rrH, 6, 6, 'FD');
                } catch (e) {
                    doc.rect(rrX, rrY, rrW, rrH, 'FD');
                }
                doc.setTextColor(...textColor);
                const textY = rrY + rrH / 2 + 1;
                doc.text(label, x + w / 2, textY, { align: 'center', baseline: 'middle' });
                doc.setTextColor(0, 0, 0);
                doc.setFont(undefined, 'normal');
            };

            const drawSectionTitle = (title) => {
                ensureSpace(34);
                doc.setFontSize(12);
                doc.setFont(undefined, 'bold');
                doc.text(title, 40, cursorY);
                doc.setDrawColor(...GRID);
                doc.setLineWidth(0.8);
                const lx1 = 40;
                const lx2 = Number.isFinite(Number(pageWidth)) ? Number(pageWidth) - 40 : 555;
                doc.line(lx1, cursorY + 8, lx2, cursorY + 8);
                cursorY += 22;
            };

            // === Resumen financiero (como vista de padres) ===
            const preInsertCursor = cursorY;
            try {
                drawSectionTitle('Resumen financiero');
            const schoolName = (periodsPayment?.School?.name || periodsPayment?.school?.name || familyPayment?.School?.name || familyPayment?.school?.name || 'Transporte Luvan').toString();
            const cicloLabel = (periodsPayment?.CicloEscolar?.label || periodsPayment?.cicloEscolar?.label || periodsPayment?.CicloEscolar?.nombre || periodsPayment?.cicloEscolar?.nombre || '').toString();

            const computedStatus = (familyPayment?.finalStatus || (Number(totals?.penaltyDue || 0) > 0 ? 'MORA' : 'PENDIENTE'));
            ensureSpace(76);
            doc.setFontSize(11);
            doc.setFont(undefined, 'bold');
            doc.text(schoolName, 40, cursorY);
            doc.setFontSize(9);
            doc.setFont(undefined, 'normal');
            if (cicloLabel) {
                doc.text(String(cicloLabel), 40, cursorY + 14);
            }

            cursorY += cicloLabel ? 26 : 18;

            const metricCards = [
                { label: 'Tarifa pendiente', value: moneyCard(totals?.balanceDue || 0) },
                { label: 'Mora pendiente', value: moneyCard(totals?.penaltyDue || 0) },
                // Solo se agrega cuando existe cargo de inscripción, para no alterar el layout
                // de 4 tarjetas en colegios que no cobran inscripción.
                ...(enrollmentPaymentForPdf
                    ? [{ label: 'Inscripción pendiente', value: moneyCard(enrollmentPaymentForPdf.amountDue || 0) }]
                    : []),
                { label: 'Crédito a favor', value: moneyCard(totals?.creditBalance || 0) },
                { label: 'Períodos pendientes', value: String(totals?.unpaidPeriodsCount || 0) },
            ];

            const cardGap = 10;
            const cardsCount = metricCards.length;
            const cardW = Number.isFinite(Number(pageWidth))
                ? ((Number(pageWidth) - 80 - (cardGap * (cardsCount - 1))) / cardsCount)
                : ((595 - 80 - (cardGap * (cardsCount - 1))) / cardsCount);
            const cardH = 54;
            const cardY = cursorY;

            ensureSpace(cardH + 12);
            for (let i = 0; i < metricCards.length; i++) {
                const x = 40 + i * (cardW + cardGap);
                doc.setDrawColor(...GRID);
                doc.setLineWidth(0.8);
                doc.setFillColor(255, 255, 255);
                const sx = Number.isFinite(Number(x)) ? Number(x) : 40;
                const sW = Number.isFinite(Number(cardW)) && Number(cardW) > 0 ? Number(cardW) : 100;
                const sH = Number.isFinite(Number(cardH)) && Number(cardH) > 0 ? Number(cardH) : 40;
                try {
                    doc.roundedRect(sx, cardY, sW, sH, 8, 8, 'FD');
                } catch (e) {
                    doc.rect(sx, cardY, sW, sH, 'FD');
                }

                // Con 5 tarjetas el ancho baja (~95pt): reducir la etiqueta evita que se corte.
                doc.setFontSize(cardsCount > 4 ? 7.5 : 8.5);
                doc.setFont(undefined, 'normal');
                doc.setTextColor(90);
                doc.text(metricCards[i].label, x + 10, cardY + 16);
                doc.setTextColor(0, 0, 0);
                doc.setFontSize(cardsCount > 4 ? 11.5 : 13);
                doc.setFont(undefined, 'bold');
                doc.text(String(metricCards[i].value || '-'), x + 10, cardY + 38);
            }
            doc.setFont(undefined, 'normal');
            cursorY = cardY + cardH + 30;

            // Separator before the transaction history table
            doc.setDrawColor(220, 220, 220);
            doc.setLineWidth(0.6);
            doc.line(40, cursorY - 8, pageWidth - 40, cursorY - 8);
            cursorY += 10;
            } catch (pdfSectionError) {
                console.error('[handleDownloadHistory] Error rendering additional PDF sections:', pdfSectionError);
                // Revert to cursor before attempt so main table renders in expected place
                cursorY = preInsertCursor;
            }

            drawSectionTitle('Historial de Transacciones');
            const normalizePeriod = (p) => {
                const periodKey = (p?.period || '').toString().slice(0, 7);
                const originalAmount = Number(p?.originalAmount || p?.monthlyFee || 0) || 0;
                const discountApplied = Number(p?.discountApplied || p?.discount || 0) || 0;
                const netAmount = Number(p?.netAmount || (originalAmount - discountApplied)) || 0;
                const amountPaid = Number(p?.amountPaid || 0) || 0;
                const amountDue = Number(p?.amountDue || p?.amount || 0) || 0;
                const status = (p?.status || '').toString().toUpperCase().trim();
                return { period: periodKey, originalAmount, discountApplied, netAmount, amountPaid, amountDue, status };
            };

            const periods = (() => {
                if (Array.isArray(periodsRaw) && periodsRaw.length > 0) return periodsRaw.map(normalizePeriod);
                try {
                    const raw = periodsPayment?.unpaidPeriods
                        ? (typeof periodsPayment.unpaidPeriods === 'string'
                            ? JSON.parse(periodsPayment.unpaidPeriods)
                            : periodsPayment.unpaidPeriods)
                        : [];
                    return (Array.isArray(raw) ? raw : []).map(normalizePeriod);
                } catch (e) {
                    return [];
                }
            })();

            periods.sort((a, b) => String(a.period || '').localeCompare(String(b.period || '')));

            let noPeriods = false;
            if (!Array.isArray(periods) || periods.length === 0) {
                // No periods available: mark flag so we render a placeholder table instead of aborting
                noPeriods = true;
            }

            // Simular asignación de pagos a períodos para llenar FECHA/MONTO/BOLETA (best-effort)
            const txs = Array.isArray(transactions) ? transactions : [];
            const tariffLikeTx = txs
                .filter(t => {
                    const tt = String(t?.type || '').toUpperCase().trim();
                    return tt === 'TARIFA' || tt === 'CREDITO' || tt === 'CREDIT' || tt === 'PAYMENT';
                })
                .slice()
                .sort((a, b) => {
                    // Prefer `realPaymentDate` for sorting. Fallback to `registeredAt` then `createdAt` only if necessary.
                    const da = moment.parseZone(a?.realPaymentDate || a?.registeredAt || a?.createdAt || 0).valueOf();
                    const db = moment.parseZone(b?.realPaymentDate || b?.registeredAt || b?.createdAt || 0).valueOf();
                    // Descending (newest first)
                    return db - da;
                });

            const periodAlloc = periods.map(p => ({
                ...p,
                remainingDueSim: Math.max(0, Number(p.netAmount || 0)),
                appliedSim: 0,
                paidAt: null,
                receiptNumber: '',
                notes: ''
            }));

            for (const tx of tariffLikeTx) {
                let txRemaining = Math.max(0, Number(tx?.amount || 0));
                if (txRemaining <= 0) continue;

                while (txRemaining > 0) {
                    const idx = periodAlloc.findIndex(pp => Number(pp.remainingDueSim || 0) > 0);
                    if (idx === -1) break;

                    const due = Number(periodAlloc[idx].remainingDueSim || 0);
                    const apply = Math.min(txRemaining, due);
                    if (apply <= 0) break;

                    periodAlloc[idx].remainingDueSim = Math.max(0, due - apply);
                    periodAlloc[idx].appliedSim = Number(periodAlloc[idx].appliedSim || 0) + apply;

                    // Guardar el último comprobante/fecha asociado a este período
                    periodAlloc[idx].paidAt = tx?.realPaymentDate || tx?.createdAt || tx?.registeredAt || null;
                    periodAlloc[idx].receiptNumber = String(tx?.receiptNumber || '').trim();
                    periodAlloc[idx].notes = String(tx?.notes || '').trim();

                    txRemaining -= apply;
                }
            }

            const formatTransactionDate = (d) => {
                if (!d) return '';
                const m = moment.parseZone(d);
                if (!m.isValid()) return '';
                return m.locale('es').format('DD-MMM-YYYY').toLowerCase();
            };
            const formatTransactionType = (type) => {
                const t = String(type || '').trim().toUpperCase();
                if (t === 'TARIFA' || t === 'PAYMENT') return 'Tarifa';
                if (t === 'MORA' || t === 'PENALTY') return 'Mora';
                if (t === 'CREDITO' || t === 'CREDIT' || t === 'CREDITO AUTOMATICO' || t === 'AUTOCREDIT') return 'Crédito Automático';
                if (t === 'DEBITO' || t === 'DEBIT' || t === 'DEBITO AUTOMATICO' || t === 'AUTODEBIT') return 'Débito Automático';
                return (type || 'Transacción').toString().replace(/_/g, ' ').replace(/\b\w/g, ch => ch.toUpperCase());
            };

            let bodyRows = [];

            if (!noTransactions && !noPeriods) {
                // Sort transactions for display by `realPaymentDate` (desc). Fallback to `registeredAt` then `createdAt`.
                const sortedTxsForDisplay = (Array.isArray(transactions) ? transactions.slice().sort((a, b) => {
                    const da = moment.parseZone(a?.realPaymentDate || a?.registeredAt || a?.createdAt || 0).valueOf();
                    const db = moment.parseZone(b?.realPaymentDate || b?.registeredAt || b?.createdAt || 0).valueOf();
                    return db - da; // newest first
                }) : []);

                const txRows = sortedTxsForDisplay
                        .filter(tx => Number(tx?.amount || 0) !== 0 || String(tx?.receiptNumber || '').trim())
                        .map(tx => {
                            const amount = Number(tx?.amount || 0) || 0;
                            const date = formatTransactionDate(tx?.realPaymentDate || tx?.registeredAt || tx?.createdAt || tx?.paidAt);
                            const type = formatTransactionType(tx?.type || tx?.transactionType || 'Tarifa');
                            const discount = Number(tx?.extraordinaryDiscount || tx?.discountApplied || tx?.discount || 0) || 0;
                            const receipt = String(tx?.receiptNumber || tx?.boleta || tx?.reference || '').trim();
                            return [date, type, money(amount), money(discount), receipt || '-'];
                        });

                if (txRows.length > 0) {
                    bodyRows = txRows;
                } else {
                    bodyRows = periodAlloc.map(p => {
                        const fecha = formatTransactionDate(p.paidAt || p.dueDate || p.period);
                        const tarifaV = Number(p.originalAmount || 0);
                        const descV = Number(p.discountApplied || 0);
                        const montoRaw = Number(p.amountPaid || 0) > 0 ? Number(p.amountPaid || 0) : Number(p.appliedSim || 0);
                        const boleta = (p.receiptNumber) ? String(p.receiptNumber) : '-';
                        return [fecha || '-', 'Tarifa', money(montoRaw), money(descV), boleta];
                    });
                }
            } else if (noTransactions) {
                bodyRows = [[{ content: 'No hay transacciones registradas.', colSpan: 5, styles: { halign: 'center' } }]];
            } else {
                bodyRows = [[{ content: 'No se encontraron períodos para mostrar', colSpan: 5, styles: { halign: 'center' } }]];
            }

            // Main periods table: compute column widths to fit available space
            const periodsAvailable = Math.floor(pageWidth - 40 - 40);
            const periodsColSpec = [88, 92, 66, 78, 78];
            const periodsColWidths = computeColumnWidths(periodsColSpec, periodsAvailable);
            autoTable(doc, {
                startY: cursorY,
                margin: { left: 40, right: 40 },
                tableWidth: periodsAvailable,
                head: [[ 'FECHA', 'TIPO', 'MONTO', 'DESCUENTOS EXTRA.', 'N° BOLETA' ]],
                body: bodyRows,
                styles: { fontSize: 8.2, textColor: 0, cellPadding: 4, lineColor: GRID, lineWidth: 1, overflow: 'linebreak' },
                headStyles: { fillColor: GREEN, textColor: 0, halign: 'center', fontStyle: 'bold', lineColor: GRID, lineWidth: 1 },
                columnStyles: {
                    0: { halign: 'center', cellWidth: periodsColWidths[0] },
                    1: { halign: 'center', cellWidth: periodsColWidths[1] },
                    2: { halign: 'center', cellWidth: periodsColWidths[2] },
                    3: { halign: 'center', cellWidth: periodsColWidths[3] },
                    4: { halign: 'center', cellWidth: periodsColWidths[4] }
                },
                theme: 'grid',
                // no footer page numbers
            });

            const buildPaymentPeriods = (payment) => {
                const sources = [
                    payment?.periodsSummary,
                    payment?.periods,
                    payment?.PaymentPeriods,
                    payment?.unpaidPeriods,
                    payment?.penaltyPeriods,
                ];
                const byKey = new Map();

                sources.forEach((source) => {
                    if (!Array.isArray(source)) return;
                    source.forEach((period) => {
                        if (!period) return;
                        const key = period.id || period.period || `${period.dueDate || ''}-${period.amountDue || ''}-${period.amountPaid || ''}`;
                        if (!key) return;
                        const existing = byKey.get(key) || {};
                        byKey.set(key, {
                            ...existing,
                            ...period,
                            paymentId: period.paymentId || payment?.id,
                        });
                    });
                });

                return Array.from(byKey.values());
            };

            const calculatePenaltyDays = (period) => {
                if (period?.penaltyStartDate) {
                    const endDate = period.penaltyFrozen && period.penaltyFrozenAt
                        ? period.penaltyFrozenAt
                        : new Date().toISOString().split('T')[0];
                    const start = new Date(period.penaltyStartDate);
                    const end = new Date(endDate);
                    if (start.toString() !== 'Invalid Date' && end.toString() !== 'Invalid Date') {
                        const msPerDay = 1000 * 60 * 60 * 24;
                        const days = Math.max(0, Math.floor((end.getTime() - start.getTime()) / msPerDay));
                        return days > 0 ? days : null;
                    }
                }
                const dailyRate = Number(period?.penaltyAccrualBaseAmount || 0);
                const penaltyAmount = Number(period?.penaltyAmount || 0);
                if (dailyRate > 0 && penaltyAmount > 0) {
                    const days = Math.round(penaltyAmount / dailyRate);
                    return days > 0 ? days : null;
                }
                return null;
            };

            // DEBUG: mostrar fuentes antes de construir periodDetails
            try {
                console.debug('[PDF DEBUG] paymentDetail present:', !!paymentDetail, 'familyAccount.payments length:', Array.isArray(familyAccount?.payments) ? familyAccount.payments.length : 0);
                if (Array.isArray(familyAccount?.payments) && familyAccount.payments.length > 0) {
                    console.debug('[PDF DEBUG] familyAccount.payments sample:', familyAccount.payments.slice(0, 3).map(p => ({ id: p.id, periodsCount: (Array.isArray(p.periods) ? p.periods.length : 0) })));
                }
                if (paymentDetail) {
                    console.debug('[PDF DEBUG] paymentDetail sample:', { id: paymentDetail.id, periodsCount: (Array.isArray(paymentDetail.periods) ? paymentDetail.periods.length : 0) });
                }
            } catch (e) {
                console.warn('[PDF DEBUG] error while logging sources', e);
            }

            const periodSourcePayments = [
                paymentDetail,
                ...(Array.isArray(familyAccount?.payments) ? familyAccount.payments : []),
            ].filter(Boolean);

            const rawPeriodDetails = periodSourcePayments
                .flatMap((paymentItem) => buildPaymentPeriods(paymentItem))
                .filter(Boolean);

            // Normalize period key to YYYY-MM when possible and dedupe globally across payments
            const normalizePeriodKey = (p) => {
                if (!p) return null;
                const rawPeriod = String(p.period || '').trim();
                if (rawPeriod) {
                    const m = /^(\d{4})-(\d{2})/.exec(rawPeriod);
                    if (m) return `${m[1]}-${m[2]}`;
                    const tryMoment = moment(rawPeriod);
                    if (tryMoment.isValid()) return tryMoment.format('YYYY-MM');
                }

                const candidateDate = p.dueDate || p.paidAt || p.createdAt || p.paymentDate || '';
                if (candidateDate) {
                    const mm = moment(candidateDate);
                    if (mm.isValid()) return mm.format('YYYY-MM');
                }

                if (p.id) return `id:${p.id}`;
                // final fallback
                return `${p.dueDate || ''}-${p.amountDue || ''}`;
            };

            const mergedByKey = new Map();
            rawPeriodDetails.forEach((p) => {
                if (!p) return;
                const key = normalizePeriodKey(p);
                if (!key) return;
                const existing = mergedByKey.get(key) || {};
                mergedByKey.set(key, { ...existing, ...p });
            });

            const periodDetails = Array.from(mergedByKey.values())
                .filter(Boolean)
                .sort((a, b) => String(normalizePeriodKey(a) || '').localeCompare(String(normalizePeriodKey(b) || '')));

            console.debug('[PDF DEBUG] periodDetails length before sort/filter:', periodDetails.length);
            try {
                console.debug('[PDF DEBUG] periodDetails sample keys:', periodDetails.slice(0, 5).map(p => ({ id: p.id, period: p.period, dueDate: p.dueDate, amountDue: p.amountDue })));
            } catch (e) {
                console.warn('[PDF DEBUG] error while logging periodDetails', e);
            }

            if (periodDetails.length > 0) {
                try {
                    doc.addPage();
                    cursorY = 52;

                    drawSectionTitle('Detalles de Períodos de Pago');

                    for (const period of periodDetails) {
                        if (!period) continue;
                        ensureSpace(240);

                        const displayPeriodName = formatPeriodName(period.period, period.periodName);
                        const dueDate = period?.dueDate || '';
                        const periodRouteType = String(period?.routeType || routeType || 'N/A');
                        const studentsCountValue = Number(period?.studentsCount || studentsArr?.length || studentCount || 1) || 1;
                        const originalAmount = Number(period?.originalAmount || period?.monthlyFee || 0) || 0;
                        const discountApplied = Number(period?.discountApplied || 0) || 0;
                        const extraDiscount = Number(period?.extraordinaryTariffDiscount || 0) || 0;
                        const netAmount = Number(period?.netAmount ?? (originalAmount - discountApplied - extraDiscount)) || 0;
                        const amountPaid = Number(period?.amountPaid ?? period?.paidAmount ?? 0) || 0;
                        const amountDue = Number(period?.amountDue ?? 0) || 0;
                        const penaltyDue = Number(period?.penaltyDue || 0) || 0;
                        const penaltyAmount = Number(period?.penaltyAmount || 0) || 0;
                        const penaltyPaid = Number(period?.penaltyPaid || 0) || 0;
                        const penaltyDiscountApplied = Number(period?.penaltyDiscountApplied || 0) || 0;
                        const penaltyAccrualBaseAmount = Number(period?.penaltyAccrualBaseAmount || 0) || 0;
                        const penaltyDays = calculatePenaltyDays(period);
                        const status = String(period?.status || 'PENDIENTE').toUpperCase();
                        const hasMora = penaltyDue > 0 || penaltyAmount > 0 || !!period?.penaltyFrozen || !!period?.penaltyCleared;
                        const effectiveStatus = hasMora ? 'MORA' : status;
                        const totalPending = amountDue + penaltyDue;

                        const headerY = Number(cursorY) || 52;
                        const headerH = 28;
                        const headerLineY = headerY + 14;
                        doc.setFillColor(245, 245, 245);
                        doc.setDrawColor(200, 200, 200);
                        doc.setLineWidth(0.8);
                        doc.rect(40, headerY, pageWidth - 80, headerH, 'FD');

                        doc.setFont(undefined, 'bold');
                        doc.setFontSize(11);
                        doc.setTextColor(0, 0, 0);
                        doc.text(displayPeriodName, pageWidth / 2, headerLineY + 5, { align: 'center' });
                        doc.setFont(undefined, 'normal');
                        doc.setFontSize(8);
                        doc.setTextColor(100);
                        doc.text(`Vencimiento: ${formatDateDMY(dueDate)}`, 52, headerLineY + 3);
                        drawStatusBadge(pageWidth - 50, headerLineY + 6, effectiveStatus);
                        doc.setTextColor(0, 0, 0);

                        cursorY = headerY + headerH + 10;
                        doc.setDrawColor(225, 225, 225);
                        doc.setLineWidth(0.6);
                        doc.line(40, cursorY, pageWidth - 40, cursorY);
                        cursorY += 12;

                        doc.setFont(undefined, 'bold');
                        doc.setFontSize(9);
                        doc.text('SERVICIO', 50, cursorY);
                        cursorY += 15;
                        doc.setFont(undefined, 'normal');
                        doc.setFontSize(8);
                        doc.setTextColor(80);
                        doc.text('Tipo de ruta:', 50, cursorY);
                        doc.text(periodRouteType, pageWidth - 50, cursorY, { align: 'right' });
                        cursorY += 12;
                        doc.text('Estudiantes:', 50, cursorY);
                        doc.text(String(studentsCountValue), pageWidth - 50, cursorY, { align: 'right' });
                        cursorY += 12;
                        doc.setDrawColor(230, 230, 230);
                        doc.setLineWidth(0.6);
                        doc.line(42, cursorY - 4, pageWidth - 42, cursorY - 4);
                        cursorY += 12;

                        doc.setFont(undefined, 'bold');
                        doc.setFontSize(9);
                        doc.setTextColor(0, 0, 0);
                        doc.text('TARIFA', 50, cursorY);
                        cursorY += 15;
                        doc.setFont(undefined, 'normal');
                        doc.setFontSize(8);
                        doc.setTextColor(80);
                        doc.text('Tarifa base:', 50, cursorY);
                        doc.text(money(originalAmount), pageWidth - 50, cursorY, { align: 'right' });
                        cursorY += 12;
                        if (discountApplied > 0) {
                            doc.text('Descuento familiar:', 50, cursorY);
                            doc.setTextColor(21, 128, 61);
                            doc.text(`- ${money(discountApplied)}`, pageWidth - 50, cursorY, { align: 'right' });
                            cursorY += 12;
                        }
                        if (extraDiscount > 0) {
                            doc.setTextColor(80);
                            doc.text('Descuento extraordinario:', 50, cursorY);
                            doc.setTextColor(21, 128, 61);
                            doc.text(`- ${money(extraDiscount)}`, pageWidth - 50, cursorY, { align: 'right' });
                            cursorY += 12;
                        }
                        doc.setTextColor(80);
                        doc.text('Tarifa neta:', 50, cursorY);
                        doc.setTextColor(0, 0, 0);
                        doc.text(money(netAmount), pageWidth - 50, cursorY, { align: 'right' });
                        cursorY += 12;
                        doc.setTextColor(80);
                        doc.text('Pagado:', 50, cursorY);
                        doc.setTextColor(21, 128, 61);
                        doc.text(money(amountPaid), pageWidth - 50, cursorY, { align: 'right' });
                        cursorY += 12;
                        doc.setDrawColor(230, 230, 230);
                        doc.setLineWidth(0.6);
                        doc.line(42, cursorY - 4, pageWidth - 42, cursorY - 4);
                        cursorY += 12;

                        if (hasMora) {
                            doc.setFont(undefined, 'bold');
                            doc.setFontSize(9);
                            doc.setTextColor(0, 0, 0);
                            doc.text('MORA', 50, cursorY);
                            cursorY += 15;
                            doc.setFont(undefined, 'normal');
                            doc.setFontSize(8);
                            doc.setTextColor(80);
                            if (penaltyAccrualBaseAmount > 0) {
                                doc.text('Mora por día:', 50, cursorY);
                                doc.text(money(penaltyAccrualBaseAmount), pageWidth - 50, cursorY, { align: 'right' });
                                cursorY += 12;
                            }
                            if (penaltyDays !== null) {
                                doc.text('Días de mora:', 50, cursorY);
                                doc.text(`${penaltyDays} ${penaltyDays === 1 ? 'día' : 'días'}`, pageWidth - 50, cursorY, { align: 'right' });
                                cursorY += 12;
                            }
                            if (penaltyAmount > 0) {
                                doc.text('Mora acumulada:', 50, cursorY);
                                doc.text(money(penaltyAmount), pageWidth - 50, cursorY, { align: 'right' });
                                cursorY += 12;
                            }
                            if (penaltyDiscountApplied > 0) {
                                doc.text('Descuento extraordinario:', 50, cursorY);
                                doc.setTextColor(21, 128, 61);
                                doc.text(`- ${money(penaltyDiscountApplied)}`, pageWidth - 50, cursorY, { align: 'right' });
                                cursorY += 12;
                            }
                            if (penaltyPaid > 0) {
                                doc.setTextColor(80);
                                doc.text('Mora pagada:', 50, cursorY);
                                doc.setTextColor(21, 128, 61);
                                doc.text(money(penaltyPaid), pageWidth - 50, cursorY, { align: 'right' });
                                cursorY += 12;
                            }
                            doc.setTextColor(80);
                            doc.text('Mora pendiente:', 50, cursorY);
                            if (penaltyDue > 0) doc.setTextColor(244, 67, 54);
                            else doc.setTextColor(0, 0, 0);
                            doc.text(money(penaltyDue), pageWidth - 50, cursorY, { align: 'right' });
                            cursorY += 12;
                            if (period?.penaltyFrozen) {
                                doc.setTextColor(0, 0, 0);
                                doc.text('Mora congelada', 50, cursorY);
                                cursorY += 12;
                            }
                            if (period?.penaltyCleared) {
                                doc.setTextColor(0, 0, 0);
                                doc.text('Mora exonerada', 50, cursorY);
                                cursorY += 12;
                            }
                            doc.setDrawColor(230, 230, 230);
                            doc.setLineWidth(0.6);
                            doc.line(42, cursorY - 4, pageWidth - 42, cursorY - 4);
                            cursorY += 12;
                        }

                        doc.setFont(undefined, 'bold');
                        doc.setFontSize(9);
                        doc.setTextColor(0, 0, 0);
                        doc.text('RESUMEN', 50, cursorY);
                        cursorY += 15;
                        doc.setFont(undefined, 'normal');
                        doc.setFontSize(8);
                        doc.setTextColor(80);
                        doc.text('Tarifa pendiente:', 50, cursorY);
                        if (amountDue > 0) doc.setTextColor(244, 67, 54);
                        else doc.setTextColor(21, 128, 61);
                        doc.text(money(amountDue), pageWidth - 50, cursorY, { align: 'right' });
                        cursorY += 12;
                        if (penaltyDue > 0) {
                            doc.text('Mora pendiente:', 50, cursorY);
                            doc.setTextColor(244, 67, 54);
                            doc.text(money(penaltyDue), pageWidth - 50, cursorY, { align: 'right' });
                            cursorY += 12;
                        }
                        doc.setFont(undefined, 'bold');
                        if (totalPending > 0) doc.setTextColor(244, 67, 54);
                        else doc.setTextColor(21, 128, 61);
                        doc.text('Total a pagar:', 50, cursorY);
                        doc.text(money(totalPending), pageWidth - 50, cursorY, { align: 'right' });
                        cursorY += 12;
                        doc.setDrawColor(225, 225, 225);
                        doc.setLineWidth(0.6);
                        doc.line(40, cursorY + 2, pageWidth - 40, cursorY + 2);
                        cursorY += 8;
                    }
                } catch (err) {
                    console.error('[handleDownloadHistory] Error rendering period details:', err);
                }
            }


            // finalize and save
            const familyForName = (familyLastName && String(familyLastName).trim())
                ? String(familyLastName).replace(/\s+/g, '_')
                : 'Familia';
            const fileName = `Reporte Historial de Pagos Familia ${familyForName}.pdf`;
            doc.save(fileName);
            setSnackbar({ open: true, message: 'Descarga iniciada', severity: 'success' });
        } catch (err) {
            console.error('handleDownloadHistory', err);
            setSnackbar({ open: true, message: 'Error descargando reporte PDF', severity: 'error' });
        }
    }, []);

    // Notes handlers
    const handleOpenNotes = useCallback(async (payment) => {
        setNotesTarget(payment);
        setNotesDraft(payment?.notes || '');
        setOpenNotesDialog(true);
    }, []);
    const handleCloseNotes = () => {
        setOpenNotesDialog(false);
        setNotesTarget(null);
        setNotesDraft('');
    };
    const handleSaveNotes = async () => {
        if (!notesTarget) return;
        try {
            await api.put(`/payments/v2/${notesTarget.id}/notes`, { notes: notesDraft });
            setSnackbar({ open: true, message: 'Notas actualizadas', severity: 'success' });
            handleCloseNotes();
            // Invalidate cache for this user so modal will fetch fresh histories
            invalidatePaymentHistCacheForUser(notesTarget?.User?.id || notesTarget?.userId);
            fetchAllPayments(statusFilter, search);
        } catch (err) {
            console.error('Error actualizando notas', err);
            setSnackbar({ open: true, message: 'Error actualizando notas', severity: 'error' });
        }
    };

    const handleManageAction = async (actionName, payload) => {
        // Implement common management actions using available backend endpoints
        const payment = payload && payload.payment ? payload.payment : payload || manageTarget;

        try {
            if (!payment || !payment.id) {
                setSnackbar({ open: true, message: 'Pago no válido', severity: 'error' });
                return;
            }

            if (actionName === 'exoneratePenalty') {
                const amount = payload?.exonerateAmount ?? payload?.amount ?? null;
                if (!amount || Number.isNaN(amount) || amount <= 0) {
                    setSnackbar({ open: true, message: 'Monto inválido para exonerar', severity: 'error' });
                    return;
                }
                await api.post(`/payments/${payment.id}/exonerate-penalty`, { discountAmount: amount, type: 'EXONERATION' });
                setSnackbar({ open: true, message: 'Mora exonerada', severity: 'success' });
            } else if (actionName === 'freezePenalty') {
                // Congelar mora con fecha específica del backend (simulada o real)
                let freezeDate;
                if (payload?.freezeDate) {
                    freezeDate = payload.freezeDate;
                } else {
                    // Obtener fecha actual del backend
                    const currentDate = await getCurrentDate();
                    freezeDate = currentDate.format('YYYY-MM-DD');
                }
                console.log('[FREEZE_PENALTY] Sending request:', { paymentId: payment.id, freezeDate });
                const response = await api.post('/payments/penalties/freeze', { 
                    paymentId: payment.id,
                    freezeDate,
                    notes: `Mora congelada manualmente el ${moment(freezeDate).format('DD/MM/YYYY')}`
                });
                console.log('[FREEZE_PENALTY] Response received:', response.data);
                setSnackbar({ open: true, message: 'Mora congelada', severity: 'success' });
            } else if (actionName === 'unfreezePenalty') {
                // Descongelar mora (reanudar acumulación)
                await api.post('/payments/penalties/unfreeze', { 
                    paymentId: payment.id,
                    notes: 'Mora reanudada manualmente'
                });
                setSnackbar({ open: true, message: 'Mora reanudada', severity: 'success' });
            } else if (actionName === 'freezeGlobalPenalty') {
                const reason = String(payload?.reason || '').trim();
                if (!reason) {
                    setSnackbar({ open: true, message: 'La razón es requerida para congelar mora global', severity: 'error' });
                    return;
                }

                await api.post('/payments/penalties/freeze-global', {
                    paymentId: payment.id,
                    freezeDate: payload?.freezeDate,
                    reason,
                    notes: payload?.notes || reason
                });
                setSnackbar({ open: true, message: 'Mora global congelada', severity: 'success' });
            } else if (actionName === 'unfreezeGlobalPenalty') {
                await api.post('/payments/penalties/unfreeze-global', {
                    paymentId: payment.id,
                    notes: payload?.notes || 'Mora global descongelada desde hoy'
                });
                setSnackbar({ open: true, message: 'Mora global descongelada', severity: 'success' });
            } else if (actionName === 'suspend' || actionName === 'activate') {
                // Cambiar status del payment usando endpoints V2
                const endpoint = actionName === 'suspend' ? 'suspend' : 'activate';
                await api.post(`/payments/v2/${payment.id}/${endpoint}`);
                setSnackbar({ open: true, message: actionName === 'suspend' ? 'Familia suspendida' : 'Familia activada', severity: 'success' });
            } else if (actionName === 'toggleAutoDebit') {
                const userId = payload?.payment?.User?.id || payment.User?.id;
                if (!userId) return setSnackbar({ open: true, message: 'Usuario no encontrado', severity: 'error' });
                const val = payload?.value;
                
                // Si se está ACTIVANDO el auto débito, mostrar diálogo para seleccionar mes
                if (val === true) {
                    setAutoDebitPayload({ userId, payment: payload?.payment || payment });
                    setOpenAutoDebitDialog(true);
                    return; // No ejecutar aún, esperar respuesta del diálogo
                } else {                
                    // Si se está DESACTIVANDO, usar el nuevo endpoint para actualizar ambas tablas (FamilyDetail y NewPayment)
                    await api.post('/payments/v2/activate-auto-debit', { 
                        userId, 
                        paymentId: payment?.id || payload?.payment?.id,
                        schoolId: payment?.schoolId || payload?.payment?.schoolId,
                        cicloEscolarId: payment?.cicloEscolarId || payload?.payment?.cicloEscolarId,
                        activateAutoDebit: false 
                    });
                }
                setSnackbar({ open: true, message: 'Débito automático desactivado', severity: 'success' });
            
            } else if (actionName === 'toggleRequiresInvoice') {
                // Use payments endpoint to set invoice need
                const val = payload?.value;
                await api.put(`/payments/${payment.id}/set-invoice-need`, { requiresInvoice: !!val });
                setSnackbar({ open: true, message: `Requiere factura: ${val ? 'Sí' : 'No'}`, severity: 'success' });
            } else if (actionName === 'deletePayment') {
                // Revert last payment transaction. '/payments/revert' con { paymentId } reemplaza
                // a '/payments/:id/revert': mismo comportamiento (resuelve la última transacción
                // del pago), consolidado en el único entrypoint de reversión.
                try {
                    await api.post('/payments/revert', { paymentId: payment.id });
                    setSnackbar({ open: true, message: 'Pago revertido', severity: 'success' });
                    // invalidate caches for this payment/user and refresh payments/analysis
                    invalidatePaymentHistCache(payment || manageTarget);
                    // close manage modal if open
                    setOpenManageModal(false);
                    // refresh dataset and analysis
                    await fetchAllPayments(statusFilter, search);
                    await fetchPaymentsAnalysis(schoolId);
                } catch (e) {
                    console.error('Error revirtiendo pago', e);
                    // El backend puede rechazar con un motivo específico (mora ya limpiada,
                    // crédito ya auto-aplicado): mostrarlo en vez de un mensaje genérico.
                    const errorMsg = e?.response?.data?.error || 'Error revirtiendo pago';
                    setSnackbar({ open: true, message: errorMsg, severity: 'error' });
                }
            } else if (actionName === 'payPenalty') {
                // Pagar mora congelada
                const { amountPaid, numeroBoleta, bankAccountNumber, paidAt } = payload;
                if (!amountPaid || Number.isNaN(amountPaid) || amountPaid <= 0) {
                    setSnackbar({ open: true, message: 'Monto inválido para pagar mora', severity: 'error' });
                    return;
                }
                await api.post(`/payments/${payment.id}/pay-penalty`, {
                    amountPaid,
                    numeroBoleta,
                    bankAccountNumber,
                    paidAt
                });
                setSnackbar({ open: true, message: 'Pago de mora registrado exitosamente', severity: 'success' });
            } else if (actionName === 'discountPenalty') {
                // Exonerar o aplicar descuento a mora congelada
                const { discountAmount, type, notes } = payload;
                if (!discountAmount || Number.isNaN(discountAmount) || discountAmount <= 0) {
                    setSnackbar({ open: true, message: 'Monto inválido para exonerar/descontar', severity: 'error' });
                    return;
                }
                await api.post(`/payments/${payment.id}/exonerate-penalty`, {
                    discountAmount,
                    type,
                    notes
                });
                const message = type === 'EXONERATION' ? 'Mora exonerada exitosamente' : 'Descuento aplicado a mora';
                setSnackbar({ open: true, message, severity: 'success' });
            } else if (actionName === 'refreshPayments') {
                // Child requested a refresh only (no API call needed here).
            } else {
                setSnackbar({ open: true, message: `Acción no manejada: ${actionName}`, severity: 'info' });
            }
            // Invalidate payment history cache for affected user and refresh payments
            // Invalidate cache for this payment/user so modal will fetch fresh histories
            invalidatePaymentHistCache(payment || manageTarget);
            fetchAllPayments(statusFilter, search);
        } catch (err) {
            console.error('[handleManageAction] Error with action:', actionName);
            console.error('[handleManageAction] Error details:', err);
            console.error('[handleManageAction] Response data:', err?.response?.data);
            const errorMsg = err?.response?.data?.error || err?.message || 'Error ejecutando acción';
            setSnackbar({ open: true, message: errorMsg, severity: 'error' });
        }
    };

    const handleToggleInvoiceSent = async (row, newVal) => {
        try {
            // V2: La actualización ya se hizo en ManagePaymentsModal a nivel de transacción
            // Ya no necesitamos actualizar a nivel de pago porque ese campo no existe en V2
            
            setSnackbar({ open: true, message: `Factura ${newVal ? 'marcada como enviada' : 'desmarcada'}`, severity: 'success' });
            // Invalidate cache for this user so modal will fetch fresh histories
            invalidatePaymentHistCacheForUser(row?.userId || row?.User?.id || manageTarget?.User?.id || manageTarget?.userId);
            fetchAllPayments(statusFilter, search);
        } catch (err) {
            console.error('handleToggleInvoiceSent', err);
            setSnackbar({ open: true, message: 'Error actualizando envío de factura', severity: 'error' });
        }
    };

    // --- Export by status UI state ---
    const [openExportStatusDialog, setOpenExportStatusDialog] = useState(false);
    // Export dialog additional filters: separate month and year selectors
    const [exportMonth, setExportMonth] = useState('TODOS'); // 'TODOS' or '01'..'12'
    // Final status selector for export (MORA, PENDIENTE, PAGADO)
    const [exportFinalStatus, setExportFinalStatus] = useState('TODOS');
    // Service status selector for export (ACTIVE, PAUSED, SUSPENDED, INACTIVE, ALL)
    const [exportServiceStatus, setExportServiceStatus] = useState('TODOS'); 
    // Año seleccionable para exportación
    const [exportYear, setExportYear] = useState(String(moment().year()));
    const exportYearOptions = React.useMemo(() => {
        const currentYear = moment().year();
        const baseYear = Number(currentCycleYear);
        const startYear = Number.isFinite(baseYear) ? baseYear : currentYear;

        const years = [];
        for (let y = startYear; y <= currentYear; y++) years.push(String(y));
        // Mostrar descendente (más reciente primero)
        return years.reverse();
    }, [currentCycleYear]);

    // Si cambia el año del ciclo y el exportYear ya no está en el rango, ajustarlo.
    useEffect(() => {
        if (!exportYearOptions || exportYearOptions.length === 0) return;
        if (!exportYearOptions.includes(exportYear)) {
            setExportYear(exportYearOptions[0]);
        }
    }, [exportYearOptions]);

    // --- Export by estado (new) ---
    const [openExportByStateDialog, setOpenExportByStateDialog] = useState(false);
    const [exportByStateValue, setExportByStateValue] = useState('TODOS');
    const [exportByStateServiceStatus, setExportByStateServiceStatus] = useState('TODOS');

    // Download menu (unifies download actions to save space in the filters row)
    const [downloadMenuAnchorEl, setDownloadMenuAnchorEl] = useState(null);
    const downloadMenuOpen = Boolean(downloadMenuAnchorEl);
    const handleOpenDownloadMenu = (event) => setDownloadMenuAnchorEl(event.currentTarget);
    const handleCloseDownloadMenu = () => setDownloadMenuAnchorEl(null);


    // Export payments filtered by status and build Excel
    const handleDownloadPaymentsByStatus = useCallback(async (month = '', year = '') => {
        try {
            // Use new backend endpoint to fetch all PaymentHistory rows for payments
            // matching the selected final status. Backend will JOIN PaymentHistory -> Payment.
            // If 'GENERAL' is selected, omit the finalstatus param so backend returns all statuses.
            const selectedYear = String(year || exportYear || moment().year());
            const params = buildPaymentParams({ page: 1, limit: 10000 });
            // Manejar agrupación de estados: PAGADO incluye CONFIRMADO y ADELANTADO
            if (exportFinalStatus && String(exportFinalStatus).toUpperCase() === 'PAGADO') {
            } else if (exportFinalStatus && exportFinalStatus !== 'TODOS') {
                params.finalstatus = exportFinalStatus;
            }
            setOpenExportStatusDialog(false);
            setSnackbar({ open: true, message: 'Preparando descarga...', severity: 'info' });

            const res = await api.get('/payments/histories-by-finalstatus', { params });
            const historiesAll = res.data.histories || res.data || [];

            if (!Array.isArray(historiesAll) || historiesAll.length === 0) {
                setSnackbar({ open: true, message: 'No se encontraron historiales para el filtro seleccionado', severity: 'info' });
                return;
            }

            // Build workbook
            const workbook = new ExcelJS.Workbook();
            const sheet = workbook.addWorksheet('Historiales');

            const headers = [
                'Apellidos Familia',
                'Estado de Pago',
                'Estado del Servicio',
                'Fecha Pago',
                'Crédito/Saldo',
                'Tarifa',
                'Descuento Familia',
                'Descuento Extraordinario',
                'Mora',
                'Total a Pagar',
                'Monto Pagado',
                'Total pendiente de pago',
                'Crédito/Saldo Disponible'
            ];
            sheet.addRow(headers);

            // historiesAll contains PaymentHistory rows for all matching payments.
            // Apply month/year filter and write rows directly (assume each history row includes familyLastName)
            const yearToUse = selectedYear ? Number(selectedYear) : moment().year();
            const monthStrPad = (month && month !== 'TODOS') ? String(month).padStart(2, '0') : null;

            const normalizeUpper = (val) => (val == null ? '' : String(val)).trim().toUpperCase();
            const getHistoryUserId = (h) => (
                h?.userId ||
                h?.User?.id ||
                h?.Payment?.userId ||
                h?.Payment?.User?.id ||
                h?.payment?.userId ||
                h?.payment?.User?.id ||
                null
            );

            const buildServiceStatusMap = (paymentsArr) => new Map(
                (Array.isArray(paymentsArr) ? paymentsArr : []).map(p => {
                    const id = p?.userId || p?.User?.id || null;
                    const st = p?.serviceStatus || p?.User?.FamilyDetail?.serviceStatus || '';
                    return [id, normalizeUpper(st)];
                }).filter(([id]) => Boolean(id))
            );

            const buildFamilyDiscountMap = (paymentsArr) => new Map(
                (Array.isArray(paymentsArr) ? paymentsArr : []).map(p => {
                    const id = p?.userId || p?.User?.id || null;
                    const disc = p?.User?.FamilyDetail?.specialFee ?? p?.specialFee ?? 0;
                    return [id, Number(disc || 0)];
                }).filter(([id]) => Boolean(id))
            );

            // Build a map of current serviceStatus by userId. Prefer a fresh unfiltered /payments fetch
            // so this works even if the current UI has status/search filters applied.
            let serviceStatusByUserId = buildServiceStatusMap(paymentsAll);
            let familyDiscountByUserId = buildFamilyDiscountMap(paymentsAll);
            if (schoolId) {
                try {
                    const mergedServiceStatusMap = new Map(serviceStatusByUserId);
                    const mergedFamilyDiscountMap = new Map(familyDiscountByUserId);

                    const resPayments = await api.get('/payments', { params: buildPaymentParams({ page: 1, limit: 10000 }) });
                    const allPayments = resPayments.data.payments || resPayments.data.rows || [];

                    const partialServiceMap = buildServiceStatusMap(allPayments);
                    partialServiceMap.forEach((v, k) => mergedServiceStatusMap.set(k, v));

                    const partialDiscMap = buildFamilyDiscountMap(allPayments);
                    partialDiscMap.forEach((v, k) => mergedFamilyDiscountMap.set(k, v));

                    if (mergedServiceStatusMap.size > 0) serviceStatusByUserId = mergedServiceStatusMap;
                    if (mergedFamilyDiscountMap.size > 0) familyDiscountByUserId = mergedFamilyDiscountMap;
                } catch (e) {
                    // If this fails, fall back to paymentsAll-based maps.
                    console.warn('[export] Could not build /payments maps:', e);
                }
            }

            historiesAll.forEach(h => {
                const historyUserId = getHistoryUserId(h);

                // Determinar estado del servicio (preferir lo que venga en el historial, y si no, usar paymentsAll)
                const serviceStatus = normalizeUpper(
                    h?.serviceStatus ||
                    h?.User?.FamilyDetail?.serviceStatus ||
                    h?.Payment?.User?.FamilyDetail?.serviceStatus ||
                    (historyUserId ? serviceStatusByUserId.get(historyUserId) : '') ||
                    ''
                );
                
                // Filtrar por estado del servicio
                if (exportServiceStatus && exportServiceStatus !== 'TODOS') {
                    if (serviceStatus !== normalizeUpper(exportServiceStatus)) return;
                }
                
                const familyLast = h.familyLastName || h.familyLast || h.familyLastname || h.User?.FamilyDetail?.familyLastName || h.User?.familyLastName || '';
                // Use only finalStatus (as required). Fallback to Payment.finalStatus if the snapshot doesn't include it.
                const estado = h.finalStatus || (h.Payment && h.Payment.finalStatus) || '';
                const estadoUpper = normalizeUpper(estado);
                
                // Filtrar por estado de pago
                if (exportFinalStatus && exportFinalStatus !== 'TODOS') {
                    const filtroUpper = normalizeUpper(exportFinalStatus);
                    
                    // PAGADO corresponde solo a CONFIRMADO; ADELANTADO es separado
                    if (filtroUpper === 'PAGADO') {
                        if (estadoUpper !== 'CONFIRMADO') return;
                    } else {
                        if (estadoUpper !== filtroUpper) return;
                    }
                }

                // Mostrar estado de pago en formato UI
                const estadoPagoText = (estadoUpper === 'CONFIRMADO')
                    ? 'PAGADO'
                    : (estadoUpper || '');
                
                // Mapear estado del servicio a texto legible
                const serviceStatusText = {
                    'ACTIVE': 'ACTIVO',
                    'PAUSED': 'PAUSADO',
                    'SUSPENDED': 'SUSPENDIDO',
                    'INACTIVE': 'INACTIVO'
                }[serviceStatus] || serviceStatus || 'NO ESPECIFICADO';
                
                // Filtrar por fecha (mes y año)
                const pd = h.paymentDate || h.lastPaymentDate || h.snapshotDate || null;
                if (!pd) return;
                const mm = moment.parseZone(pd);
                
                // Si hay mes específico seleccionado, filtrar por mes y año exactos
                if (monthStrPad) {
                    if (mm.format('YYYY-MM') !== `${yearToUse}-${monthStrPad}`) return;
                }
                // Si no hay mes (TODOS), filtrar únicamente por el año seleccionado
                else if (year && year !== 'TODOS') {
                    if (mm.year() !== yearToUse) return;
                }

                const fecha = (typeof h.paymentDate !== 'undefined' && h.paymentDate !== null && h.paymentDate !== '') ? moment.parseZone(h.paymentDate).format('DD/MM/YY') : '0';
                const creditoSaldo = Number(h.creditBalanceBefore ?? 0);
                const tarifa = Number(h.tarif ?? 0);
                const descuentoFamFromHistory = Number(
                    h.familyDiscount ||
                    h.family_discount ||
                    h?.metadata?.familyDiscount ||
                    0
                );
                const descuentoFamFromFamily = historyUserId ? Number(familyDiscountByUserId.get(historyUserId) || 0) : 0;
                const descuentoFam = descuentoFamFromHistory > 0 ? descuentoFamFromHistory : descuentoFamFromFamily;

                const descExtra = Number(
                    h.extraordinaryDiscount ||
                    h.extraordinary_discount ||
                    h.discountAmount ||
                    0
                );
                const mora = Number(h.penaltyBefore ?? 0);
                const totalDueBefore = Number(h.totalDueBefore ?? 0);
                const totalAPagar = Number(totalDueBefore - creditoSaldo - descExtra);
                const monto = Number(h.amountPaid ?? 0);
                const totalPendiente = Number(h.totalDueAfter ?? 0);
                const creditoDisponible = Number(h.creditBalanceAfter ?? 0);

                // Insert Estado de Pago y Estado del Servicio
                sheet.addRow([familyLast, estadoPagoText, serviceStatusText, fecha, creditoSaldo, tarifa, descuentoFam, descExtra, mora, totalAPagar, monto, totalPendiente, creditoDisponible]);
            });

            if (sheet.rowCount <= 1) {
                setSnackbar({ open: true, message: 'No se encontraron registros con los filtros seleccionados', severity: 'info' });
                return;
            }

            // Styling header (match historial style)
            sheet.getRow(1).eachCell((cell, colNumber) => {
                cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4472C4' } };
                // Column 2 (Apellidos Familia) left-aligned header, others centered
                cell.alignment = colNumber === 2 ? { horizontal: 'left', vertical: 'middle' } : { horizontal: 'center', vertical: 'middle' };
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            });

            // Alternating row colors and alignment (center data cells; keep numeric right-aligned)
            sheet.eachRow((row, rowIndex) => {
                if (rowIndex === 1) return;
                const isEven = rowIndex % 2 === 0;
                row.eachCell((cell, colNumber) => {
                    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: isEven ? 'FFF2F2F2' : 'FFFFFFFF' } };
                    // center by default for text/date columns
                    cell.alignment = { horizontal: 'center', vertical: 'middle' };
                    // Numeric columns start at column 5 now (Crédito/Saldo). Right-align and format numbers for columns >= 5
                    if (colNumber >= 5) {
                        cell.numFmt = '0.00';
                        cell.alignment = { horizontal: 'right', vertical: 'middle' };
                    }
                });
            });

            // Autofilter and freeze first row
            const lastCol = sheet.getRow(1).cellCount;
            const getColumnLetter = (colNumber) => {
                let letter = '';
                while (colNumber > 0) {
                    const remainder = (colNumber - 1) % 26;
                    letter = String.fromCharCode(65 + remainder) + letter;
                    colNumber = Math.floor((colNumber - 1) / 26);
                }
                return letter;
            };
            const lastColLetter = getColumnLetter(lastCol);
            sheet.autoFilter = `A1:${lastColLetter}1`;
            sheet.views = [{ state: 'frozen', xSplit: 1, ySplit: 1 }];

            // Autosize columns
            sheet.columns.forEach((column, index) => {
                const header = headers[index] || '';
                let maxLength = header.length;
                sheet.eachRow((row) => {
                    const cell = row.getCell(index + 1);
                    const value = cell && cell.value ? String(cell.value) : '';
                    if (value.length > maxLength) maxLength = value.length;
                });
                column.width = Math.min(Math.max(maxLength + 2, 10), 60);
            });

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            // Build filename with school and period (year or year-month)
            const monthNames = {
                '01': 'Enero','02': 'Febrero','03': 'Marzo','04': 'Abril','05': 'Mayo','06': 'Junio',
                '07': 'Julio','08': 'Agosto','09': 'Septiembre','10': 'Octubre','11': 'Noviembre','12': 'Diciembre'
            };
            // Build filename in requested format:
            // "Historial Pagos estadoFinal nombreColegio Mes Año"
            const periodYear = year || exportYear || moment().year();
            const monthPadded = (month && String(month).toUpperCase() !== 'TODOS') ? String(month).padStart(2, '0') : '';
            const monthName = monthPadded ? (monthNames[monthPadded] || monthPadded) : '';
            // sanitize school name for filenames (remove newlines/slashes, collapse spaces)
            const rawSchoolName = school?.name ? String(school.name) : String(schoolId || '');
            const schoolClean = rawSchoolName.replace(/[\n\r/\\]+/g, ' ').replace(/\s+/g, ' ').trim();
            const statusPart = exportFinalStatus ? String(exportFinalStatus).toUpperCase() : '';
            let fileName;
            if (monthName) {
                fileName = `Historial Pagos ${statusPart} ${schoolClean} ${monthName} ${periodYear}.xlsx`;
            } else {
                fileName = `Historial Pagos ${statusPart} ${schoolClean} ${periodYear}.xlsx`;
            }
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            setSnackbar({ open: true, message: 'Descarga iniciada', severity: 'success' });
        } catch (err) {
            console.error('handleDownloadPaymentsByStatus', err);
            setSnackbar({ open: true, message: 'Error generando exportación', severity: 'error' });
        }
    }, [schoolId, buildPaymentParams, school, paymentsAll, exportYear, exportFinalStatus, exportServiceStatus]);

    // Export payments filtered by estado using client-side dataset (paymentsAll)
    const handleDownloadByState = useCallback(async (estado) => {
        try {
            setOpenExportByStateDialog(false);
            setSnackbar({ open: true, message: 'Preparando descarga...', severity: 'info' });

            const estadoNorm = estado ? String(estado).toUpperCase().trim() : 'TODOS';
            const arr = Array.isArray(paymentsAll) ? paymentsAll : [];
            const serviceFilter = String(exportByStateServiceStatus || 'TODOS').toUpperCase().trim();

            const toServiceText = (stRaw) => {
                const st = String(stRaw || '').toUpperCase().trim();
                return {
                    ACTIVE: 'ACTIVO',
                    PAUSED: 'PAUSADO',
                    SUSPENDED: 'SUSPENDIDO',
                    INACTIVE: 'INACTIVO'
                }[st] || (st ? st : 'NO ESPECIFICADO');
            };

            const toEstadoPagoText = (stRaw) => {
                const s = String(stRaw || '').toUpperCase().trim();
                return s === 'CONFIRMADO' ? 'PAGADO' : s;
            };

            const filteredRows = arr.filter(p => {
                // Determinar si la familia/usuario está marcado como eliminado (deleted)
                const isDeleted = !!p.User?.FamilyDetail?.deleted;

                // Siempre ignorar registros marcados como deleted
                if (isDeleted) return false;

                const paymentServiceStatus = String(p.serviceStatus || p.User?.FamilyDetail?.serviceStatus || '').toUpperCase().trim();
                if (serviceFilter && serviceFilter !== 'TODOS') {
                    if (paymentServiceStatus !== serviceFilter) return false;
                }

                const s = (p.finalStatus || '').toString().toUpperCase();

                if (!estadoNorm || estadoNorm === 'TODOS') return true;
                if (estadoNorm === 'PAGADO') return s === 'CONFIRMADO';
                return s === estadoNorm;
            });

            if (!filteredRows || filteredRows.length === 0) {
                setSnackbar({ open: true, message: 'No se encontraron registros para el estado seleccionado', severity: 'info' });
                return;
            }

            const workbook = new ExcelJS.Workbook();
            const sheet = workbook.addWorksheet('Datos');

            const headers = ['Estado del Servicio','Estado de Pago','Apellidos Familia','Débito Automático','Cant. Estudiantes','Tipo Ruta','Fecha Último Pago','Descuento','Envío Factura'];
            sheet.addRow(headers);

            filteredRows.forEach(p => {
                const estadoVal = toEstadoPagoText(p.finalStatus || '');
                const serviceText = toServiceText(p.serviceStatus || p.User?.FamilyDetail?.serviceStatus || '');
                const familyLast = p.User?.FamilyDetail?.familyLastName || p.User?.familyLastName || '';
                const autoDebit = !!(p.automaticDebit || p.User?.FamilyDetail?.automaticDebit || p.User?.FamilyDetail?.autoDebit);
                const autoStr = autoDebit ? 'Sí' : 'No';
                const studentsCount = Array.isArray(p.User?.FamilyDetail?.Students) ? p.User.FamilyDetail.Students.length : (p.studentCount || 0);
                const studentsInt = Number.isFinite(Number(studentsCount)) ? Math.trunc(Number(studentsCount)) : 0;
                const routeType = p.User?.FamilyDetail?.routeType || '';
                const lastPaymentRaw = p.lastPaymentDate || p.lastPaidDate || p.lastPayment || p.lastPaymentAt || p.last_paid_at || null;
                const lastPaymentDate = lastPaymentRaw ? moment.parseZone(lastPaymentRaw).toDate() : null;
                const discount = Number(p.User?.FamilyDetail?.specialFee ?? p.specialFee ?? 0) || 0;
                const invoiceSent = !!(p.User?.FamilyDetail?.requiresInvoice || p.requiresInvoice);
                const invoiceStr = invoiceSent ? 'Sí' : 'No';

                // Add row: for Fecha Último Pago we add a Date object (or empty string if missing) so Excel recognizes it as date
                sheet.addRow([serviceText, estadoVal, familyLast, autoStr, studentsInt, routeType, lastPaymentDate || '', discount, invoiceStr]);
            });

            // Styling header (match historial style)
            sheet.getRow(1).eachCell((cell) => {
                cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4472C4' } };
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            });

            // Alternating row colors and alignment (center text/date columns; numeric right-aligned)
            sheet.eachRow((row, rowIndex) => {
                if (rowIndex === 1) return;
                const isEven = rowIndex % 2 === 0;
                row.eachCell((cell, colNumber) => {
                    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: isEven ? 'FFF2F2F2' : 'FFFFFFFF' } };
                    // Apellidos Familia (col 3) left-aligned; numeric fields right-aligned; dates centered; others centered
                    if (colNumber === 3) {
                        cell.alignment = { horizontal: 'left', vertical: 'middle' };
                    } else {
                        cell.alignment = { horizontal: 'center', vertical: 'middle' };
                    }
                    // Numeric columns: Cant. Estudiantes (col 5) integer, Descuento (col 8) two decimals
                    if (colNumber === 5) {
                        cell.numFmt = '0';
                        cell.alignment = { horizontal: 'center', vertical: 'middle' };
                    }
                    if (colNumber === 8) {
                        cell.numFmt = '0.00';
                        cell.alignment = { horizontal: 'center', vertical: 'middle' };
                    }
                    // Fecha Último Pago (col 7) should be date dd/mm/yyyy
                    if (colNumber === 7) {
                        cell.numFmt = 'dd/mm/yyyy';
                        cell.alignment = { horizontal: 'center', vertical: 'middle' };
                    }
                });
            });

            // Autofilter and freeze first row
            const lastCol = sheet.getRow(1).cellCount;
            const getColumnLetter = (colNumber) => {
                let letter = '';
                while (colNumber > 0) {
                    const remainder = (colNumber - 1) % 26;
                    letter = String.fromCharCode(65 + remainder) + letter;
                    colNumber = Math.floor((colNumber - 1) / 26);
                }
                return letter;
            };
            const lastColLetter = getColumnLetter(lastCol);
            sheet.autoFilter = `A1:${lastColLetter}1`;
            sheet.views = [{ state: 'frozen', xSplit: 1, ySplit: 1 }];

            // Autosize columns
            sheet.columns.forEach((column, index) => {
                const header = headers[index] || '';
                let maxLength = header.length;
                sheet.eachRow((row) => {
                    const cell = row.getCell(index + 1);
                    const value = cell && cell.value ? String(cell.value) : '';
                    if (value.length > maxLength) maxLength = value.length;
                });
                column.width = Math.min(Math.max(maxLength + 2, 10), 60);
            });

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const fileName = `Pagos_por_estado_${(estadoNorm || 'TODOS')}_servicio_${(serviceFilter || 'TODOS')}_${school?.name ? school.name.replace(/[^a-z0-9]/gi,'_') : schoolId}.xlsx`;
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);

            setSnackbar({ open: true, message: 'Descarga iniciada', severity: 'success' });
        } catch (err) {
            console.error('handleDownloadByState', err);
            setSnackbar({ open: true, message: 'Error generando exportación', severity: 'error' });
        }
    }, [paymentsAll, exportByStateServiceStatus, school, schoolId]);

    // Legacy discount-save flow removed.
    // The new retroactive discount flow is applied via POST /payments/v2/:id/apply-family-discount.
    const handleSaveDiscount = async (payment) => {
        try {
            invalidatePaymentHistCacheForUser(payment?.User?.id || payment?.userId);
            fetchAllPayments(statusFilter, search);
        } catch (err) {
            console.error(err);
        }
    };

    // Derived values for the Register Payment dialog summary
    const formatCurrency = (v) => `Q ${Number(v || 0).toFixed(2)}`;
    
    // Función para calcular mora hasta una fecha específica (retroactiva) — POR PERÍODO
    // Itera cada período con mora y calcula días desde su penaltyStartDate hasta paymentDate.
    const calculateRetroactivePenalty = useCallback((paymentDate, penaltyPeriods) => {
        if (!paymentDate || !penaltyPeriods || penaltyPeriods.length === 0) {
            return null;
        }

        const paymentMoment = moment(paymentDate).startOf('day');
        const today = moment().startOf('day');

        // Si la fecha de pago es igual o después de hoy, no es retroactivo
        if (paymentMoment.isSameOrAfter(today)) {
            return null;
        }

        const dailyPenalty = parseFloat(school?.dailyPenalty || allSchools.find(s => s.id === registerPaymentTarget?.schoolId)?.dailyPenalty || 0);
        const fallbackStudentCount = Number(
            registerPaymentTarget?.studentCount ||
            registerPaymentTarget?.User?.FamilyDetail?.studentsCount ||
            ((registerPaymentTarget?.User?.FamilyDetail?.Students || []).length || 0) ||
            1
        ) || 1;

        let totalPenalty = 0;
        const periodBreakdown = [];

        for (const period of penaltyPeriods) {
            const periodStudentCountRaw = Number(period?.studentsCount);
            const periodStudentCount = (Number.isFinite(periodStudentCountRaw) && periodStudentCountRaw > 0)
                ? periodStudentCountRaw
                : fallbackStudentCount;

            // Períodos congelados o limpiados mantienen su mora fija
            if (period.penaltyFrozen || period.penaltyCleared) {
                periodBreakdown.push({
                    period: period.period,
                    daysLate: 0,
                    penaltyAmount: Number(period.penaltyDue || 0),
                    studentCount: periodStudentCount,
                    frozen: !!period.penaltyFrozen,
                    cleared: !!period.penaltyCleared,
                });
                totalPenalty += Number(period.penaltyDue || 0);
                continue;
            }

            if (!period.penaltyStartDate) continue;

            const penaltyStart = moment(period.penaltyStartDate).startOf('day');

            if (paymentMoment.isBefore(penaltyStart)) {
                // Fecha de pago anterior al inicio de mora de este período
                periodBreakdown.push({
                    period: period.period,
                    daysLate: 0,
                    penaltyAmount: 0,
                    frozen: false,
                    cleared: false,
                });
                continue;
            }

            const daysLate = paymentMoment.diff(penaltyStart, 'days') + 1;
            const periodPenalty = daysLate * dailyPenalty * periodStudentCount;

            periodBreakdown.push({
                period: period.period,
                daysLate,
                penaltyAmount: periodPenalty,
                studentCount: periodStudentCount,
                penaltyStartDate: penaltyStart.format('DD/MM/YYYY'),
                frozen: false,
                cleared: false,
            });
            totalPenalty += periodPenalty;
        }

        return {
            isRetroactive: true,
            periods: periodBreakdown,
            penaltyUntilPaymentDate: totalPenalty,
            dailyPenalty,
            studentCount: fallbackStudentCount,
            paymentDate: paymentMoment.format('DD [de] MMMM, YYYY'),
            message: `Pago retroactivo: La mora acumulada hasta el ${paymentMoment.format('DD/MM/YYYY')} es de Q${totalPenalty.toFixed(2)}`
        };
    }, [registerPaymentTarget, school, allSchools]);
    
    // Valores del sistema V2
    // monthlyFee = tarifa base × cantidad de estudiantes (antes de descuentos)
    const dialogMonthlyFee = Number(registerPaymentTarget?.monthlyFee || 0);
    
    // specialDiscount (UI): prefer per-period discountApplied when there is a single unpaid period.
    // This avoids showing the latest configured family discount (future periods) for an already-generated period.
    const dialogFamilySpecialFee = (() => {
        try {
            const raw = registerPaymentTarget?.unpaidPeriods
                ? (typeof registerPaymentTarget.unpaidPeriods === 'string'
                    ? JSON.parse(registerPaymentTarget.unpaidPeriods)
                    : registerPaymentTarget.unpaidPeriods)
                : [];

            const periods = (Array.isArray(raw) ? raw : []).filter(p => Number(p?.amountDue ?? p?.amount ?? 0) > 0);
            if (periods.length === 1) {
                const perPeriodDiscount = Number(periods[0]?.discountApplied ?? periods[0]?.discount ?? NaN);
                if (Number.isFinite(perPeriodDiscount) && perPeriodDiscount >= 0) return perPeriodDiscount;
            }
        } catch (e) {
            // ignore and fall back
        }
        return Number(registerPaymentTarget?.specialDiscount || 0);
    })();
    
    // netMonthlyFee = monthlyFee - specialDiscount (lo que realmente debe pagar mensualmente)
    const dialogNetMonthlyFee = Number(registerPaymentTarget?.netMonthlyFee || 0);
    
    // Cantidad de estudiantes
    const studentCount = Number(
        registerPaymentTarget?.studentCount ||
        (Array.isArray(registerPaymentTarget?.User?.FamilyDetail?.Students) ? registerPaymentTarget.User.FamilyDetail.Students.length : undefined) ||
        registerPaymentTarget?.User?.FamilyDetail?.studentsCount ||
        1
    ) || 1;

    const dialogBaseFee = studentCount > 0 ? dialogMonthlyFee / studentCount : dialogMonthlyFee;
    
    // Tipo de ruta
    const routeType = registerPaymentTarget?.User?.FamilyDetail?.routeType || 'N/A';
    
    // Mora: preferir la suma de períodos cuando están disponibles (per-period), fallback a valor global
    const basePenaltyDueGlobal = Number(registerPaymentTarget?.penaltyDue || 0);
    
    const dialogCredito = Number(registerPaymentTarget?.creditBalance ?? 0);
    
    // Saldo pendiente: usar balanceDue
    const dialogLeftover = Number(registerPaymentTarget?.balanceDue || 0);
    
    const dialogExtraDiscount = Number(registerPaymentExtra?.extraordinaryDiscount || 0);
    const effectivePendingBalance = Math.max(0, dialogLeftover - dialogCredito);
    const extraDiscountError = dialogExtraDiscount > effectivePendingBalance + 0.005;
    const dialogPaidAmount = Math.max(0, Number(registerAmount || 0));
    const dialogPendingBalance = Math.max(0, dialogLeftover);
    const dialogExtraDiscountAppliedToBalance = Math.min(Math.max(0, dialogExtraDiscount), dialogPendingBalance);
    const dialogGeneratedCredit = Math.max(
        0,
        dialogPaidAmount - Math.max(0, dialogPendingBalance - dialogExtraDiscountAppliedToBalance)
    );

    // Períodos pendientes (para desglose): filtrar solo los que tienen monto pendiente
    const dialogPendingPeriods = (() => {
        try {
            const raw = registerPaymentTarget?.unpaidPeriods
                ? (typeof registerPaymentTarget.unpaidPeriods === 'string'
                    ? JSON.parse(registerPaymentTarget.unpaidPeriods)
                    : registerPaymentTarget.unpaidPeriods)
                : [];
            return (Array.isArray(raw) ? raw : []).filter(p => Number(p?.amountDue ?? p?.amount ?? 0) > 0);
        } catch (e) {
            return [];
        }
    })();

    const hasSinglePendingPeriod = dialogPendingPeriods.length === 1;
    const hasMultiplePendingPeriods = dialogPendingPeriods.length > 1;

    // Períodos con mora pendiente (para desglose en Tab 1 "Pago de Mora")
    // Usa penaltyPeriods del backend (incluye périodos PAGADOS en tarifa con mora pendiente).
    // Fallback: filtrar dialogPendingPeriods para compatibilidad con respuestas antiguas.
    const dialogPenaltyPeriods = (() => {
        const raw = registerPaymentTarget?.penaltyPeriods;
        if (raw && Array.isArray(raw) && raw.length > 0) return raw;
        if (typeof raw === 'string') {
            try { const parsed = JSON.parse(raw); if (Array.isArray(parsed) && parsed.length > 0) return parsed; } catch (e) { /* ignore */ }
        }
        return dialogPendingPeriods.filter(p => Number(p?.penaltyDue || 0) > 0 || p?.penaltyFrozen);
    })();
    const totalPenaltyFromPeriods = dialogPenaltyPeriods.reduce(
        (sum, p) => sum + Number(p?.penaltyDue || 0), 0
    );
    const hasFrozenPeriods = dialogPenaltyPeriods.some(p => p?.penaltyFrozen);
    // basePenaltyDue: usa la suma de períodos si hay desglose, fallback al valor global del payment
    const basePenaltyDue = dialogPenaltyPeriods.length > 0 ? totalPenaltyFromPeriods : basePenaltyDueGlobal;

    // Recalcular mora retroactiva cuando cambia la fecha de pago o los períodos con mora
    // Se usa un useMemo en vez de useEffect+state para evitar ciclos de render innecesarios
    const retroactivePenaltyCalc = React.useMemo(() => {
        if (paymentTab !== 1 || dialogPenaltyPeriods.length === 0) return null;
        return calculateRetroactivePenalty(payPenaltyDate, dialogPenaltyPeriods);
    }, [payPenaltyDate, paymentTab, dialogPenaltyPeriods, calculateRetroactivePenalty]);

    // Index retroactive per-period results for O(1) lookups in the main breakdown
    const retroPenaltyByPeriod = React.useMemo(() => {
        const map = new Map();
        (retroactivePenaltyCalc?.periods || []).forEach(p => {
            if (p?.period) map.set(p.period, p);
        });
        return map;
    }, [retroactivePenaltyCalc]);

    // Mora en tiempo real: si hay cálculo retroactivo, usar ese total; sino, el valor almacenado
    const effectivePenaltyDue = React.useMemo(() => {
        if (retroactivePenaltyCalc) return retroactivePenaltyCalc.penaltyUntilPaymentDate;
        return basePenaltyDue;
    }, [retroactivePenaltyCalc, basePenaltyDue]);

    // UX (previous behavior):
    // - If there is exactly 1 pending period, show only the "CARGOS BASE" section (no per-period orange box).
    // - Ensure CARGOS BASE reflects that pending period's snapshot values (routeType/originalAmount) to avoid
    //   showing the latest FamilyDetail.routeType for a historical month.
    const singlePendingPeriod = hasSinglePendingPeriod ? dialogPendingPeriods[0] : null;
    const effectiveRouteType = singlePendingPeriod?.routeType || routeType;
    const effectiveMonthlyFee = Number(singlePendingPeriod?.originalAmount ?? dialogMonthlyFee ?? 0) || 0;
    // Preferir el conteo de estudiantes almacenado en el período (snapshot) cuando exista.
    const effectiveStudents = Number(singlePendingPeriod?.studentsCount || registerPaymentTarget?.studentCount || studentCount) || 1;
    const effectiveBaseFee = effectiveStudents > 0 ? (effectiveMonthlyFee / effectiveStudents) : effectiveMonthlyFee;
    // Para UI: mostrar estudiantes de CARGOS BASE según si hay un período pendiente único
    const cargosStudents = hasSinglePendingPeriod ? effectiveStudents : studentCount;
    
    // Total a pagar en pestaña de Tarifa: NO incluye mora (se paga por separado)
    const dialogTotalToPay = Math.max(0,
        dialogLeftover - dialogCredito - dialogExtraDiscount
    );
    if (!school && loading) {
        return (
            <PageContainer>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', minHeight: '60vh' }}>
                    <CircularProgress />
                    <Typography sx={{ mt: 2 }}>Cargando datos...</Typography>
                </Box>
            </PageContainer>
        );
    }

    if (school && school.paymentsEnabled === false) {
        return (
            <PageContainer>
                <HeaderCard>
                    <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Button startIcon={<ArrowBack />} onClick={handleBack}>
                                Volver
                            </Button>
                            <Typography variant="h6">{school.name || 'Colegio'}</Typography>
                        </Box>
                    </CardContent>
                </HeaderCard>
                <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="h6" gutterBottom>
                        Este colegio no utiliza el flujo de pagos
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        No se generan cuotas, mora, inscripciones ni débitos automáticos, y el colegio
                        no aparece en las estadísticas financieras.
                    </Typography>
                </Box>
            </PageContainer>
        );
    }

    return (
        <PageContainer>
            {/* Loading overlay while initial data is fetched */}
            {loading && (
                <Box sx={{ position: 'fixed', inset: 0, zIndex: 2000, backgroundColor: 'rgba(255,255,255,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                    <CircularProgress />
                    <Typography sx={{ mt: 2 }}>Cargando datos...</Typography>
                </Box>
            )}
            <HeaderCard>
                <CardContent sx={{ position: 'relative' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Button
                            startIcon={<ArrowBack />}
                            onClick={handleBack}
                            sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
                        >
                            Volver al Dashboard
                        </Button>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <SchoolIcon sx={{ fontSize: 40 }} />
                        <Box>
                            <Typography variant="h4" component="h1" gutterBottom>
                                Gestión de Pagos - {school?.name || 'Cargando...'}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Chip
                                    size="small"
                                    icon={<CalendarToday />}
                                    label={`Ciclo Escolar ${currentCycleLabel || ''}`}
                                    sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }}
                                />
                                <Chip
                                    size="small"
                                    icon={<People />}
                                    label={`${totalFamiliesCount || 0} familias en sistema`}
                                    sx={{ backgroundColor: 'rgba(255,255,255,0.12)', color: 'white', fontWeight: 600 }}
                                />
                                {/* Opciones Extra moved to bottom-right of header */}
                            </Box>
                        </Box>
                    </Box>
                    {hasAnyPermission(['pagos-v2-crear-periodo-colegio', 'pagos-v2-reintegrar-credito']) && (
                    <Box sx={{ position: 'absolute', right: 16, bottom: 12 }}>
                        <Button
                            size="small"
                            variant="contained"
                            sx={{
                                backgroundColor: 'rgba(255,255,255,0.18)',
                                color: 'white',
                                fontWeight: 700,
                                boxShadow: '0 1px 6px rgba(0,0,0,0.12)',
                                '&:hover': { backgroundColor: 'rgba(255,255,255,0.22)', boxShadow: '0 3px 10px rgba(0,0,0,0.15)' }
                            }}
                            onClick={(e) => setOpenExtraAnchorEl(e.currentTarget)}
                        >
                            Opciones Extra
                        </Button>
                        <Menu anchorEl={openExtraAnchorEl} open={openExtraMenu} onClose={() => setOpenExtraAnchorEl(null)}>
                            <PermissionGuard permission="pagos-v2-crear-periodo-colegio">
                                <MenuItem onClick={() => { setOpenCreateSchoolPeriodModal(true); setOpenExtraAnchorEl(null); }}>
                                    Crear período extracurricular
                                </MenuItem>
                            </PermissionGuard>
                            <PermissionGuard permission="pagos-v2-reintegrar-credito">
                                <MenuItem onClick={() => { setOpenCreditRefundModal(true); setOpenExtraAnchorEl(null); }}>
                                    Reintegrar crédito a favor
                                </MenuItem>
                            </PermissionGuard>
                        </Menu>
                    </Box>
                    )}
                </CardContent>
            </HeaderCard>

            {/* Analysis General de Pagos (full from PaymentsManagementPage) */}
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <SectionCard sx={{ mb: 2 }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <IconButton
                                        size="small"
                                        onClick={() => setCollapsedAnalysis(s => !s)}
                                        aria-label={collapsedAnalysis ? 'Expandir análisis' : 'Colapsar análisis'}
                                    >
                                        <ExpandMoreIcon sx={{ transform: collapsedAnalysis ? 'rotate(0deg)' : 'rotate(180deg)', transition: 'transform 0.2s' }} />
                                    </IconButton>
                                    <Box>
                                        <Typography variant="h6" sx={{ fontWeight: 700 }}>Análisis General de Pagos</Typography>
                                        <Typography variant="caption" color="text.secondary">Resumen y métricas principales del colegio</Typography>
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button size="small" onClick={() => fetchPaymentsAnalysis(schoolId)} sx={{ textTransform: 'none' }}>Actualizar</Button>
                                </Box>
                            </Box>

                            <Collapse in={!collapsedAnalysis} timeout="auto" unmountOnExit>
                                {/* If analysisData available show extended grid and chart */}
                                {analysisData ? (
                                    <Box sx={{ mt: 2 }}>
                                        {/* Controles de período y base (Caja/Devengado) — las métricas se recalculan en el backend */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 2, p: 1.5, background: '#f1f5f9', borderRadius: 2 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <IconButton size="small" onClick={() => shiftSelectedPeriod(-1)} disabled={minPeriod ? selectedPeriod <= minPeriod : false} aria-label="Mes anterior">
                                                    <ChevronLeft />
                                                </IconButton>
                                                <Box sx={{ minWidth: 150, textAlign: 'center' }}>
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 700, textTransform: 'capitalize', lineHeight: 1.1 }}>
                                                        {moment(`${selectedPeriod}-01`).format('MMMM YYYY')}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">Período de métricas</Typography>
                                                </Box>
                                                <IconButton size="small" onClick={() => shiftSelectedPeriod(1)} disabled={selectedPeriod >= todayPeriod} aria-label="Mes siguiente">
                                                    <ChevronRight />
                                                </IconButton>
                                            </Box>
                                            {metricsLoading && <CircularProgress size={18} />}
                                            <Box sx={{ flexGrow: 1 }} />
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Typography variant="caption" color="text.secondary">Base</Typography>
                                                <Tooltip title={
                                                    "Caja: agrupa los pagos por la fecha en que se recibió el dinero, sin importar a qué mes pertenece la cuota. " +
                                                    "Devengado: agrupa los pagos por el mes al que corresponde la cuota, sin importar cuándo se recibió el dinero. " +
                                                    "Ejemplo: una cuota de marzo pagada el 5 de abril aparece en abril en base Caja, y en marzo en base Devengado."
                                                } arrow>
                                                    <InfoOutlined sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help' }} />
                                                </Tooltip>
                                                <ToggleButtonGroup size="small" exclusive value={metricsBasis} onChange={(e, v) => { if (v) setMetricsBasis(v); }}>
                                                    <ToggleButton value="caja">Caja</ToggleButton>
                                                    <ToggleButton value="devengado">Devengado</ToggleButton>
                                                </ToggleButtonGroup>
                                            </Box>
                                        </Box>
                                        {/* KPIs Principales */}
                                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#1976d2' }}>📊 Indicadores Clave (KPIs)</Typography>
                                        <Box sx={{ mb: 3, p: 2, background: 'linear-gradient(135deg, #667eea15 0%, #764ba215 100%)', borderRadius: 2, boxShadow: 1 }}>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} sm={6} md={3}>
                                                    <Box sx={{ p: 2, background: 'white', borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <Typography variant="caption" color="text.secondary">Tasa de Pago</Typography>
                                                            <Tooltip title={
                                                                <Box>
                                                                    <Box sx={{ fontWeight: 600, fontSize: '0.8125rem', mb: 0.5, lineHeight: 1.3 }}>
                                                                        {"Porcentaje de familias con cuota generada en el período seleccionado que la pagaron por completo."}
                                                                    </Box>
                                                                    <Box sx={{ fontSize: '0.75rem', opacity: 0.8, lineHeight: 1.3 }}>
                                                                        {"Solo se cuentan las familias que tienen una cuota facturada en este período, independientemente de su estado de servicio. Por eso este número puede ser menor al total de familias registradas en el colegio: las que no tienen tarifa asignada o no se les generó cuota este mes no entran en el cálculo."}
                                                                    </Box>
                                                                </Box>
                                                            } arrow>
                                                                <InfoOutlined sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help' }} />
                                                            </Tooltip>
                                                        </Box>
                                                        <Typography variant="h4" sx={{ fontWeight: 700, color: '#4caf50' }}>{formatPercentOrNA(fin?.tasaDePago)}</Typography>
                                                        <Typography variant="caption" color="text.secondary" component="div">{fin?.countPagado ?? '—'} de {familiasActivas ?? '—'} familias con tarifa este período</Typography>
                                                        <Typography variant="caption" color="text.secondary" component="div">{fin?.studentsPagado ?? '—'} de {fin?.studentsActivas ?? '—'} estudiantes</Typography>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={12} sm={6} md={3}>
                                                    <Box sx={{ p: 2, background: 'white', borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <Typography variant="caption" color="text.secondary">Tasa de Mora</Typography>
                                                            <Tooltip title={
                                                                <Box>
                                                                    <Box sx={{ fontWeight: 600, fontSize: '0.8125rem', mb: 0.5, lineHeight: 1.3 }}>
                                                                        {"Porcentaje de familias con cuota generada en el período seleccionado que tienen mora sin pagar."}
                                                                    </Box>
                                                                    <Box sx={{ fontSize: '0.75rem', opacity: 0.8, lineHeight: 1.3 }}>
                                                                        {"Se considera en mora cuando el período tiene una penalidad pendiente (parcial o total). Solo se cuentan las familias que tienen una cuota facturada en este período."}
                                                                    </Box>
                                                                </Box>
                                                            } arrow>
                                                                <InfoOutlined sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help' }} />
                                                            </Tooltip>
                                                        </Box>
                                                        <Typography variant="h4" sx={{ fontWeight: 700, color: '#f44336' }}>{formatPercentOrNA(fin?.tasaDeMora)}</Typography>
                                                        <Typography variant="caption" color="text.secondary" component="div">{fin?.countMora ?? '—'} de {familiasActivas ?? '—'} familias con mora este período</Typography>
                                                        <Typography variant="caption" color="text.secondary" component="div">{fin?.studentsMora ?? '—'} de {fin?.studentsActivas ?? '—'} estudiantes</Typography>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={12} sm={6} md={3}>
                                                    <Box sx={{ p: 2, background: 'white', borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <Typography variant="caption" color="text.secondary">Eficiencia de Cobro</Typography>
                                                            <Tooltip title={
                                                                <Box>
                                                                    <Box sx={{ fontWeight: 600, fontSize: '0.8125rem', mb: 0.5, lineHeight: 1.3 }}>
                                                                        {"Porcentaje del monto neto facturado en el período que ya fue cobrado."}
                                                                    </Box>
                                                                    <Box sx={{ fontSize: '0.75rem', opacity: 0.8, lineHeight: 1.3 }}>
                                                                        {"El monto neto ya incluye los descuentos familiares."}
                                                                        <br />
                                                                        <br />
                                                                        {"Un 100% significa que se cobró todo lo esperado del período; un valor menor indica cuotas aún sin pagar. Puede superar 100% cuando en Base Caja entran pagos de cuotas de meses anteriores, o cuando hay adelantos; en Base Devengado, cuando el pago de una cuota se registra en un período anterior al que corresponde."}
                                                                    </Box>
                                                                </Box>
                                                            } arrow>
                                                                <InfoOutlined sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help' }} />
                                                            </Tooltip>
                                                        </Box>
                                                        <Typography variant="h4" sx={{ fontWeight: 700, color: '#2196f3' }}>{formatPercentOrNA(finByBasis(fin?.eficienciaCobro))}</Typography>
                                                        <Typography variant="caption" color="text.secondary">cobrado vs. monto neto facturado</Typography>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={12} sm={6} md={3}>
                                                    <Box sx={{ p: 2, background: 'white', borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <Typography variant="caption" color="text.secondary">Tasa de Puntualidad</Typography>
                                                            <Tooltip title={
                                                                <Box>
                                                                    <Box sx={{ fontWeight: 600, fontSize: '0.8125rem', mb: 0.5, lineHeight: 1.3 }}>
                                                                        {"De las familias que completaron su pago en el período seleccionado, qué porcentaje lo hizo antes o en la fecha límite de pago (sin generar mora)."}
                                                                    </Box>
                                                                    <Box sx={{ fontSize: '0.75rem', opacity: 0.8, lineHeight: 1.3 }}>
                                                                        {"Ejemplo: si 30 familias pagaron y 25 lo hicieron a tiempo, la tasa es 83.3%. Una tasa alta indica disciplina de pago en el colegio."}
                                                                    </Box>
                                                                </Box>
                                                            } arrow>
                                                                <InfoOutlined sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help' }} />
                                                            </Tooltip>
                                                        </Box>
                                                        <Typography variant="h4" sx={{ fontWeight: 700, color: '#00897b' }}>{formatPercentOrNA(fin?.tasaPuntualidad)}</Typography>
                                                        <Typography variant="caption" color="text.secondary" component="div">{fin?.countPagadoSinMora ?? '—'} de {fin?.countPagado ?? '—'} familias pagaron sin entrar en mora este período</Typography>
                                                        <Typography variant="caption" color="text.secondary" component="div">{fin?.studentsPagadoSinMora ?? '—'} de {fin?.studentsPagado ?? '—'} estudiantes</Typography>
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                        </Box>

                                        {/* Métricas Financieras */}
                                        <Typography variant="h6" sx={{ mb: 2, mt: 3, fontWeight: 600, color: '#1976d2' }}>💰 Métricas Financieras</Typography>
                                        <Box sx={{ mb: 3, p: 2, background: '#f9fafb', borderRadius: 2, boxShadow: 1 }}>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} sm={6} md={3}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                                        <Typography variant="body2" color="text.secondary"><strong>Ingreso por Tarifa</strong></Typography>
                                                        <Tooltip title={
                                                            <Box>
                                                                <Box sx={{ fontWeight: 600, fontSize: '0.8125rem', mb: 0.5, lineHeight: 1.3 }}>
                                                                    {"Total de tarifas del período seleccionado. No incluye mora ni pagos extraordinarios."}
                                                                </Box>
                                                                <Box sx={{ fontSize: '0.75rem', opacity: 0.8, lineHeight: 1.3 }}>
                                                                    {"Base Caja: pagos recibidos en este mes calendario, incluye cuotas atrasadas o adelantadas de otros meses."}
                                                                    <br />
                                                                    {"Base Devengado: lo cobrado de la cuota de este mes, sin importar cuándo se recibió."}
                                                                </Box>
                                                            </Box>
                                                        } arrow>
                                                            <InfoOutlined sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help' }} />
                                                        </Tooltip>
                                                    </Box>
                                                    <Typography variant="h6" sx={{ color: '#2196f3', fontWeight: 600 }}>{formatMoneyOrNA(finByBasis(fin?.ingresoTarifa))}</Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={6} md={3}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                                        <Typography variant="body2" color="text.secondary"><strong>Ingreso por Mora</strong></Typography>
                                                        <Tooltip title={
                                                            <Box>
                                                                <Box sx={{ fontWeight: 600, fontSize: '0.8125rem', mb: 0.5, lineHeight: 1.3 }}>
                                                                    {"Total cobrado por concepto de mora en el período seleccionado."}
                                                                </Box>
                                                                <Box sx={{ fontSize: '0.75rem', opacity: 0.8, lineHeight: 1.3 }}>
                                                                    {"Base Caja: pagos de mora recibidos en este mes. Base Devengado: mora cobrada que pertenece a este período (distribuida desde el mes con mora más antiguo)."}
                                                                </Box>
                                                            </Box>
                                                        } arrow>
                                                            <InfoOutlined sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help' }} />
                                                        </Tooltip>
                                                    </Box>
                                                    <Typography variant="h6" sx={{ color: '#ff9800', fontWeight: 600 }}>{formatMoneyOrNA(finByBasis(fin?.ingresoPorMora))}</Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={6} md={3}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                                        <Typography variant="body2" color="text.secondary"><strong>Ingreso por Inscripción</strong></Typography>
                                                        <Tooltip title={
                                                            <Box>
                                                                <Box sx={{ fontWeight: 600, fontSize: '0.8125rem', mb: 0.5, lineHeight: 1.3 }}>
                                                                    {"Total cobrado por cargos de inscripción del ciclo, con fecha de pago dentro del período seleccionado."}
                                                                </Box>
                                                                <Box sx={{ fontSize: '0.75rem', opacity: 0.8, lineHeight: 1.3 }}>
                                                                    {"La inscripción es un cargo único del ciclo escolar, no una cuota mensual: no tiene mes de devengo, así que esta cifra es siempre base Caja (fecha real del abono) y no cambia entre Caja y Devengado. No incluye exoneraciones: un descuento extraordinario baja el pendiente pero no es dinero que entró."}
                                                                </Box>
                                                            </Box>
                                                        } arrow>
                                                            <InfoOutlined sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help' }} />
                                                        </Tooltip>
                                                    </Box>
                                                    <Typography variant="h6" sx={{ color: '#43a047', fontWeight: 600 }}>{formatMoneyOrNA(fin?.ingresoInscripcion)}</Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={6} md={3}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                                        <Typography variant="body2" color="text.secondary"><strong>Tarifa Pendiente</strong></Typography>
                                                        <Tooltip title={
                                                            <Box>
                                                                <Box sx={{ fontWeight: 600, fontSize: '0.8125rem', mb: 0.5, lineHeight: 1.3 }}>
                                                                    {"Suma de todas las cuotas de tarifa sin pagar de las familias que tienen un período facturado en el mes seleccionado."}
                                                                </Box>
                                                                <Box sx={{ fontSize: '0.75rem', opacity: 0.8, lineHeight: 1.3 }}>
                                                                    {"Refleja cuánto falta por pagar de las cuotas de este período específico, sin incluir mora."}
                                                                </Box>
                                                            </Box>
                                                        } arrow>
                                                            <InfoOutlined sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help' }} />
                                                        </Tooltip>
                                                    </Box>
                                                    <Typography variant="h6" sx={{ color: '#f44336', fontWeight: 600 }}>{formatMoneyOrNA(fin?.totalPendiente)}</Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={6} md={3}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                                        <Typography variant="body2" color="text.secondary"><strong>Mora Pendiente</strong></Typography>
                                                        <Tooltip title={
                                                            <Box>
                                                                <Box sx={{ fontWeight: 600, fontSize: '0.8125rem', mb: 0.5, lineHeight: 1.3 }}>
                                                                    {"Suma de todas las penalidades por mora sin pagar de las familias que tienen un período facturado en el mes seleccionado."}
                                                                </Box>
                                                                <Box sx={{ fontSize: '0.75rem', opacity: 0.8, lineHeight: 1.3 }}>
                                                                    {"Corresponde a la mora generada específicamente en este período, no incluye mora de meses anteriores."}
                                                                </Box>
                                                            </Box>
                                                        } arrow>
                                                            <InfoOutlined sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help' }} />
                                                        </Tooltip>
                                                    </Box>
                                                    <Typography variant="h6" sx={{ color: '#d32f2f', fontWeight: 600 }}>{formatMoneyOrNA(fin?.moraPendiente)}</Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={6} md={3}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                                        <Typography variant="body2" color="text.secondary"><strong>Inscripción Pendiente</strong></Typography>
                                                        <Tooltip title={
                                                            <Box>
                                                                <Box sx={{ fontWeight: 600, fontSize: '0.8125rem', mb: 0.5, lineHeight: 1.3 }}>
                                                                    {"Saldo de inscripción sin cobrar al cierre del período seleccionado."}
                                                                </Box>
                                                                <Box sx={{ fontSize: '0.75rem', opacity: 0.8, lineHeight: 1.3 }}>
                                                                    {"Se calcula como los cargos de inscripción existentes a esa fecha, menos lo abonado y lo exonerado hasta esa fecha. Igual que el Crédito a favor Acumulado, es un saldo cortado al último día del período (o a hoy, lo que ocurra primero), no un movimiento del mes."}
                                                                </Box>
                                                            </Box>
                                                        } arrow>
                                                            <InfoOutlined sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help' }} />
                                                        </Tooltip>
                                                    </Box>
                                                    <Typography variant="h6" sx={{ color: '#e65100', fontWeight: 600 }}>{formatMoneyOrNA(fin?.inscripcionPendiente)}</Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={6} md={3}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                                        <Typography variant="body2" color="text.secondary"><strong>Crédito a favor Acumulado</strong></Typography>
                                                        <Tooltip title={
                                                            <Box>
                                                                <Box sx={{ fontWeight: 600, fontSize: '0.8125rem', mb: 0.5, lineHeight: 1.3 }}>
                                                                    {"Saldo a favor total de todas las familias del ciclo al cierre del período seleccionado."}
                                                                </Box>
                                                                <Box sx={{ fontSize: '0.75rem', opacity: 0.8, lineHeight: 1.3 }}>
                                                                    {"Se genera cuando una familia paga un monto mayor al de su tarifa del mes. Ese excedente queda como crédito disponible que se aplica automáticamente a la tarifa del próximo período al generar la facturación mensual."}
                                                                </Box>
                                                                <Box sx={{ fontSize: '0.75rem', opacity: 0.8, lineHeight: 1.3, mt: 0.5 }}>
                                                                    {"El crédito ya reintegrado a la familia no cuenta acá — ver \"Crédito a favor Reintegrado\"."}
                                                                </Box>
                                                            </Box>
                                                        } arrow>
                                                            <InfoOutlined sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help' }} />
                                                        </Tooltip>
                                                    </Box>
                                                    <Typography variant="h6" sx={{ color: '#9c27b0', fontWeight: 600 }}>{formatMoneyOrNA(fin?.creditoAcumulado)}</Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={6} md={3}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                                        <Typography variant="body2" color="text.secondary"><strong>Crédito a favor Reintegrado</strong></Typography>
                                                        <Tooltip title={
                                                            <Box>
                                                                <Box sx={{ fontWeight: 600, fontSize: '0.8125rem', mb: 0.5, lineHeight: 1.3 }}>
                                                                    {"Total de crédito a favor devuelto a las familias del ciclo por transferencia, acumulado hasta el cierre del período seleccionado."}
                                                                </Box>
                                                                <Box sx={{ fontSize: '0.75rem', opacity: 0.8, lineHeight: 1.3 }}>
                                                                    {"Complementa el Crédito a favor Acumulado: lo acumulado es lo que las familias aún tienen a favor; lo reintegrado es lo que ya se les devolvió. No forma parte de los ingresos."}
                                                                </Box>
                                                            </Box>
                                                        } arrow>
                                                            <InfoOutlined sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help' }} />
                                                        </Tooltip>
                                                    </Box>
                                                    <Typography variant="h6" sx={{ color: '#9c27b0', fontWeight: 600 }}>{formatMoneyOrNA(fin?.creditoReintegrado)}</Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={6} md={3}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                                        <Typography variant="body2" color="text.secondary"><strong>Ingreso Tarifa Esperado (Bruto)</strong></Typography>
                                                        <Tooltip title={
                                                            <Box>
                                                                <Box sx={{ fontWeight: 600, fontSize: '0.8125rem', mb: 0.5, lineHeight: 1.3 }}>
                                                                    {"Monto total de tarifa que se obtendría si todas las familias pagaran completo en el período seleccionado, SIN considerar descuentos familiares."}
                                                                </Box>
                                                                <Box sx={{ fontSize: '0.75rem', opacity: 0.8, lineHeight: 1.3 }}>
                                                                    {"Refleja el ingreso máximo potencial de tarifa del período."}
                                                                </Box>
                                                            </Box>
                                                        } arrow>
                                                            <InfoOutlined sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help' }} />
                                                        </Tooltip>
                                                    </Box>
                                                    <Typography variant="h6" sx={{ color: '#1565c0', fontWeight: 600 }}>{formatMoneyOrNA(fin?.ingresoEsperadoBruto)}</Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={6} md={3}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                                        <Typography variant="body2" color="text.secondary"><strong>Ingreso Tarifa Esperado (Neto)</strong></Typography>
                                                        <Tooltip title={
                                                            <Box>
                                                                <Box sx={{ fontWeight: 600, fontSize: '0.8125rem', mb: 0.5, lineHeight: 1.3 }}>
                                                                    {"Monto total de tarifa que se obtendría si todas las familias pagaran completo en el período seleccionado, CON descuentos familiares aplicados."}
                                                                </Box>
                                                                <Box sx={{ fontSize: '0.75rem', opacity: 0.8, lineHeight: 1.3 }}>
                                                                    {"Representa el ingreso esperado real del período después de aplicar los descuentos familiares."}
                                                                </Box>
                                                            </Box>
                                                        } arrow>
                                                            <InfoOutlined sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help' }} />
                                                        </Tooltip>
                                                    </Box>
                                                    <Typography variant="h6" sx={{ color: '#2e7d32', fontWeight: 600 }}>{formatMoneyOrNA(fin?.ingresoEsperadoNeto)}</Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={6} md={3}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                                        <Typography variant="body2" color="text.secondary"><strong>Total Descuentos</strong></Typography>
                                                        <Tooltip title={
                                                            <Box>
                                                                <Box sx={{ fontWeight: 600, fontSize: '0.8125rem', mb: 0.5, lineHeight: 1.3 }}>
                                                                    {"Suma de descuentos aplicados a las cuotas de tarifa en el período seleccionado."}
                                                                </Box>
                                                                <Box sx={{ fontSize: '0.75rem', opacity: 0.8, lineHeight: 1.3 }}>
                                                                    {"Incluye el descuento especial permanente de cada familia más los descuentos extraordinarios manuales realizados en el período."}
                                                                </Box>
                                                            </Box>
                                                        } arrow>
                                                            <InfoOutlined sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help' }} />
                                                        </Tooltip>
                                                    </Box>
                                                    <Typography variant="h6" sx={{ color: '#795548', fontWeight: 600 }}>{formatMoneyOrNA(fin?.totalDescuentos)}</Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={6} md={3}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                                        <Typography variant="body2" color="text.secondary"><strong>Mora Exonerada</strong></Typography>
                                                        <Tooltip title={
                                                            <Box>
                                                                <Box sx={{ fontWeight: 600, fontSize: '0.8125rem', mb: 0.5, lineHeight: 1.3 }}>
                                                                    {"Monto total de mora que fue exonerada o descontada en el período seleccionado."}
                                                                </Box>
                                                                <Box sx={{ fontSize: '0.75rem', opacity: 0.8, lineHeight: 1.3 }}>
                                                                    {"Corresponde a descuentos de penalidad aplicados a las cuotas de las familias, ya sea por exoneraciones parciales o totales."}
                                                                </Box>
                                                            </Box>
                                                        } arrow>
                                                            <InfoOutlined sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help' }} />
                                                        </Tooltip>
                                                    </Box>
                                                    <Typography variant="h6" sx={{ color: '#607d8b', fontWeight: 600 }}>{formatMoneyOrNA(fin?.descuentosMoraExonerados)}</Typography>
                                                </Grid>
                                            </Grid>
                                        </Box>

                                        

                                        {/* Gráficos */}
                                        <Grid container spacing={3} sx={{ mt: 2 }}>
                                            <Grid item xs={12} md={12}>
                                                <Box sx={{ p: 2, background: 'white', borderRadius: 2, boxShadow: 1 }}>
                                                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                                                        📈 Ingresos Mensuales (Pagos + Extraordinarios)
                                                </Typography>
                                                    <Box sx={{ width: '100%', height: 350 }}>
                                                    <ResponsiveContainer width="100%" height="100%">
                                                        <BarChart
                                                                data={(combinedEarnings || []).map(item => ({ 
                                                                    ...item, 
                                                                    label: moment({ year: item.year, month: item.month - 1 }).format('MMM YY'),
                                                                    fullLabel: moment({ year: item.year, month: item.month - 1 }).format('MMMM YYYY')
                                                                }))}
                                                        >
                                                            <CartesianGrid strokeDasharray="3 3" />
                                                                <XAxis dataKey="label" angle={-45} textAnchor="end" height={80} />
                                                                <YAxis tickFormatter={(value) => `Q${(value/1000).toFixed(0)}k`} />
                                                                <RechartsTooltip 
                                                                    formatter={(value) => [`Q ${Number(value).toLocaleString('es-GT', {minimumFractionDigits: 2})}`, 'Ingreso']}
                                                                    labelFormatter={(label, payload) => payload && payload[0] ? payload[0].payload.fullLabel : label}
                                                                />
                                                            <Legend />
                                                                <Bar dataKey="total" name="Ingreso Total" radius={[8, 8, 0, 0]}>
                                                                    {(combinedEarnings || []).map((entry, index) => {
                                                                        const colors = ['#667eea', '#764ba2', '#f093fb', '#4facfe', '#43e97b', '#fa709a', '#fee140', '#30cfd0'];
                                                                return <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />;
                                                            })}
                                                            </Bar>
                                                        </BarChart>
                                                    </ResponsiveContainer>
                                                    </Box>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                ) : (
                                    <Box sx={{ mt: 2 }}>
                                        <Typography variant="body2" color="text.secondary">Análisis no disponible. Pulsa Actualizar para intentar cargar datos.</Typography>
                                    </Box>
                                )}
                            </Collapse>
                        </CardContent>
                    </SectionCard>
                </Grid>
                {/* Sección de Pagos Extraordinarios: reutilizamos el componente compartido.
                    Su listado exige GET /payments/extraordinary (pagos-listar-extraordinarios). */}
                {hasPermission('pagos-listar-extraordinarios') && (
                <Grid item xs={12}>
                    <SectionCard sx={{ mb: 2 }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <IconButton
                                        size="small"
                                        onClick={() => setCollapsedExtra(s => !s)}
                                        aria-label={collapsedExtra ? 'Expandir registro extraordinario' : 'Colapsar registro extraordinario'}
                                    >
                                        <ExpandMoreIcon sx={{ transform: collapsedExtra ? 'rotate(0deg)' : 'rotate(180deg)', transition: 'transform 0.2s' }} />
                                    </IconButton>
                                    <Box>
                                        <Typography variant="h6" sx={{ fontWeight: 700 }}>Registro de Pago Extraordinario</Typography>
                                        <Typography variant="caption" color="text.secondary">Registro y listado de pagos extraordinarios del colegio</Typography>
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button size="small" onClick={() => fetchPaymentsAnalysis(schoolId)} sx={{ textTransform: 'none' }}>Actualizar</Button>
                                </Box>
                            </Box>

                            <Collapse in={!collapsedExtra} timeout="auto" unmountOnExit>
                                {/* Inner light box to match Analysis visuals */}
                                <Box sx={{ mt: 2, p: 2, background: '#f9fafb', borderRadius: 2, boxShadow: 1 }}>
                                    <ExtraordinaryPaymentSection noWrapper={true} initialSchoolId={schoolId} hideSchoolSelect={true} onPaymentCreated={(newExtra) => {
                                        setSnackbar({ open: true, message: 'Pago extraordinario registrado', severity: 'success' });
                                        // refresh analysis and payments
                                        fetchPaymentsAnalysis(schoolId);
                                        fetchAllPayments(statusFilter, search);
                                    }} />
                                </Box>
                            </Collapse>
                        </CardContent>
                    </SectionCard>
                </Grid>
                )}
                <Grid item xs={12}>
                    <ChipsRow>
                        <ToggleButtonGroup
                            value={countersView}
                            exclusive
                            onChange={(e, newView) => {
                                if (newView !== null) setCountersView(newView);
                            }}
                            size="small"
                            sx={{ mr: 2 }}
                        >
                            <ToggleButton value="payment">Estado de Pago</ToggleButton>
                            <ToggleButton value="service">Estado del Servicio</ToggleButton>
                        </ToggleButtonGroup>
                        
                        {countersView === 'payment' ? (
                            <>
                                <Chip label={`Pagados: ${displayPaid}`} color="success" />
                                <Chip label={`Pendientes: ${displayPendiente}`} color="warning" />
                                <Chip label={`En Mora: ${displayMora}`} color="error" />
                                <Chip label={`Adelantado: ${displayAdelantado}`} sx={{ ml: 1, backgroundColor: '#1976D2', color: '#fff' }} />
                                <Chip label={`Pago Parcial: ${displayParcial}`} sx={{ ml: 1, backgroundColor: '#7c4dff', color: '#fff' }} />
                            </>
                        ) : (
                            <>
                                <Chip label={`Activos: ${serviceActiveCount}`} color="success" />
                                <Chip label={`Pausados: ${servicePausedCount}`} color="warning" />
                                <Chip label={`Suspendidos: ${serviceSuspendedCount}`} color="error" />
                                <Chip label={`Inactivos: ${serviceInactiveCount}`} sx={{ backgroundColor: '#9e9e9e', color: 'white' }} />
                                <Chip label={`Eliminados: ${eliminadoCount}`} sx={{ backgroundColor: '#000000', color: 'white', ml: 1 }} />
                            </>
                        )}
                        
                        <Box sx={{ flex: 1 }} />
                    </ChipsRow>
                </Grid>

                <Grid item xs={12}>
                    <Paper sx={{ p: 2 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 2,
                                mb: 2,
                                alignItems: { xs: 'stretch', sm: 'center' },
                                flexDirection: { xs: 'column', sm: 'row' },
                                pr: { xs: 0, sm: 6 },
                            }}
                        >
                            <PaymentFilters 
                                search={search} 
                                onSearchChange={setSearch} 
                                status={statusFilter} 
                                onStatusChange={setStatusFilter} 
                                autoDebit={autoDebitFilter} 
                                onAutoDebitChange={setAutoDebitFilter} 
                                showInactive={showInactive}
                                onShowInactiveChange={setShowInactive}
                                showDeleted={showDeleted} 
                                onShowDeletedChange={setShowDeleted}
                                serviceStatus={serviceStatusFilter}
                                onServiceStatusChange={setServiceStatusFilter}
                            />
                            <Button
                                startIcon={<DownloadIcon />}
                                size="small"
                                onClick={handleOpenDownloadMenu}
                                sx={{ textTransform: 'none', ml: { sm: 'auto' }, alignSelf: { xs: 'flex-start', sm: 'center' } }}
                            >
                                Descargar
                            </Button>
                            <Menu
                                anchorEl={downloadMenuAnchorEl}
                                open={downloadMenuOpen}
                                onClose={handleCloseDownloadMenu}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                            >
                                <MenuItem
                                    onClick={() => {
                                        handleCloseDownloadMenu();
                                        setOpenExportStatusDialog(true);
                                    }}
                                >
                                    Descargar Historial
                                </MenuItem>
                                <MenuItem
                                    onClick={() => {
                                        handleCloseDownloadMenu();
                                        setOpenExportByStateDialog(true);
                                    }}
                                >
                                    Descargar por Estado
                                </MenuItem>
                            </Menu>
                            {/* Enviar Recordatorio button hidden temporarily per request. Restore when needed:
                                <Button startIcon={<SendIcon />} onClick={() => setSnackbar({ open: true, message: 'Enviar recordatorios (pendiente)', severity: 'info' })}>
                                    Enviar Recordatorio
                                </Button>
                            */}
                        </Box>

                        <PaymentTable
                            payments={pageSlice}
                            onRegisterClick={hasPermission('pagos-pagar-tarifa') ? handleOpenRegister : undefined}
                            onManageClick={hasPermission('pagos-ver-detalle') ? handleManagePayments : undefined}
                            onManagePeriodsClick={hasPermission('pagos-ver-detalle') ? handleManagePeriods : undefined}
                            onNotesClick={hasPermission('pagos-ver-detalle') ? handleOpenNotes : undefined}
                            onDownloadHistory={hasPermission('pagos-ver-detalle') ? handleDownloadHistory : undefined}
                            order={order}
                            orderBy={orderBy}
                            onRequestSort={handleRequestSort}
                        />

                        <ManagePaymentsModal
                            open={openManageModal}
                            onClose={() => setOpenManageModal(false)}
                            payment={manageTarget}
                            onAction={handleManageAction}
                            onToggleInvoiceSent={handleToggleInvoiceSent}
                        />

                        <ManagePeriodsModal
                            open={openPeriodsModal}
                            onClose={() => setOpenPeriodsModal(false)}
                            payment={periodsTarget}
                            onChanged={() => {
                                // Períodos impactan balances/estados, refrescar dataset y análisis
                                fetchAllPayments(statusFilter, search);
                                fetchPaymentsAnalysis(schoolId);
                            }}
                        />

                        <CreateSchoolPeriodModal
                            open={openCreateSchoolPeriodModal}
                            onClose={() => setOpenCreateSchoolPeriodModal(false)}
                            schoolId={schoolId}
                            schoolSchedules={school?.schedules}
                            onCreated={(message) => {
                                // refrescar datos tras creación
                                fetchAllPayments(statusFilter, search);
                                fetchPaymentsAnalysis(schoolId);
                                if (message) {
                                    setSnackbar({ open: true, message, severity: 'success' });
                                }
                            }}
                        />

                        <CreditRefundModal
                            open={openCreditRefundModal}
                            onClose={() => setOpenCreditRefundModal(false)}
                            schoolId={schoolId}
                            cicloEscolarId={currentCicloEscolarId}
                            buildPaymentParams={buildPaymentParams}
                            onApplied={(message) => {
                                fetchAllPayments(statusFilter, search);
                                fetchMetrics(selectedPeriod);
                                if (message) {
                                    setSnackbar({ open: true, message, severity: 'success' });
                                }
                            }}
                        />

                        {/* Dialog: Exportar pagos por periodo */}
                        <Dialog
                            open={openExportStatusDialog}
                            onClose={() => setOpenExportStatusDialog(false)}
                            maxWidth="xs"
                            PaperProps={{
                                sx: {
                                    borderRadius: 2,
                                    overflow: 'visible',
                                    width: 380,
                                    maxWidth: 'calc(100% - 32px)'
                                }
                            }}
                        >
                            <DialogTitle sx={{ pb: 2 }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                        Exportar historial de pagos
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Selecciona período y filtros para el reporte en excel.
                                    </Typography>
                                </Box>
                            </DialogTitle>
                            <DialogContent sx={{ pt: 2.5, overflow: 'visible' }}>
                                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                                    <Box sx={{ width: '100%', maxWidth: 380, display: 'flex', flexDirection: 'column', gap: 2, mt: 0.5 }}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel id="export-finalstatus-label">Estado de Pago</InputLabel>
                                        <Select
                                            labelId="export-finalstatus-label"
                                            label="Estado de Pago"
                                            value={exportFinalStatus}
                                            onChange={(e) => setExportFinalStatus(e.target.value)}
                                        >
                                            <MenuItem value="TODOS">Todos los estados</MenuItem>
                                            <MenuItem value="PAGADO">PAGADO</MenuItem>
                                            <MenuItem value="PENDIENTE">PENDIENTE</MenuItem>
                                            <MenuItem value="MORA">MORA</MenuItem>
                                            <MenuItem value="ELIMINADO">ELIMINADO</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <FormControl fullWidth size="small">
                                        <InputLabel id="export-servicestatus-label">Estado del Servicio</InputLabel>
                                        <Select
                                            labelId="export-servicestatus-label"
                                            label="Estado del Servicio"
                                            value={exportServiceStatus}
                                            onChange={(e) => setExportServiceStatus(e.target.value)}
                                        >
                                            <MenuItem value="TODOS">Todos los estados</MenuItem>
                                            <MenuItem value="ACTIVE">ACTIVO</MenuItem>
                                            <MenuItem value="PAUSED">PAUSADO</MenuItem>
                                            <MenuItem value="SUSPENDED">SUSPENDIDO</MenuItem>
                                            <MenuItem value="INACTIVE">INACTIVO</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <FormControl fullWidth size="small">
                                        <InputLabel id="export-month-label">Mes</InputLabel>
                                        <Select
                                            labelId="export-month-label"
                                            label="Mes"
                                            value={exportMonth}
                                            onChange={(e) => setExportMonth(e.target.value)}
                                            MenuProps={{
                                                PaperProps: {
                                                    sx: {
                                                        maxHeight: 220
                                                    }
                                                }
                                            }}
                                        >
                                            <MenuItem value="TODOS">Todos los meses</MenuItem>
                                            <MenuItem value="01">Enero</MenuItem>
                                            <MenuItem value="02">Febrero</MenuItem>
                                            <MenuItem value="03">Marzo</MenuItem>
                                            <MenuItem value="04">Abril</MenuItem>
                                            <MenuItem value="05">Mayo</MenuItem>
                                            <MenuItem value="06">Junio</MenuItem>
                                            <MenuItem value="07">Julio</MenuItem>
                                            <MenuItem value="08">Agosto</MenuItem>
                                            <MenuItem value="09">Septiembre</MenuItem>
                                            <MenuItem value="10">Octubre</MenuItem>
                                            <MenuItem value="11">Noviembre</MenuItem>
                                            <MenuItem value="12">Diciembre</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <FormControl fullWidth size="small">
                                        <InputLabel id="export-year-label">Año</InputLabel>
                                        <Select
                                            labelId="export-year-label"
                                            label="Año"
                                            value={exportYear}
                                            onChange={(e) => setExportYear(e.target.value)}
                                            MenuProps={{
                                                PaperProps: {
                                                    sx: {
                                                        maxHeight: 220
                                                    }
                                                }
                                            }}
                                        >
                                            {exportYearOptions.map((y) => (
                                                <MenuItem key={y} value={y}>{y}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    </Box>
                                </Box>
                            </DialogContent>
                            <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
                                <Button onClick={() => setOpenExportStatusDialog(false)}>
                                    Cancelar
                                </Button>
                                <Button variant="contained" onClick={() => handleDownloadPaymentsByStatus(exportMonth, exportYear)}>
                                    Descargar
                                </Button>
                            </DialogActions>
                        </Dialog>

                        {/* Dialog: Exportar por Estado (nuevo) */}
                        <Dialog
                            open={openExportByStateDialog}
                            onClose={() => setOpenExportByStateDialog(false)}
                            maxWidth="xs"
                            PaperProps={{
                                sx: {
                                    borderRadius: 2,
                                    overflow: 'visible',
                                    width: 380,
                                    maxWidth: 'calc(100% - 32px)'
                                }
                            }}
                        >
                            <DialogTitle sx={{ pb: 2 }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                        Exportar pagos
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Selecciona el estado de pago y el estado del servicio.
                                    </Typography>
                                </Box>
                            </DialogTitle>
                            <DialogContent sx={{ pt: 2.5, overflow: 'visible' }}>
                                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                                    <Box sx={{ width: '100%', maxWidth: 380, display: 'flex', flexDirection: 'column', gap: 2, mt: 0.5 }}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel id="export-by-state-label">Estado de Pago</InputLabel>
                                        <Select
                                            labelId="export-by-state-label"
                                            label="Estado de Pago"
                                            value={exportByStateValue}
                                            onChange={(e) => setExportByStateValue(e.target.value)}
                                        >
                                            <MenuItem value="TODOS">Todos los estados</MenuItem>
                                            <MenuItem value="PAGADO">PAGADO</MenuItem>
                                            <MenuItem value="PENDIENTE">PENDIENTE</MenuItem>
                                            <MenuItem value="MORA">MORA</MenuItem>
                                            <MenuItem value="ELIMINADO">ELIMINADO</MenuItem>
                                        </Select>
                                    </FormControl>

                                    <FormControl fullWidth size="small">
                                        <InputLabel id="export-by-state-servicestatus-label">Estado del Servicio</InputLabel>
                                        <Select
                                            labelId="export-by-state-servicestatus-label"
                                            label="Estado del Servicio"
                                            value={exportByStateServiceStatus}
                                            onChange={(e) => setExportByStateServiceStatus(e.target.value)}
                                        >
                                            <MenuItem value="TODOS">Todos los estados</MenuItem>
                                            <MenuItem value="ACTIVE">ACTIVO</MenuItem>
                                            <MenuItem value="PAUSED">PAUSADO</MenuItem>
                                            <MenuItem value="SUSPENDED">SUSPENDIDO</MenuItem>
                                            <MenuItem value="INACTIVE">INACTIVO</MenuItem>
                                        </Select>
                                    </FormControl>
                                    </Box>
                                </Box>
                            </DialogContent>
                            <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
                                <Button onClick={() => setOpenExportByStateDialog(false)}>
                                    Cancelar
                                </Button>
                                <Button variant="contained" onClick={() => handleDownloadByState(exportByStateValue)}>
                                    Descargar
                                </Button>
                            </DialogActions>
                        </Dialog>

                        {/* Dialog: Registrar Pago */}
                        <Dialog open={openRegisterDialog} onClose={() => { setOpenRegisterDialog(false); setSelectedReceipt(null); setReceiptZoom(1); setPaymentTab(0); }} fullWidth maxWidth="lg">
                            <DialogTitle>Registrar Pago</DialogTitle>
                            <DialogContent>
                                <Box sx={{ position: 'relative' }}>
                                <Grid container spacing={2}>
                                    {/* Panel de Boletas (Columna Izquierda - Siempre Visible) */}
                                    <Grid item xs={12} md={7}>
                                        <Box sx={{ border: '1px solid rgba(0,0,0,0.06)', borderRadius: 1, p: 1, height: 580, overflow: 'auto', backgroundColor: '#fff' }}>
                                            <ReceiptsPane
                                                uploadedReceipts={uploadedReceipts}
                                                uploadedReceiptsLoading={uploadedReceiptsLoading}
                                                boletaMonth={boletaMonth}
                                                setBoletaMonth={setBoletaMonth}
                                                boletaMonthOptions={boletaMonthOptions}
                                                filteredUploadedReceipts={filteredUploadedReceipts}
                                                selectedReceipt={selectedReceipt}
                                                setSelectedReceipt={setSelectedReceipt}
                                                receiptZoom={receiptZoom}
                                                setReceiptZoom={setReceiptZoom}
                                                canManageReceipts={hasPermission('admin-subir-boletas')}
                                                uploadReceiptLoading={receiptUploadLoading}
                                                onUploadReceipt={handleUploadRegisterReceipt}
                                                onReceiptError={(message) => setSnackbar({ open: true, message, severity: 'warning' })}
                                                onChangeReceiptStatus={handleChangeReceiptStatus}
                                                downloadFile={(url, name) => {
                                                    const a = document.createElement('a');
                                                    a.href = url;
                                                    a.download = name || '';
                                                    document.body.appendChild(a);
                                                    a.click();
                                                    document.body.removeChild(a);
                                                }}
                                            />
                                        </Box>
                                    </Grid>
                                    
                                    {/* Panel de Formulario (Columna Derecha - Con Pestañas) */}
                                    <Grid item xs={12} md={5}>
                                        {/* Pestañas para separar Pago de Tarifa y Pago de Mora */}
                                        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                                            <Tabs value={paymentTab} onChange={(e, newValue) => setPaymentTab(newValue)}>
                                                <Tab label="Pago de Tarifa" />
                                                <Tab
                                                    label={`Pago de Mora ${effectivePenaltyDue > 0 ? `(Q ${effectivePenaltyDue.toFixed(2)})` : '(Q 0.00)'}`}
                                                    sx={{
                                                        color: effectivePenaltyDue > 0 ? 'error.main' : 'inherit',
                                                        fontWeight: effectivePenaltyDue > 0 ? 700 : 400,
                                                        '&.Mui-selected': {
                                                            color: effectivePenaltyDue > 0 ? 'error.main' : 'primary.main'
                                                        }
                                                    }}
                                                />
                                                <Tab label="Inscripción" />
                                            </Tabs>
                                        </Box>

                                        {/* Tab Panel 0: Pago de Tarifa */}
                                        {paymentTab === 0 && (
                                        <Box sx={{ maxWidth: 420 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                {registerPaymentTarget ? `Familia: ${registerPaymentTarget.User?.FamilyDetail?.familyLastName || ''}` : ''}
                                                {registerPaymentTarget && (
                                                    ` (${studentCount} ${studentCount === 1 ? 'estudiante' : 'estudiantes'})`
                                                )}
                                            </Typography>
                                            {/* Show D/A chip when automatic debit is active on the payment or the family's record */}
                                            { (registerPaymentTarget && (registerPaymentTarget.automaticDebit || registerPaymentTarget.User?.FamilyDetail?.automaticDebit)) && (
                                                <Chip label="D/A" size="small" color="primary" sx={{ height: 22, fontSize: '0.75rem' }} />
                                            ) }
                                            {registerPaymentTarget?.autoDebit && (
                                                <Chip label="D/A" size="small" color="primary" sx={{ height: 22, fontSize: '0.75rem' }} />
                                            )}
                                        </Box>
                                        {/* Payment summary block: Desglose detallado del cálculo */}
                                        <Box sx={{ mb: 2, p: 2, backgroundColor: '#fafafa', borderRadius: 1, border: '1px solid rgba(0,0,0,0.04)' }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>📋 Desglose de Pago</Typography>
                                            
                                            {/* Sección: Cargos Base */}
                                            {dialogPendingPeriods.length <= 1 && (
                                                <Box sx={{ mb: 2, pb: 1.5, borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                                                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'primary.main', display: 'block', mb: 1 }}>
                                                        CARGOS BASE
                                                    </Typography>
                                                    
                                                    {/* Tarifa base por estudiante */}
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Tarifa base
                                                            <Typography component="span" variant="caption" sx={{ ml: 0.5, color: 'text.disabled' }}>
                                                                ({effectiveRouteType})
                                                            </Typography>
                                                        </Typography>
                                                        <Typography variant="body2">{formatCurrency(effectiveBaseFee)}</Typography>
                                                    </Box>
                                                    
                                                    {/* Tarifa mensual total (base × estudiantes) */}
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Tarifa mensual
                                                            <Typography component="span" variant="caption" sx={{ ml: 0.5, color: 'text.disabled' }}>
                                                                            ({cargosStudents} {cargosStudents === 1 ? 'estudiante' : 'estudiantes'})
                                                                        </Typography>
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{formatCurrency(effectiveMonthlyFee)}</Typography>
                                                    </Box>
                                                </Box>
                                            )}

                                            {/* Sección: Descuentos y Créditos */}
                                            {(() => {
                                                // When there are multiple unpaid periods, the family discount can vary per period.
                                                // Avoid showing a misleading single "Descuento familiar" line here.
                                                const showFamilyLine = dialogFamilySpecialFee > 0 && !hasMultiplePendingPeriods;
                                                const showBlock = dialogCredito > 0 || showFamilyLine || dialogExtraDiscount > 0;
                                                if (!showBlock) return null;

                                                return (
                                                <Box sx={{ mb: 2, pb: 1.5, borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                                                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'success.main', display: 'block', mb: 1 }}>
                                                        DESCUENTOS Y CRÉDITOS
                                                    </Typography>
                                                    
                                                    {dialogCredito > 0 && (
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                                                            <Typography variant="body2" color="text.secondary">
                                                                <Box component="span" title="Crédito generado por pagos anteriores donde el monto excedió la tarifa del mes. Se aplica automáticamente." sx={{ borderBottom: '1px dashed #999', cursor: 'help' }}>
                                                                    Crédito a favor ↗
                                                                </Box>
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 500 }}>
                                                                - {formatCurrency(dialogCredito)}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                    
                                                    {showFamilyLine && (
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                                                            <Typography variant="body2" color="text.secondary">
                                                                Descuento familiar
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 500 }}>
                                                                - {formatCurrency(dialogFamilySpecialFee)}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                    
                                                    {dialogExtraDiscount > 0 && (
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                                                            <Typography variant="body2" color="text.secondary">Descuento extraordinario</Typography>
                                                            <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 500 }}>
                                                                - {formatCurrency(dialogExtraDiscount)}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </Box>
                                                );
                                            })()}

                                            {/* Desglose por períodos pendientes si hay varios meses */}
                                            {(() => {
                                                try {
                                                    const periods = dialogPendingPeriods;
                                                    
                                                    if (Array.isArray(periods) && periods.length > 1) {
                                                        const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
                                                        
                                                        return (
                                                            <Box sx={{ mb: 2, pb: 1.5, borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                                                                <Box sx={{ p: 1.5, bgcolor: '#fff3e0', borderRadius: 1, border: '1px solid #ff9800' }}>
                                                                    <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1.5, color: '#e65100' }}>
                                                                        ⚠️ Períodos pendientes ({periods.length} meses):
                                                                    </Typography>
                                                                    {periods.map((period, idx) => {
                                                                        const periodDate = moment(period.period);
                                                                        const monthName = monthNames[periodDate.month()];
                                                                        const year = periodDate.year();
                                                                        const dueDate = period.dueDate ? moment(period.dueDate).format('DD/MM/YY') : 'N/A';
                                                                        
                                                                        // Verificar si es pago parcial
                                                                        const originalAmount = Number(period.originalAmount ?? dialogMonthlyFee ?? 0) || 0;
                                                                        const periodDiscount = Number(period.discountApplied ?? 0) || 0;
                                                                        const periodNetAmount = Number(period.netAmount ?? (originalAmount - periodDiscount)) || 0;
                                                                        const periodAmountDue = Number(period.amountDue ?? period.amount ?? 0) || 0;

                                                                        const periodRouteType = period.routeType || routeType;

                                                                        // Tarifa base por estudiante para este período (preferir snapshot del período)
                                                                        const periodStudents = Number(period?.studentsCount ?? registerPaymentTarget?.studentCount ?? studentCount) || 1;
                                                                        const periodBaseFee = periodStudents > 0 ? (originalAmount / periodStudents) : originalAmount;

                                                                        const isParcial = periodAmountDue < periodNetAmount && periodAmountDue > 0;
                                                                        const amountPaid = isParcial
                                                                            ? (periodNetAmount - periodAmountDue)
                                                                            : (Number(period.amountPaid ?? 0) || 0);
                                                                        
                                                                        return (
                                                                            <Box key={idx} sx={{ mb: idx < periods.length - 1 ? 2 : 0, pb: idx < periods.length - 1 ? 2 : 0, borderBottom: idx < periods.length - 1 ? '1px dashed rgba(0,0,0,0.15)' : 'none' }}>
                                                                                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5, color: 'text.primary' }}>
                                                                                    • {monthName} {year}
                                                                                    <Typography component="span" sx={{ fontSize: '0.65rem', ml: 0.5, color: 'text.disabled' }}>
                                                                                        (Vence: {dueDate})
                                                                                    </Typography>
                                                                                    {isParcial && (
                                                                                        <Typography component="span" sx={{ 
                                                                                            fontSize: '0.65rem', 
                                                                                            ml: 1, 
                                                                                            px: 0.75, 
                                                                                            py: 0.25,
                                                                                            backgroundColor: '#fff3e0',
                                                                                            color: '#e65100',
                                                                                            borderRadius: '4px',
                                                                                            fontWeight: 600
                                                                                        }}>
                                                                                            📝 PAGO PARCIAL
                                                                                        </Typography>
                                                                                    )}
                                                                                </Typography>

                                                                                {/* Tarifa base */}
                                                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5, ml: 2 }}>
                                                                                    <Typography variant="caption" color="text.secondary">
                                                                                        Tarifa base
                                                                                        <Typography component="span" variant="caption" sx={{ ml: 0.5, color: 'text.disabled' }}>
                                                                                            ({periodRouteType})
                                                                                        </Typography>
                                                                                        :
                                                                                    </Typography>
                                                                                    <Typography variant="caption">
                                                                                        {formatCurrency(periodBaseFee)}
                                                                                    </Typography>
                                                                                </Box>
                                                                                
                                                                                {/* Tarifa mensual */}
                                                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5, ml: 2 }}>
                                                                                    <Typography variant="caption" color="text.secondary">
                                                                                        Tarifa mensual ({periodStudents} {periodStudents === 1 ? 'estudiante' : 'estudiantes'}):
                                                                                    </Typography>
                                                                                    <Typography variant="caption">
                                                                                        {formatCurrency(originalAmount)}
                                                                                    </Typography>
                                                                                </Box>
                                                                                
                                                                                {/* Descuento familiar aplicado a este período (puede variar por mes) */}
                                                                                {periodDiscount > 0 && (
                                                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.3, ml: 2 }}>
                                                                                        <Typography variant="caption" color="text.secondary">
                                                                                            Descuento familiar:
                                                                                        </Typography>
                                                                                        <Typography variant="caption" sx={{ color: 'success.main' }}>
                                                                                            - {formatCurrency(periodDiscount)}
                                                                                        </Typography>
                                                                                    </Box>
                                                                                )}
                                                                                
                                                                                {/* Mostrar desglose de pago parcial */}
                                                                                {isParcial && (
                                                                                    <>
                                                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.3, ml: 2 }}>
                                                                                            <Typography variant="caption" sx={{ color: 'info.main', fontStyle: 'italic' }}>
                                                                                                Ya pagado:
                                                                                            </Typography>
                                                                                            <Typography variant="caption" sx={{ color: 'info.main', fontWeight: 500 }}>
                                                                                                {formatCurrency(amountPaid)}
                                                                                            </Typography>
                                                                                        </Box>
                                                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.3, ml: 2 }}>
                                                                                            <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: 500 }}>
                                                                                                Saldo pendiente:
                                                                                            </Typography>
                                                                                            <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: 600 }}>
                                                                                                {formatCurrency(periodAmountDue)}
                                                                                            </Typography>
                                                                                        </Box>
                                                                                    </>
                                                                                )}
                                                                                
                                                                                {/* Subtotal del período */}
                                                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5, ml: 2, pt: 0.5, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
                                                                                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                                                                        {isParcial ? 'A pagar este período:' : 'Subtotal período:'}
                                                                                    </Typography>
                                                                                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                                                                        {formatCurrency(periodAmountDue)}
                                                                                    </Typography>
                                                                                </Box>
                                                                            </Box>
                                                                        );
                                                                    })}
                                                                </Box>
                                                            </Box>
                                                        );
                                                    }

                                                    // Caso: solo 1 período pendiente
                                                    // UX: cuando hay 1 solo período pendiente, se muestra únicamente "CARGOS BASE" arriba.
                                                    if (Array.isArray(periods) && periods.length === 1) {
                                                        return null;
                                                    }
                                                } catch (e) {
                                                    console.error('Error parsing periods:', e);
                                                }
                                                return null;
                                            })()}

                                            {/* Total a pagar */}
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1.5, borderTop: '2px solid rgba(0,0,0,0.12)' }}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>💰 Total a pagar</Typography>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: '1.1rem', color: 'primary.main' }}>
                                                    {formatCurrency(dialogTotalToPay)}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        {/* 🔄 INFO: Explicación del flujo de crédito cuando hay crédito a favor */}
                                        {dialogCredito > 0 && (
                                            <Box sx={{ mb: 2, p: 1.5, bgcolor: '#e3f2fd', borderRadius: 1.5, border: '1px solid #90caf9' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#1565c0' }}>
                                                        🔄 Flujo del crédito disponible
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <Box component="span" sx={{ px: 0.6, py: 0.2, bgcolor: '#e8f5e9', borderRadius: 0.5, fontWeight: 600, fontSize: '0.65rem', color: '#2e7d32', whiteSpace: 'nowrap' }}>
                                                            ↗ Crédito disponible
                                                        </Box>
                                                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#1565c0' }}>
                                                            Q {dialogCredito.toFixed(2)}
                                                        </Typography>
                                                    </Box>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Este crédito proviene de pagos anteriores donde el monto excedió la tarifa del mes.
                                                        Se aplicará automáticamente a la tarifa del próximo período al generar la facturación mensual.
                                                    </Typography>
                                                    {dialogPendingBalance > 0 && (
                                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.3 }}>
                                                            {dialogCredito >= dialogPendingBalance
                                                                ? `✅ El crédito cubre todo el saldo pendiente (Q${dialogPendingBalance.toFixed(2)}). No se requiere pago adicional para ponerse al día.`
                                                                : `💡 El crédito (Q${dialogCredito.toFixed(2)}) se aplicará primero al saldo pendiente (Q${dialogPendingBalance.toFixed(2)}), reduciéndolo a Q${Math.max(0, dialogPendingBalance - dialogCredito).toFixed(2)}.`
                                                            }
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        )}

                                        <TextField
                                            label="Monto pagado"
                                            type="number"
                                            fullWidth
                                            value={registerAmount}
                                            onChange={(e) => setRegisterAmount(e.target.value)}
                                            disabled={uploadedReceiptsLoading || regHistLoading}
                                            inputProps={{ min: 0, step: '0.01' }}
                                            sx={{ mb: 2 }}
                                        />
                                        <TextField
                                            label="Fecha del Pago"
                                            type="date"
                                            fullWidth
                                            InputLabelProps={{ shrink: true }}
                                            value={registerPaymentExtra.paymentDate}
                                            onChange={(e) => setRegisterPaymentExtra(prev => ({ ...prev, paymentDate: e.target.value }))}
                                            disabled={uploadedReceiptsLoading || regHistLoading}
                                            sx={{ mb: 2 }}
                                        />
                                        <TextField
                                            label="Número de Boleta"
                                            fullWidth
                                            value={registerPaymentExtra.numeroBoleta}
                                            onChange={(e) => setRegisterPaymentExtra(prev => ({ ...prev, numeroBoleta: e.target.value }))}
                                            disabled={uploadedReceiptsLoading || regHistLoading}
                                            sx={{ mb: 2 }}
                                        />
                                        <TextField
                                            label="Descuento Extraordinario (Q)"
                                            type="number"
                                            fullWidth
                                            value={registerPaymentExtra.extraordinaryDiscount}
                                            onChange={(e) => setRegisterPaymentExtra(prev => ({ ...prev, extraordinaryDiscount: e.target.value }))}
                                            disabled={uploadedReceiptsLoading || regHistLoading}
                                            error={extraDiscountError}
                                            helperText={extraDiscountError ? `El descuento no puede exceder el saldo pendiente (Q${effectivePendingBalance.toFixed(2)})` : undefined}
                                            sx={{ mb: 2 }}
                                        />
                                        {dialogGeneratedCredit > 0 && (
                                            <Alert severity="info" sx={{ mb: 2 }}>
                                                Se generará {formatCurrency(dialogGeneratedCredit)} como crédito a favor.
                                            </Alert>
                                        )}
                                        <FormControl fullWidth sx={{ mb: 0 }}>
                                            <InputLabel>Número de Cuenta</InputLabel>
                                            <Select
                                                label="Número de Cuenta"
                                                value={registerPaymentExtra.bankAccountNumber}
                                                onChange={(e) => setRegisterPaymentExtra(prev => ({ ...prev, bankAccountNumber: e.target.value }))}
                                                disabled={uploadedReceiptsLoading || regHistLoading}
                                            >
                                                    {(() => {
                                                        const seen = new Set();
                                                        const entries = [];
                                                        try {
                                                            const sources = [];
                                                            if (Array.isArray(allSchools) && allSchools.length > 0) sources.push(...allSchools);
                                                            if (registerPaymentTarget?.schoolId && !sources.find(s => s.id === registerPaymentTarget.schoolId)) {
                                                                const found = allSchools.find(s => s.id === registerPaymentTarget.schoolId);
                                                                if (found) sources.push(found);
                                                            }

                                                            sources.forEach(sch => {
                                                                const raw = sch?.bankAccount || '';
                                                                if (!raw) return;
                                                                const parts = raw.split(/[|,;]+/).map(p => p.trim()).filter(Boolean);
                                                                if (parts.length > 0) {
                                                                    parts.forEach(p => {
                                                                        const key = `${sch.id}::${p}`;
                                                                        if (!seen.has(key)) { seen.add(key); entries.push({ key, value: p, label: `${sch.name} - ${p}` }); }
                                                                    });
                                                                } else {
                                                                    const key = `${sch.id}::${raw}`;
                                                                    if (!seen.has(key)) { seen.add(key); entries.push({ key, value: raw, label: `${sch.name} - ${raw}` }); }
                                                                }
                                                            });
                                                        } catch (e) {
                                                            // ignore
                                                        }
                                                        if (entries.length === 0) return <MenuItem value="">(No configurado)</MenuItem>;
                                                        return entries.map(opt => <MenuItem key={opt.key} value={opt.value}>{opt.label}</MenuItem>);
                                                    })()}
                                            </Select>
                                        </FormControl>
                                        </Box>
                                        )}

                                {/* Tab Panel 1: Pago de Mora */}
                                {paymentTab === 1 && (
                                <Box>
                                    <Typography variant="h6" sx={{ mb: 2, color: effectivePenaltyDue > 0 ? 'error.main' : 'success.main' }}>
                                        {effectivePenaltyDue > 0 ? '💰 Pago de Mora Acumulada' : '✅ Sin Mora Pendiente'}
                                    </Typography>
                                    
                                    {/* Información de la familia */}
                                    <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                                            {registerPaymentTarget ? `Familia: ${registerPaymentTarget.User?.FamilyDetail?.familyLastName || ''}` : ''}
                                            {registerPaymentTarget && (
                                                ` (${studentCount} ${studentCount === 1 ? 'estudiante' : 'estudiantes'})`
                                            )}
                                        </Typography>
                                        
                                        {/* Desglose de mora por período */}
                                        <Box sx={{ mt: 2, p: 2, bgcolor: '#fff', borderRadius: 1, border: '1px solid rgba(211, 47, 47, 0.2)' }}>
                                            <Typography variant="caption" sx={{ fontWeight: 600, color: 'error.main', display: 'block', mb: 1 }}>
                                                MORA ACUMULADA {dialogPenaltyPeriods.length > 0 && `— ${dialogPenaltyPeriods.length} período${dialogPenaltyPeriods.length > 1 ? 's' : ''}`}
                                            </Typography>
                                            
                                            {/* Desglose por período */}
                                            {dialogPenaltyPeriods.length > 0 && dialogPenaltyPeriods.map((period, idx) => {
                                                const periodDate = moment(period.period);
                                                const monthNames = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
                                                const label = `${monthNames[periodDate.month()]} ${periodDate.year()}`;
                                                const isFrozen = !!period.penaltyFrozen;
                                                const isCleared = !!period.penaltyCleared;

                                                // Fecha inicio mora
                                                const moraStart = period.penaltyStartDate ? moment(period.penaltyStartDate) : null;
                                                // Fecha fin mora: si está congelada → penaltyFrozenAt; si acumulando → hoy
                                                const moraEnd = isFrozen && period.penaltyFrozenAt
                                                    ? moment(period.penaltyFrozenAt)
                                                    : moment(payPenaltyDate || moment().format('YYYY-MM-DD'));
                                                // Días = fin - inicio + 1 (ambos extremos inclusivos)
                                                const moraDays = moraStart ? Math.max(0, moraEnd.diff(moraStart, 'days') + 1) : 0;

                                                // En modo retroactivo: reutilizar el cálculo ya hecho en retroactivePenaltyCalc
                                                const retroItem = retroactivePenaltyCalc ? retroPenaltyByPeriod.get(period.period) : null;
                                                const computedPenaltyForDisplay = retroItem
                                                    ? Number(retroItem.penaltyAmount || 0)
                                                    : Number(period.penaltyDue || 0);

                                                return (
                                                    <Box key={idx} sx={{ mb: 1, p: 1.5, borderLeft: '4px solid', borderColor: isFrozen ? '#1976d2' : isCleared ? '#4caf50' : '#d32f2f', bgcolor: isFrozen ? '#e3f2fd' : isCleared ? '#e8f5e9' : '#fce4ec', borderRadius: '0 4px 4px 0' }}>
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                            <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                                                {label} {isFrozen && '❄️'} {isCleared && '✅'}
                                                            </Typography>
                                                            <Typography variant="caption" sx={{ fontWeight: 700, color: isFrozen ? '#1976d2' : 'error.main' }}>
                                                                {formatCurrency(computedPenaltyForDisplay)}
                                                            </Typography>
                                                        </Box>
                                                        {moraStart && (
                                                            <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    📅 Inicio: <strong>{moraStart.format('DD/MM/YYYY')}</strong>
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {isFrozen ? '🔒 Fin:' : '📆 Hasta:'} <strong>{moraEnd.format('DD/MM/YYYY')}</strong>
                                                                </Typography>
                                                                <Typography variant="caption" sx={{ color: isFrozen ? '#1976d2' : 'error.main', fontWeight: 600 }}>
                                                                    {moraDays} día{moraDays !== 1 ? 's' : ''}
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                );
                                            })}

                                            {/* Indicador global de períodos congelados */}
                                            {hasFrozenPeriods && (
                                                <Box sx={{ mb: 1, p: 1, bgcolor: '#fff3cd', borderRadius: 1, border: '1px solid #ffc107' }}>
                                                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#856404' }}>
                                                        ❄️ {dialogPenaltyPeriods.filter(p => p.penaltyFrozen).length} período(s) con mora congelada
                                                    </Typography>
                                                </Box>
                                            )}
                                            
                                            {/* Mora acumulada */}
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                                                <Typography variant="body2" color="text.secondary">Mora acumulada:</Typography>
                                                <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 500 }}>
                                                    {formatCurrency(effectivePenaltyDue)}
                                                </Typography>
                                            </Box>

                                            {/* Exoneración (si hay monto en el campo) */}
                                            {(() => {
                                                const exonerateValue = isExonerating ? Number(exonerateAmount || 0) : Number(discountPenaltyAmount || 0);
                                                if (exonerateValue > 0) {
                                                    return (
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                                                            <Typography variant="body2" color="text.secondary">
                                                                {isExonerating ? 'Exoneración completa:' : 'Exoneración parcial:'}
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 500 }}>
                                                                - {formatCurrency(exonerateValue)}
                                                            </Typography>
                                                        </Box>
                                                    );
                                                }
                                                return null;
                                            })()}

                                            {/* Total a pagar (mora - exoneración) */}
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, pt: 1, borderTop: '1px solid rgba(0,0,0,0.1)' }}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Total a pagar:</Typography>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: effectivePenaltyDue > 0 ? 'error.main' : 'success.main' }}>
                                                    {(() => {
                                                        const exonerateValue = isExonerating ? Number(exonerateAmount || 0) : Number(discountPenaltyAmount || 0);
                                                        const total = Math.max(0, effectivePenaltyDue - exonerateValue);
                                                        return formatCurrency(total);
                                                    })()}
                                                </Typography>
                                            </Box>
                                            {dialogCredito > 0 && (
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                                                    <Typography variant="body2" sx={{ color: 'success.main' }}>
                                                        <Box component="span" title="Crédito acumulado de pagos anteriores. Puede usarse para cubrir la mora." sx={{ borderBottom: '1px dashed #999', cursor: 'help' }}>
                                                            Crédito disponible ↗
                                                        </Box>
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600 }}>Q {dialogCredito.toFixed(2)}</Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>

                                    {/* Toggle: Usar crédito disponible */}
                                    {effectivePenaltyDue > 0 && dialogCredito > 0 && !isExonerating && hasPermission('pagos-v2-usar-credito') && (
                                        <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
                                            <Button
                                                size="small"
                                                variant={!payPenaltyUseCredit ? 'contained' : 'outlined'}
                                                onClick={() => {
                                                    setPayPenaltyUseCredit(false);
                                                    setPayPenaltyAmount('');
                                                    setPayPenaltyBoletaAmount('');
                                                }}
                                                fullWidth
                                            >
                                                Pago con boleta
                                            </Button>
                                            <Button
                                                size="small"
                                                variant={payPenaltyUseCredit ? 'contained' : 'outlined'}
                                                color="success"
                                                onClick={() => {
                                                    const creditToApply = Math.min(dialogCredito, effectivePenaltyDue);
                                                    const remainder = Math.max(0, Math.round((effectivePenaltyDue - creditToApply) * 100) / 100);
                                                    setPayPenaltyUseCredit(true);
                                                    setPayPenaltyAmount(creditToApply.toFixed(2));
                                                    setPayPenaltyBoletaAmount(remainder.toFixed(2));
                                                }}
                                                fullWidth
                                            >
                                                Usar crédito (Q {Math.min(dialogCredito, effectivePenaltyDue).toFixed(2)})
                                            </Button>
                                        </Box>
                                    )}

                                    {/* Toggle de Exoneración Completa */}
                                    {effectivePenaltyDue > 0 && (
                                        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                ✅ Exoneración Completa
                                            </Typography>
                                            <Switch 
                                                checked={isExonerating}
                                                onChange={(e) => {
                                                    setIsExonerating(e.target.checked);
                                                    if (e.target.checked) {
                                                        setPayPenaltyUseCredit(false);
                                                        setPayPenaltyAmount('0');
                                                        setPayPenaltyBoleta('004');
                                                        setDiscountPenaltyAmount(effectivePenaltyDue.toString());
                                                        setExonerateAmount(effectivePenaltyDue.toString());
                                                    } else {
                                                        setPayPenaltyAmount('');
                                                        setPayPenaltyBoleta('');
                                                        setDiscountPenaltyAmount('');
                                                        setExonerateAmount('');
                                                    }
                                                }}
                                                color="success"
                                            />
                                        </Box>
                                    )}

                                    {/* Formulario de pago de mora (siempre visible, pero algunos campos bloqueados cuando está exonerando) */}
                                    <TextField
                                        label={payPenaltyUseCredit ? 'Crédito a aplicar (Q)' : 'Monto a Pagar'}
                                        type="number"
                                        fullWidth
                                        value={payPenaltyAmount}
                                        onChange={(e) => {
                                            setPayPenaltyAmount(e.target.value);
                                            if (payPenaltyUseCredit) {
                                                const newCredit = Number(e.target.value || 0);
                                                const newRem = Math.max(0, Math.round((effectivePenaltyDue - newCredit) * 100) / 100);
                                                setPayPenaltyBoletaAmount(newRem.toFixed(2));
                                            }
                                        }}
                                        inputProps={{ min: 0, step: '0.01', max: payPenaltyUseCredit ? dialogCredito : effectivePenaltyDue }}
                                        sx={{ mb: 2 }}
                                        disabled={effectivePenaltyDue === 0 || isExonerating}
                                        helperText={isExonerating ? 'Exoneración completa: No se realizará pago' : payPenaltyUseCredit ? `Máximo del crédito: Q ${dialogCredito.toFixed(2)}` : ''}
                                    />
                                    {payPenaltyUseCredit && (() => {
                                        const cashRem = Math.max(0, Math.round((effectivePenaltyDue - Number(payPenaltyAmount || 0)) * 100) / 100);
                                        return cashRem > 0 ? (
                                            <Box sx={{ mb: 2, p: 1.5, bgcolor: '#fff3e0', borderRadius: 1, border: '1px solid #ffb74d' }}>
                                                <Typography variant="body2" sx={{ color: 'warning.dark' }}>
                                                    ⚠️ El crédito no cubre toda la mora. <strong>Restante a pagar con boleta: Q {cashRem.toFixed(2)}</strong>
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                                                ℹ️ El crédito cubre toda la mora. No se requiere boleta.
                                            </Typography>
                                        );
                                    })()}
                                    {(!payPenaltyUseCredit || Math.max(0, Math.round((effectivePenaltyDue - Number(payPenaltyAmount || 0)) * 100) / 100) > 0) && (
                                        <>
                                            {payPenaltyUseCredit && (
                                                <Typography variant="caption" sx={{ fontWeight: 700, color: 'warning.dark', display: 'block', mb: 1, mt: 0.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                                    📋 Datos del pago restante
                                                </Typography>
                                            )}
                                            {payPenaltyUseCredit && (
                                                <TextField
                                                    label="Monto a pagar con boleta (Q)"
                                                    type="number"
                                                    fullWidth
                                                    value={payPenaltyBoletaAmount}
                                                    onChange={(e) => setPayPenaltyBoletaAmount(e.target.value)}
                                                    inputProps={{ min: 0, step: '0.01' }}
                                                    sx={{ mb: 2 }}
                                                    required
                                                    error={!payPenaltyBoletaAmount || Number(payPenaltyBoletaAmount) <= 0}
                                                    helperText={!payPenaltyBoletaAmount || Number(payPenaltyBoletaAmount) <= 0 ? 'Ingrese el monto a pagar con boleta' : ''}
                                                />
                                            )}
                                            <TextField
                                                label={payPenaltyUseCredit ? `Fecha de pago del restante` : 'Fecha del Pago'}
                                                type="date"
                                                fullWidth
                                                InputLabelProps={{ shrink: true }}
                                                value={payPenaltyDate}
                                                onChange={(e) => setPayPenaltyDate(e.target.value)}
                                                sx={{ mb: 2 }}
                                            />
                                        </>
                                    )}
                                    
                                    {/* Alert retroactivo por período */}
                                    {retroactivePenaltyCalc && retroactivePenaltyCalc.periods && retroactivePenaltyCalc.periods.some(p => p.daysLate > 0) && (
                                        <Box sx={{ mb: 2, p: 2, bgcolor: '#fff3e0', borderRadius: 1, border: '1px solid #ff9800' }}>
                                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#e65100', display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                                                ⏰ PAGO RETROACTIVO DETECTADO
                                            </Typography>
                                            <Typography variant="body2" sx={{ mb: 1 }}>
                                                <strong>Fecha de pago:</strong> {retroactivePenaltyCalc.paymentDate}
                                            </Typography>

                                            {/* Desglose por período */}
                                            {retroactivePenaltyCalc.periods.filter(p => p.daysLate > 0 || p.frozen).map((p, idx) => {
                                                const periodDate = moment(p.period);
                                                const monthNames = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
                                                const label = `${monthNames[periodDate.month()]} ${periodDate.year()}`;
                                                return (
                                                    <Box key={idx} sx={{ ml: 1, mb: 0.5, pl: 1, borderLeft: '3px solid', borderColor: p.frozen ? '#1976d2' : '#ff9800' }}>
                                                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                                            {label} {p.frozen && '❄️'}
                                                        </Typography>
                                                        {p.frozen ? (
                                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                                Mora congelada: {formatCurrency(p.penaltyAmount)}
                                                            </Typography>
                                                        ) : (
                                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                                {p.daysLate} días × Q{retroactivePenaltyCalc.dailyPenalty} × {Number(p.studentCount || retroactivePenaltyCalc.studentCount || 1)} est. = {formatCurrency(p.penaltyAmount)}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                );
                                            })}

                                            {/* Total retroactivo */}
                                            <Box sx={{ mt: 1.5, p: 1.5, bgcolor: '#fff', borderRadius: 1, border: '2px solid #ff9800' }}>
                                                <Typography variant="body1" sx={{ fontWeight: 700, color: '#e65100' }}>
                                                    💰 Mora hasta {moment(payPenaltyDate).format('DD/MM/YYYY')}: <span style={{ fontSize: '1.2em' }}>{formatCurrency(retroactivePenaltyCalc.penaltyUntilPaymentDate)}</span>
                                                </Typography>
                                            </Box>

                                            {/* Aplicación del pago */}
                                            {(() => {
                                                const paymentAmount = Number(payPenaltyAmount || 0);
                                                const exonerationAmount = isExonerating ? Number(exonerateAmount || 0) : Number(discountPenaltyAmount || 0);
                                                const totalReduction = paymentAmount + exonerationAmount;
                                                if (totalReduction > 0) {
                                                    const willCoverFull = totalReduction >= retroactivePenaltyCalc.penaltyUntilPaymentDate;
                                                    return (
                                                        <Box sx={{ mt: 1.5, p: 1.5, bgcolor: willCoverFull ? '#e8f5e9' : '#fff3e0', borderRadius: 1, border: willCoverFull ? '2px solid #4caf50' : '1px solid #ff9800' }}>
                                                            <Typography variant="body2" sx={{ fontWeight: 600, color: willCoverFull ? '#2e7d32' : '#e65100', mb: 0.5 }}>
                                                                📊 Aplicación del Pago Retroactivo:
                                                            </Typography>
                                                            {paymentAmount > 0 && <Typography variant="body2">• Pago: {formatCurrency(paymentAmount)}</Typography>}
                                                            {exonerationAmount > 0 && <Typography variant="body2">• Exoneración: {formatCurrency(exonerationAmount)}</Typography>}
                                                            <Typography variant="body2" sx={{ fontWeight: 700, color: willCoverFull ? '#2e7d32' : '#e65100', mt: 1 }}>
                                                                {willCoverFull ? <>✅ Cubre toda la mora hasta {moment(payPenaltyDate).format('DD/MM/YYYY')}</> : <>⚠️ Pago parcial: resta {formatCurrency(retroactivePenaltyCalc.penaltyUntilPaymentDate - totalReduction)}</>}
                                                            </Typography>
                                                        </Box>
                                                    );
                                                }
                                                return null;
                                            })()}
                                        </Box>
                                    )}
                                    
                                    {(!payPenaltyUseCredit || Math.max(0, Math.round((effectivePenaltyDue - Number(payPenaltyAmount || 0)) * 100) / 100) > 0) && (
                                        <TextField
                                            label="Número de Boleta"
                                            fullWidth
                                            value={payPenaltyBoleta}
                                            onChange={(e) => setPayPenaltyBoleta(e.target.value)}
                                            sx={{ mb: 2 }}
                                            disabled={isExonerating}
                                            required={payPenaltyUseCredit && !isExonerating}
                                            error={payPenaltyUseCredit && !isExonerating && !payPenaltyBoleta && Math.max(0, Math.round((effectivePenaltyDue - Number(payPenaltyAmount || 0)) * 100) / 100) > 0}
                                            helperText={
                                                isExonerating
                                                    ? '004 - Exoneración completa'
                                                    : payPenaltyUseCredit && !payPenaltyBoleta && Math.max(0, Math.round((effectivePenaltyDue - Number(payPenaltyAmount || 0)) * 100) / 100) > 0
                                                        ? 'Ingrese el número de boleta para registrar el pago del restante'
                                                        : ''
                                            }
                                        />
                                    )}
                                    {!payPenaltyUseCredit && (
                                        <TextField
                                            label="Exonerar Mora (Q)"
                                            type="number"
                                            fullWidth
                                            value={discountPenaltyAmount}
                                            onChange={(e) => setDiscountPenaltyAmount(e.target.value)}
                                            inputProps={{ min: 0, step: '0.01', max: effectivePenaltyDue }}
                                            sx={{ mb: 2 }}
                                            disabled={effectivePenaltyDue === 0 || isExonerating}
                                            helperText={isExonerating ? 'Exoneración del monto completo de la mora' : ''}
                                        />
                                    )}
                                    {(!payPenaltyUseCredit || Math.max(0, Math.round((effectivePenaltyDue - Number(payPenaltyAmount || 0)) * 100) / 100) > 0) && (
                                    <FormControl fullWidth sx={{ mb: 2 }}>
                                        <InputLabel>Número de Cuenta</InputLabel>
                                        <Select
                                            label="Número de Cuenta"
                                            value={payPenaltyAccount}
                                            onChange={(e) => setPayPenaltyAccount(e.target.value)}
                                        >
                                            {(() => {
                                                const seen = new Set();
                                                const entries = [];
                                                try {
                                                    const sources = [];
                                                    if (Array.isArray(allSchools) && allSchools.length > 0) sources.push(...allSchools);
                                                    if (registerPaymentTarget?.schoolId && !sources.find(s => s.id === registerPaymentTarget.schoolId)) {
                                                        const found = allSchools.find(s => s.id === registerPaymentTarget.schoolId);
                                                        if (found) sources.push(found);
                                                    }

                                                    sources.forEach(sch => {
                                                        const raw = sch?.bankAccount || '';
                                                        if (!raw) return;
                                                        const parts = raw.split(/[|,;]+/).map(p => p.trim()).filter(Boolean);
                                                        if (parts.length > 0) {
                                                            parts.forEach(p => {
                                                                const key = `${sch.id}::${p}`;
                                                                if (!seen.has(key)) { seen.add(key); entries.push({ key, value: p, label: `${sch.name} - ${p}` }); }
                                                            });
                                                        } else {
                                                            const key = `${sch.id}::${raw}`;
                                                            if (!seen.has(key)) { seen.add(key); entries.push({ key, value: raw, label: `${sch.name} - ${raw}` }); }
                                                        }
                                                    });
                                                } catch (e) {
                                                    // ignore
                                                }
                                                if (entries.length === 0) return <MenuItem value="">(No configurado)</MenuItem>;
                                                    return entries.map(opt => <MenuItem key={opt.key} value={opt.value}>{opt.label}</MenuItem>);
                                                })()}
                                        </Select>
                                    </FormControl>
                                    )}

                                    {effectivePenaltyDue > 0 ? (
                                        <Box sx={{ mt: 2, p: 2, bgcolor: '#fff3cd', borderRadius: 1, border: '1px solid #ffc107' }}>
                                            <Typography variant="caption" color="text.secondary">
                                                💡 <strong>Nota:</strong> Este pago se aplicará exclusivamente a la mora acumulada. 
                                                El saldo de tarifa permanecerá sin cambios.
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <Box sx={{ mt: 2, p: 2, bgcolor: '#d4edda', borderRadius: 1, border: '1px solid #c3e6cb' }}>
                                            <Typography variant="caption" sx={{ color: '#155724' }}>
                                                ✅ <strong>Esta familia no tiene mora pendiente.</strong>
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                                )}

                                {/* Tab Panel 2: Inscripción */}
                                {paymentTab === 2 && (
                                <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                        <Typography variant="h6">🎓 Inscripción de Ciclo</Typography>
                                        {enrollmentPayment && (
                                            <Chip
                                                label={ENROLLMENT_STATUS_LABEL[enrollmentPayment.status]?.label || enrollmentPayment.status}
                                                color={ENROLLMENT_STATUS_LABEL[enrollmentPayment.status]?.color || 'default'}
                                                size="small"
                                            />
                                        )}
                                    </Box>

                                    {!enrollmentPayment && (
                                        <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                Esta familia no tiene cargo de inscripción para este ciclo (el colegio no tiene monto de inscripción configurado, o aún no se ha generado).
                                            </Typography>
                                        </Box>
                                    )}

                                    {enrollmentPayment && (
                                        <>
                                            <Box sx={{ mb: 2, p: 2, bgcolor: '#fafafa', borderRadius: 1, border: '1px solid rgba(0,0,0,0.04)' }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                    <Typography variant="body2" color="text.secondary">Monto original ({enrollmentPayment.studentsCount} est.)</Typography>
                                                    <Typography variant="body2">{formatCurrency(enrollmentPayment.originalAmount)}</Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                    <Typography variant="body2" color="text.secondary">Descuento aplicado</Typography>
                                                    <Typography variant="body2">{formatCurrency(enrollmentPayment.discountApplied)}</Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                    <Typography variant="body2" color="text.secondary">Monto neto</Typography>
                                                    <Typography variant="body2">{formatCurrency(enrollmentPayment.netAmount)}</Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                    <Typography variant="body2" color="text.secondary">Pagado</Typography>
                                                    <Typography variant="body2">{formatCurrency(enrollmentPayment.amountPaid)}</Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 0.5, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>Saldo pendiente</Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatCurrency(enrollmentPayment.amountDue)}</Typography>
                                                </Box>
                                                {enrollmentPayment.manualAdjustmentAmount > 0 && (
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                                        Ajuste manual acumulado: {formatCurrency(enrollmentPayment.manualAdjustmentAmount)}
                                                        {enrollmentPayment.manualAdjustmentReason ? ` — ${enrollmentPayment.manualAdjustmentReason}` : ''}
                                                    </Typography>
                                                )}
                                            </Box>

                                            {enrollmentPayment.amountDue > 0 && (
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                    <TextField
                                                        label="Monto pagado (Q)"
                                                        type="number"
                                                        value={payEnrollmentAmount}
                                                        onChange={(e) => setPayEnrollmentAmount(e.target.value)}
                                                        inputProps={{ min: '0', step: '0.01' }}
                                                    />
                                                    <TextField
                                                        label={`Descuento extraordinario / exoneración (Q, opcional, máx. Q${Number(enrollmentPayment.amountDue).toFixed(2)})`}
                                                        type="number"
                                                        value={payEnrollmentDiscount}
                                                        onChange={(e) => setPayEnrollmentDiscount(e.target.value)}
                                                        inputProps={{ min: '0', step: '0.01', max: enrollmentPayment.amountDue }}
                                                    />
                                                    <TextField
                                                        label="Fecha real de pago"
                                                        type="date"
                                                        value={payEnrollmentDate}
                                                        onChange={(e) => setPayEnrollmentDate(e.target.value)}
                                                        InputLabelProps={{ shrink: true }}
                                                    />
                                                    <TextField
                                                        label="Número de boleta (opcional)"
                                                        value={payEnrollmentReceiptNumber}
                                                        onChange={(e) => setPayEnrollmentReceiptNumber(e.target.value)}
                                                    />
                                                    <TextField
                                                        label="Notas / motivo del descuento (opcional)"
                                                        multiline
                                                        minRows={2}
                                                        value={payEnrollmentNotes}
                                                        onChange={(e) => setPayEnrollmentNotes(e.target.value)}
                                                    />
                                                </Box>
                                            )}
                                        </>
                                    )}
                                </Box>
                                )}
                                    </Grid>
                                </Grid>

                                {/* Loading overlay when receipts/history are being fetched */}
                                {(uploadedReceiptsLoading || regHistLoading) && (
                                    <Box sx={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(255,255,255,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', zIndex: 30 }}>
                                        <CircularProgress />
                                        <Typography sx={{ mt: 2 }}>Cargando información de boletas y historial...</Typography>
                                    </Box>
                                )}
                                </Box>
                            </DialogContent>
                            <DialogActions sx={{ flexWrap: 'wrap' }}>
                                {(paymentTab === 0 || paymentTab === 1) && selectedReceipt?.status === 'PENDIENTE' && (
                                    <FormControlLabel
                                        sx={{ mr: 'auto' }}
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={markReceiptAsRegistered}
                                                onChange={(e) => setMarkReceiptAsRegistered(e.target.checked)}
                                            />
                                        }
                                        label="Marcar boleta abierta como registrada"
                                    />
                                )}
                                <Button onClick={() => { setOpenRegisterDialog(false); setSelectedReceipt(null); setReceiptZoom(1); setPaymentTab(0); }} disabled={uploadedReceiptsLoading || regHistLoading}>Cancelar</Button>

                                {/* Botón para Pago de Tarifa */}
                                {paymentTab === 0 && hasPermission('pagos-pagar-tarifa') && (
                                    <Button variant="contained" onClick={async () => {
                                        await handleConfirmRegister();
                                        // ensure receipt view reset after confirming
                                        setSelectedReceipt(null);
                                        setReceiptZoom(1);
                                    }} disabled={!registerAmount || uploadedReceiptsLoading || regHistLoading || extraDiscountError}>
                                        Registrar Pago de Tarifa
                                    </Button>
                                )}
                                
                                {/* Botón para Pago de Mora. Con crédito puro solo llama
                                    /payments/v2/use-credit; con boleta llama /payments/pay-penalty. */}
                                {paymentTab === 1 && (payPenaltyUseCredit && Number(payPenaltyBoletaAmount || 0) === 0
                                    ? hasPermission('pagos-v2-usar-credito')
                                    : hasPermission('pagos-pagar-mora')) && (
                                    <Button variant="contained" color={isExonerating ? 'success' : payPenaltyUseCredit ? 'success' : 'warning'} onClick={async () => {
                                        const creditAmt = payPenaltyUseCredit ? Number(payPenaltyAmount || 0) : 0;
                                        const boletaAmt = payPenaltyUseCredit ? Number(payPenaltyBoletaAmount || 0) : 0;
                                        const amt = payPenaltyUseCredit ? boletaAmt : Number(payPenaltyAmount || 0);
                                        const discount = Number(discountPenaltyAmount || 0);

                                        if (payPenaltyUseCredit) {
                                            if (!creditAmt || Number.isNaN(creditAmt) || creditAmt <= 0) {
                                                setSnackbar({ open: true, message: 'Ingrese un monto de crédito válido', severity: 'error' });
                                                return;
                                            }
                                            if (creditAmt > dialogCredito) {
                                                setSnackbar({ open: true, message: `El crédito a aplicar excede el disponible (Q${dialogCredito.toFixed(2)})`, severity: 'error' });
                                                return;
                                            }
                                        } else {
                                            if (Number.isNaN(amt) || amt < 0) {
                                                setSnackbar({ open: true, message: 'Ingrese un monto válido', severity: 'error' });
                                                return;
                                            }
                                            if (amt === 0 && discount === 0) {
                                                setSnackbar({ open: true, message: 'Debe ingresar un monto de pago o de exoneración', severity: 'error' });
                                                return;
                                            }
                                            const total = amt + discount;
                                            if (total > effectivePenaltyDue) {
                                                setSnackbar({ open: true, message: `El monto total (pago + exoneración) excede la mora adeudada (Q${effectivePenaltyDue.toFixed(2)})`, severity: 'error' });
                                                return;
                                            }
                                        }

                                        try {
                                            const receiptLinkFields = selectedReceipt?.status === 'PENDIENTE' ? {
                                                receiptId: selectedReceipt.id,
                                                markReceiptRegistered: markReceiptAsRegistered
                                            } : {};
                                            if (payPenaltyUseCredit) {
                                                await api.post('/payments/v2/use-credit', {
                                                    paymentId: registerPaymentTarget.id,
                                                    amount: creditAmt,
                                                    targetType: 'PENALTY',
                                                    ...receiptLinkFields
                                                });
                                                if (boletaAmt > 0) {
                                                    await api.post('/payments/pay-penalty', {
                                                        paymentId: registerPaymentTarget.id,
                                                        amount: boletaAmt,
                                                        realPaymentDate: payPenaltyDate,
                                                        receiptNumber: payPenaltyBoleta,
                                                        bankAccount: payPenaltyAccount,
                                                        source: 'manual',
                                                        ...receiptLinkFields
                                                    });
                                                    setSnackbar({ open: true, message: `Crédito (Q${creditAmt.toFixed(2)}) y boleta (Q${boletaAmt.toFixed(2)}) aplicados a mora`, severity: 'success' });
                                                } else {
                                                    setSnackbar({ open: true, message: 'Crédito aplicado a mora exitosamente', severity: 'success' });
                                                }
                                            } else {
                                                const payloadData = {
                                                    paymentId: registerPaymentTarget.id,
                                                    amount: amt,
                                                    extraordinaryDiscount: discount || 0,
                                                    realPaymentDate: payPenaltyDate,
                                                    receiptNumber: payPenaltyBoleta,
                                                    bankAccount: payPenaltyAccount,
                                                    notes: discount > 0 ? `Descuento/Exoneración de mora: Q${discount.toFixed(2)}` : null,
                                                    source: 'manual',
                                                    ...receiptLinkFields
                                                };
                                                await api.post('/payments/pay-penalty', payloadData);
                                                setSnackbar({ open: true, message: isExonerating ? `Mora exonerada: Q${discount.toFixed(2)}` : 'Pago de mora registrado exitosamente', severity: 'success' });
                                            }
                                            setOpenRegisterDialog(false);
                                            setIsExonerating(false);
                                            setPayPenaltyUseCredit(false);
                                            setPayPenaltyBoletaAmount('');
                                            setPayPenaltyAmount('');
                                            setPayPenaltyBoleta('');
                                            setPayPenaltyAccount('');
                                            setPayPenaltyDate(moment().format('YYYY-MM-DD'));
                                            setDiscountPenaltyAmount('');
                                            setExonerateAmount('');
                                            setPaymentTab(0);
                                            await fetchAllPayments(statusFilter, search);
                                        } catch (err) {
                                            console.error('Error procesando mora:', err);
                                            const errorMsg = err.response?.data?.error || err.response?.data?.message || 'Error al procesar el pago de mora';
                                            setSnackbar({ open: true, message: errorMsg, severity: 'error' });
                                        }
                                    }} disabled={uploadedReceiptsLoading || regHistLoading || effectivePenaltyDue === 0 || (() => {
                                        if (isExonerating) return !payPenaltyDate || !payPenaltyAccount;
                                        if (payPenaltyUseCredit) {
                                            const cAmt = Number(payPenaltyAmount || 0);
                                            if (!cAmt || cAmt <= 0 || cAmt > dialogCredito) return true;
                                            const cRem = Math.max(0, Math.round((effectivePenaltyDue - cAmt) * 100) / 100);
                                            if (cRem > 0) {
                                                const bAmt = Number(payPenaltyBoletaAmount || 0);
                                                if (!bAmt || bAmt <= 0) return true;
                                                return !payPenaltyBoleta || !payPenaltyDate || !payPenaltyAccount;
                                            }
                                            return false;
                                        }
                                        const amt = Number(payPenaltyAmount || 0);
                                        const discount = Number(discountPenaltyAmount || 0);
                                        if (amt === 0 && discount === 0) return true;
                                        if (amt > 0 && !payPenaltyBoleta) return true;
                                        return !payPenaltyDate || !payPenaltyAccount;
                                    })()}>
                                        {isExonerating ? 'Confirmar Exoneración' : payPenaltyUseCredit && Number(payPenaltyBoletaAmount || 0) > 0 ? 'Aplicar Crédito + Boleta' : payPenaltyUseCredit ? 'Aplicar Crédito' : 'Registrar Pago de Mora'}
                                    </Button>
                                )}

                                {/* Botón para Inscripción */}
                                {paymentTab === 2 && hasPermission('pagos-v2-pagar-inscripcion') && (
                                    <Button
                                        variant="contained"
                                        onClick={async () => {
                                            const amount = Number(payEnrollmentAmount) || 0;
                                            const extraordinaryDiscount = Number(payEnrollmentDiscount) || 0;
                                            if (amount <= 0 && extraordinaryDiscount <= 0) {
                                                setSnackbar({ open: true, message: 'Debe indicar un monto pagado o un descuento extraordinario.', severity: 'error' });
                                                return;
                                            }
                                            setEnrollmentSaving(true);
                                            try {
                                                await api.post('/enrollment-payments/pay', {
                                                    enrollmentPaymentId: enrollmentPayment.id,
                                                    amount,
                                                    extraordinaryDiscount: extraordinaryDiscount || undefined,
                                                    realPaymentDate: payEnrollmentDate,
                                                    receiptNumber: payEnrollmentReceiptNumber || undefined,
                                                    notes: payEnrollmentNotes || undefined,
                                                });
                                                setSnackbar({ open: true, message: 'Pago de inscripción registrado.', severity: 'success' });
                                                setOpenRegisterDialog(false);
                                                setPaymentTab(0);
                                                await fetchAllPayments(statusFilter, search);
                                            } catch (err) {
                                                setSnackbar({
                                                    open: true,
                                                    message: err.response?.data?.error || 'Error al registrar el pago.',
                                                    severity: 'error',
                                                });
                                            } finally {
                                                setEnrollmentSaving(false);
                                            }
                                        }}
                                        disabled={
                                            enrollmentSaving
                                            || uploadedReceiptsLoading
                                            || regHistLoading
                                            || !enrollmentPayment
                                            || enrollmentPayment.amountDue <= 0
                                            || (!Number(payEnrollmentAmount) && !Number(payEnrollmentDiscount))
                                        }
                                    >
                                        {enrollmentSaving ? 'Guardando...' : 'Registrar Pago / Exoneración'}
                                    </Button>
                                )}
                            </DialogActions>
                        </Dialog>

                        {/* Dialog: Editar Número de Recibo */}
                        <Dialog open={openReceiptDialog} onClose={() => setOpenReceiptDialog(false)} fullWidth maxWidth="sm">
                            <DialogTitle>Editar Número de Recibo</DialogTitle>
                            <DialogContent>
                                <TextField
                                    label="Número de recibo"
                                    fullWidth
                                    value={receiptNumberDraft}
                                    onChange={(e) => setReceiptNumberDraft(e.target.value)}
                                />
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={() => setOpenReceiptDialog(false)}>Cancelar</Button>
                                <Button variant="contained" onClick={handleSaveReceipt}>Guardar</Button>
                            </DialogActions>
                        </Dialog>

                        {/* Dialog: Notas */}
                        <Dialog open={openNotesDialog} onClose={handleCloseNotes} fullWidth maxWidth="sm">
                            <DialogTitle>Editar Notas</DialogTitle>
                            <DialogContent>
                                <TextField
                                    label="Notas"
                                    fullWidth
                                    multiline
                                    minRows={3}
                                    value={notesDraft}
                                    onChange={(e) => setNotesDraft(e.target.value)}
                                />
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={handleCloseNotes}>Cancelar</Button>
                                {hasPermission('pagos-v2-actualizar-notas') && (
                                    <Button variant="contained" onClick={handleSaveNotes}>Guardar</Button>
                                )}
                            </DialogActions>
                        </Dialog>

                        {/* Dialog: Enviar Email */}
                        <Dialog open={openEmailDialog} onClose={() => setOpenEmailDialog(false)} fullWidth maxWidth="sm">
                            <DialogTitle>Enviar Correo</DialogTitle>
                            <DialogContent>
                                <TextField
                                    label="Asunto"
                                    fullWidth
                                    value={emailSubject}
                                    onChange={(e) => setEmailSubject(e.target.value)}
                                    sx={{ mb: 2 }}
                                />
                                <TextField
                                    label="Mensaje"
                                    fullWidth
                                    multiline
                                    minRows={4}
                                    value={emailMessage}
                                    onChange={(e) => setEmailMessage(e.target.value)}
                                />
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={() => setOpenEmailDialog(false)}>Cancelar</Button>
                                <Button variant="contained" onClick={handleSendEmail} disabled={!emailSubject && !emailMessage}>Enviar</Button>
                            </DialogActions>
                        </Dialog>

                        {/* Dialog: Configurar Auto Débito */}
                        <Dialog open={openAutoDebitDialog} onClose={() => setOpenAutoDebitDialog(false)} maxWidth="sm" fullWidth>
                            <DialogTitle>Activar Débito Automático</DialogTitle>
                            <DialogContent>
                                <Box sx={{ mb: 3, p: 2, bgcolor: '#e3f2fd', borderRadius: 1, border: '1px solid #2196f3' }}>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        <strong>¿A partir de cuándo deseas aplicar el débito automático?</strong>
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Selecciona si el débito automático debe aplicarse al mes actual o al siguiente mes.
                                    </Typography>
                                </Box>
                                <Typography variant="body2" sx={{ mb: 2, fontWeight: 'bold' }}>
                                    Selecciona una opción:
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Button 
                                        variant="outlined" 
                                        color="primary"
                                        fullWidth
                                        onClick={async () => {
                                            try {
                                                const { userId, payment } = autoDebitPayload;
                                                await api.post('/payments/v2/activate-auto-debit', { 
                                                    userId,
                                                    paymentId: payment?.id,
                                                    schoolId: payment?.schoolId,
                                                    cicloEscolarId: payment?.cicloEscolarId,
                                                    activateAutoDebit: true,
                                                    applyToCurrentMonth: true 
                                                });
                                                setSnackbar({ 
                                                    open: true, 
                                                    message: 'Débito automático activado para el mes actual. Se procesó el pago y se exoneró la mora existente.', 
                                                    severity: 'success' 
                                                });
                                                setOpenAutoDebitDialog(false);
                                                setAutoDebitPayload(null);
                                                // Actualizar vista
                                                await fetchAllPayments(statusFilter, search);
                                            } catch (err) {
                                                console.error(err);
                                                setSnackbar({ open: true, message: 'Error activando débito automático', severity: 'error' });
                                            }
                                        }}
                                        sx={{ py: 2, textAlign: 'left', justifyContent: 'flex-start' }}
                                    >
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                                📅 Aplicar al mes actual
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                                                Si ya pasó la fecha de pago, se procesará inmediatamente y se exonerará la mora existente.
                                            </Typography>
                                        </Box>
                                    </Button>
                                    
                                    <Button 
                                        variant="outlined" 
                                        color="secondary"
                                        fullWidth
                                        onClick={async () => {
                                            try {
                                                const { userId, payment } = autoDebitPayload;
                                                await api.post('/payments/v2/activate-auto-debit', { 
                                                    userId,
                                                    paymentId: payment?.id,
                                                    schoolId: payment?.schoolId,
                                                    cicloEscolarId: payment?.cicloEscolarId,
                                                    activateAutoDebit: true,
                                                    applyToCurrentMonth: false 
                                                });
                                                setSnackbar({ 
                                                    open: true, 
                                                    message: 'Débito automático activado. Se aplicará a partir del siguiente mes.', 
                                                    severity: 'success' 
                                                });
                                                setOpenAutoDebitDialog(false);
                                                setAutoDebitPayload(null);
                                                // Actualizar vista
                                                await fetchAllPayments(statusFilter, search);
                                            } catch (err) {
                                                console.error(err);
                                                setSnackbar({ open: true, message: 'Error activando débito automático', severity: 'error' });
                                            }
                                        }}
                                        sx={{ py: 2, textAlign: 'left', justifyContent: 'flex-start' }}
                                    >
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                                📆 Aplicar al siguiente mes
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                                                El débito automático se aplicará cuando llegue el siguiente mes. El mes actual se procesa normalmente.
                                            </Typography>
                                        </Box>
                                    </Button>
                                </Box>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={() => {
                                    setOpenAutoDebitDialog(false);
                                    setAutoDebitPayload(null);
                                }}>
                                    Cancelar
                                </Button>
                            </DialogActions>
                        </Dialog>

                        {/* Dialog: Pagar Mora Congelada */}
                        <Dialog open={openPayPenaltyDialog} onClose={() => setOpenPayPenaltyDialog(false)} maxWidth="sm" fullWidth>
                            <DialogTitle>Pagar Mora {hasFrozenPeriods ? 'Congelada' : 'Acumulada'}</DialogTitle>
                            <DialogContent>
                                <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        <strong>Mora {hasFrozenPeriods ? 'Congelada' : 'Acumulada'}:</strong> Q {effectivePenaltyDue.toFixed(2)}
                                    </Typography>
                                    {dialogCredito > 0 && (
                                        <Typography variant="body2" sx={{ color: 'success.main' }}>
                                            <strong>Crédito disponible:</strong> Q {dialogCredito.toFixed(2)}
                                        </Typography>
                                    )}
                                    {hasFrozenPeriods && (
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                            ❄️ {dialogPenaltyPeriods.filter(p => p.penaltyFrozen).length} período(s) con mora congelada
                                        </Typography>
                                    )}
                                    {!hasFrozenPeriods && (
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                            💡 Puedes pagar esta mora por separado antes de pagar la tarifa completa.
                                        </Typography>
                                    )}
                                </Box>
                                {/* Toggle método de pago si hay crédito disponible */}
                                {dialogCredito > 0 && (
                                    <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
                                        <Button
                                            size="small"
                                            variant={!payPenaltyUseCredit ? 'contained' : 'outlined'}
                                            onClick={() => {
                                                setPayPenaltyUseCredit(false);
                                                setPayPenaltyAmount('');
                                            }}
                                            fullWidth
                                        >
                                            Pago con boleta
                                        </Button>
                                        <Button
                                            size="small"
                                            variant={payPenaltyUseCredit ? 'contained' : 'outlined'}
                                            color="success"
                                            onClick={() => {
                                                setPayPenaltyUseCredit(true);
                                                const maxCredit = Math.min(dialogCredito, effectivePenaltyDue);
                                                setPayPenaltyAmount(maxCredit.toFixed(2));
                                            }}
                                            fullWidth
                                        >
                                            Usar crédito (Q {Math.min(dialogCredito, effectivePenaltyDue).toFixed(2)})
                                        </Button>
                                    </Box>
                                )}
                                {(() => {
                                    const creditAmt = Number(payPenaltyAmount || 0);
                                    const cashRemainder = payPenaltyUseCredit
                                        ? Math.max(0, Math.round((effectivePenaltyDue - creditAmt) * 100) / 100)
                                        : 0;
                                    const showBoletaFields = !payPenaltyUseCredit || cashRemainder > 0;
                                    return (
                                        <>
                                            <TextField
                                                fullWidth
                                                label={payPenaltyUseCredit ? 'Crédito a aplicar (Q)' : 'Monto a Pagar (Q)'}
                                                type="number"
                                                value={payPenaltyAmount}
                                                onChange={(e) => setPayPenaltyAmount(e.target.value)}
                                                inputProps={{ min: 0.01, step: '0.01', max: payPenaltyUseCredit ? dialogCredito : effectivePenaltyDue }}
                                                helperText={payPenaltyUseCredit ? `Máximo del crédito: Q ${dialogCredito.toFixed(2)}` : undefined}
                                                sx={{ mb: 2 }}
                                            />
                                            {payPenaltyUseCredit && cashRemainder > 0 && (
                                                <Box sx={{ mb: 2, p: 1.5, bgcolor: '#fff3e0', borderRadius: 1, border: '1px solid #ffb74d' }}>
                                                    <Typography variant="body2" sx={{ color: 'warning.dark' }}>
                                                        ⚠️ El crédito no cubre toda la mora. <strong>Restante a pagar con boleta: Q {cashRemainder.toFixed(2)}</strong>
                                                    </Typography>
                                                </Box>
                                            )}
                                            {payPenaltyUseCredit && cashRemainder <= 0 && (
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                                                    ℹ️ El crédito cubre toda la mora. No se requiere boleta.
                                                </Typography>
                                            )}
                                            {showBoletaFields && (
                                                <>
                                                    <TextField
                                                        fullWidth
                                                        label={payPenaltyUseCredit ? `Fecha de pago del restante (Q ${cashRemainder.toFixed(2)})` : 'Fecha de Pago'}
                                                        type="date"
                                                        value={payPenaltyDate}
                                                        onChange={(e) => setPayPenaltyDate(e.target.value)}
                                                        InputLabelProps={{ shrink: true }}
                                                        sx={{ mb: 2 }}
                                                    />
                                                    <TextField
                                                        fullWidth
                                                        label="Número de Boleta"
                                                        value={payPenaltyBoleta}
                                                        onChange={(e) => setPayPenaltyBoleta(e.target.value)}
                                                        sx={{ mb: 2 }}
                                                    />
                                                    <TextField
                                                        fullWidth
                                                        label="Número de Cuenta Bancaria"
                                                        value={payPenaltyAccount}
                                                        onChange={(e) => setPayPenaltyAccount(e.target.value)}
                                                    />
                                                </>
                                            )}
                                        </>
                                    );
                                })()}
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={() => {
                                    setOpenPayPenaltyDialog(false);
                                    setPayPenaltyAmount('');
                                    setPayPenaltyBoleta('');
                                    setPayPenaltyAccount('');
                                    setPayPenaltyDate(moment().format('YYYY-MM-DD'));
                                    setPayPenaltyUseCredit(false);
                                }}>Cancelar</Button>
                                <Button variant="contained" color={payPenaltyUseCredit ? 'success' : 'primary'} onClick={async () => {
                                    const creditAmt = payPenaltyUseCredit ? Number(payPenaltyAmount || 0) : 0;
                                    const cashAmt = payPenaltyUseCredit
                                        ? Math.max(0, Math.round((effectivePenaltyDue - creditAmt) * 100) / 100)
                                        : Number(payPenaltyAmount || 0);
                                    const currentPenalty = Number(registerPaymentTarget?.penaltyDue || 0);

                                    // Validaciones
                                    if (payPenaltyUseCredit) {
                                        if (!creditAmt || Number.isNaN(creditAmt) || creditAmt <= 0) {
                                            setSnackbar({ open: true, message: 'Ingrese un monto de crédito válido', severity: 'error' });
                                            return;
                                        }
                                        if (creditAmt > dialogCredito) {
                                            setSnackbar({ open: true, message: `El crédito a aplicar excede el disponible (Q${dialogCredito.toFixed(2)})`, severity: 'error' });
                                            return;
                                        }
                                        if (creditAmt + cashAmt > currentPenalty + 0.005) {
                                            setSnackbar({ open: true, message: `El total excede la mora adeudada (Q${currentPenalty.toFixed(2)})`, severity: 'error' });
                                            return;
                                        }
                                    } else {
                                        if (!cashAmt || Number.isNaN(cashAmt) || cashAmt <= 0) {
                                            setSnackbar({ open: true, message: 'Ingrese un monto válido', severity: 'error' });
                                            return;
                                        }
                                        if (cashAmt > currentPenalty) {
                                            setSnackbar({ open: true, message: `El monto excede la mora adeudada (Q${currentPenalty.toFixed(2)})`, severity: 'error' });
                                            return;
                                        }
                                    }

                                    try {
                                        if (payPenaltyUseCredit) {
                                            // 1. Aplicar crédito a mora
                                            await api.post('/payments/v2/use-credit', {
                                                paymentId: registerPaymentTarget.id,
                                                amount: creditAmt,
                                                targetType: 'PENALTY'
                                            });
                                            // 2. Si hay restante, pagar con boleta
                                            if (cashAmt > 0) {
                                                await api.post(`/payments/${registerPaymentTarget.id}/pay-penalty`, {
                                                    amountPaid: cashAmt,
                                                    receiptNumber: payPenaltyBoleta,
                                                    bankAccountNumber: payPenaltyAccount,
                                                    paidAt: payPenaltyDate
                                                });
                                                setSnackbar({ open: true, message: `Crédito (Q${creditAmt.toFixed(2)}) y boleta (Q${cashAmt.toFixed(2)}) aplicados a mora`, severity: 'success' });
                                            } else {
                                                setSnackbar({ open: true, message: 'Crédito aplicado a mora exitosamente', severity: 'success' });
                                            }
                                        } else {
                                            // Pago en efectivo/boleta
                                            await api.post(`/payments/${registerPaymentTarget.id}/pay-penalty`, {
                                                amountPaid: cashAmt,
                                                receiptNumber: payPenaltyBoleta,
                                                bankAccountNumber: payPenaltyAccount,
                                                paidAt: payPenaltyDate
                                            });
                                            setSnackbar({ open: true, message: 'Pago de mora registrado exitosamente', severity: 'success' });
                                        }
                                        setOpenPayPenaltyDialog(false);
                                        setPayPenaltyAmount('');
                                        setPayPenaltyBoleta('');
                                        setPayPenaltyAccount('');
                                        setPayPenaltyDate(moment().format('YYYY-MM-DD'));
                                        setPayPenaltyUseCredit(false);
                                        // Refrescar datos
                                        await fetchAllPayments(statusFilter, search);
                                        // Actualizar el registerPaymentTarget con los datos frescos
                                        const updated = await api.get(`/payments/${registerPaymentTarget.id}`);
                                        if (updated.data) {
                                            setRegisterPaymentTarget(updated.data);
                                        }
                                    } catch (err) {
                                        console.error('Error pagando mora:', err);
                                        console.error('Error response:', err.response?.data);
                                        const errorMsg = err.response?.data?.error || err.response?.data?.message || 'Error al registrar pago de mora';
                                        setSnackbar({ open: true, message: errorMsg, severity: 'error' });
                                    }
                                }}>{payPenaltyUseCredit && Math.max(0, Math.round((effectivePenaltyDue - Number(payPenaltyAmount || 0)) * 100) / 100) > 0 ? 'Aplicar Crédito + Boleta' : payPenaltyUseCredit ? 'Aplicar Crédito' : 'Registrar Pago'}</Button>
                            </DialogActions>
                        </Dialog>

                        {/* Dialog: Descontar/Exonerar Mora Congelada */}
                        <Dialog open={openDiscountPenaltyDialog} onClose={() => setOpenDiscountPenaltyDialog(false)} maxWidth="sm" fullWidth>
                            <DialogTitle>Descontar / Exonerar Mora {hasFrozenPeriods ? 'Congelada' : 'Acumulada'}</DialogTitle>
                            <DialogContent>
                                <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        <strong>Mora {hasFrozenPeriods ? 'Congelada' : 'Acumulada'}:</strong> Q {effectivePenaltyDue.toFixed(2)}
                                    </Typography>
                                    {hasFrozenPeriods && (
                                        <Typography variant="caption" color="text.secondary">
                                            ❄️ {dialogPenaltyPeriods.filter(p => p.penaltyFrozen).length} período(s) con mora congelada
                                        </Typography>
                                    )}
                                </Box>
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>Tipo de Operación</Typography>
                                    <Box sx={{ display: 'flex', gap: 2 }}>
                                        <Button 
                                            variant={discountPenaltyType === 'DISCOUNT' ? 'contained' : 'outlined'}
                                            onClick={() => setDiscountPenaltyType('DISCOUNT')}
                                            fullWidth
                                        >
                                            Descuento Parcial
                                        </Button>
                                        <Button 
                                            variant={discountPenaltyType === 'EXONERATION' ? 'contained' : 'outlined'}
                                            onClick={() => setDiscountPenaltyType('EXONERATION')}
                                            fullWidth
                                        >
                                            Exoneración Total
                                        </Button>
                                    </Box>
                                </Box>
                                <TextField 
                                    fullWidth 
                                    label="Monto a Descontar/Exonerar (Q)" 
                                    type="number" 
                                    value={discountPenaltyAmount} 
                                    onChange={(e) => setDiscountPenaltyAmount(e.target.value)}
                                    sx={{ mb: 2 }}
                                />
                                <TextField 
                                    fullWidth 
                                    label="Notas" 
                                    multiline
                                    rows={3}
                                    value={discountPenaltyNotes} 
                                    onChange={(e) => setDiscountPenaltyNotes(e.target.value)}
                                    placeholder="Razón del descuento o exoneración..."
                                />
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={() => {
                                    setOpenDiscountPenaltyDialog(false);
                                    setDiscountPenaltyAmount('');
                                    setDiscountPenaltyType('DISCOUNT');
                                    setDiscountPenaltyNotes('');
                                }}>Cancelar</Button>
                                <Button variant="contained" color="warning" onClick={async () => {
                                    const amt = Number(discountPenaltyAmount || 0);
                                    if (!amt || Number.isNaN(amt) || amt <= 0) {
                                        setSnackbar({ open: true, message: 'Ingrese un monto válido', severity: 'error' });
                                        return;
                                    }
                                    const currentPenalty = (registerPaymentTarget?.frozenPenalty || 0) > 0 
                                        ? registerPaymentTarget?.frozenPenalty 
                                        : registerPaymentTarget?.accumulatedPenalty || 0;
                                    if (amt > currentPenalty) {
                                        setSnackbar({ open: true, message: `El monto excede la mora (Q${currentPenalty.toFixed(2)})`, severity: 'error' });
                                        return;
                                    }
                                    try {
                                        // Determinar si es mora congelada o acumulada
                                        const isFrozen = (registerPaymentTarget?.frozenPenalty || 0) > 0;
                                        
                                        if (isFrozen) {
                                            // Usar endpoint de exoneración de mora congelada
                                            await api.post(`/payments/${registerPaymentTarget.id}/exonerate-penalty`, {
                                                discountAmount: amt,
                                                type: discountPenaltyType,
                                                notes: discountPenaltyNotes
                                            });
                                        } else {
                                            // Usar endpoint específico para exonerar mora acumulada
                                            await api.post(`/payments/${registerPaymentTarget.id}/exonerate-accumulated-penalty`, {
                                                discountAmount: amt,
                                                type: discountPenaltyType,
                                                notes: discountPenaltyNotes
                                            });
                                        }
                                        const message = discountPenaltyType === 'EXONERATION' ? 'Mora exonerada exitosamente' : 'Descuento aplicado a mora';
                                        setSnackbar({ open: true, message, severity: 'success' });
                                        setOpenDiscountPenaltyDialog(false);
                                        setDiscountPenaltyAmount('');
                                        setDiscountPenaltyType('DISCOUNT');
                                        setDiscountPenaltyNotes('');
                                        // Refrescar datos
                                        await fetchAllPayments(statusFilter, search);
                                        // Actualizar el registerPaymentTarget con los datos frescos
                                        const updated = await api.get(`/payments/${registerPaymentTarget.id}`);
                                        if (updated.data) {
                                            setRegisterPaymentTarget(updated.data);
                                        }
                                    } catch (err) {
                                        console.error('Error descatando/exonerando mora:', err);
                                        setSnackbar({ open: true, message: 'Error al descontar/exonerar mora', severity: 'error' });
                                    }
                                }}>{discountPenaltyType === 'EXONERATION' ? 'Exonerar' : 'Aplicar Descuento'}</Button>
                            </DialogActions>
                        </Dialog>

                        {/* Dialog: Preview de Boleta (triggered from notification) */}
                        <Dialog open={!!previewReceipt} onClose={() => setPreviewReceipt(null)} fullWidth maxWidth="md">
                            <DialogTitle>Vista previa de Boleta</DialogTitle>
                            <DialogContent>
                                {previewReceipt ? (
                                    previewReceipt.fileUrl ? (
                                        (previewReceipt.fileUrl.match(/(\.png|\.jpe?g|\.gif|\.webp|\.bmp)(\?|$)/i)) ? (
                                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                                <img src={previewReceipt.fileUrl} alt="boleta" style={{ maxWidth: '100%', maxHeight: '60vh' }} />
                                            </Box>
                                        ) : (
                                            <Box>
                                                <Button variant="outlined" href={previewReceipt.fileUrl} target="_blank" rel="noreferrer">Abrir archivo</Button>
                                            </Box>
                                        )
                                    ) : (
                                        <Typography variant="body2">No se encontró la imagen de la boleta.</Typography>
                                    )
                                ) : null}
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={() => setPreviewReceipt(null)}>Cerrar</Button>
                                <Button variant="contained" onClick={() => {
                                    const navPayment = location.state && location.state.payment ? location.state.payment : null;
                                    const localPreview = previewReceipt;
                                    setPreviewReceipt(null);
                                    if (navPayment) {
                                        const key = `navPaymentButton:${location.search || navPayment?.id || navPayment?.User?.id || ''}`;
                                        if (!processedOpenRegisterRef.current.has(key)) {
                                            processedOpenRegisterRef.current.add(key);
                                            handleOpenRegister(navPayment);
                                        }
                                    } else if (localPreview) {
                                        (async () => {
                                            try {
                                                const uid = localPreview.userId || new URLSearchParams(location.search).get('userId');
                                                if (uid) {
                                                    const res = await api.get('/payments', { params: buildPaymentParams({ userId: uid, page: 1, limit: 1 }) });
                                                    const arr = res.data.payments || res.data.rows || [];
                                                    const first = arr[0];
                                                    if (first) {
                                                        const key = `previewFallback:${location.search || uid}`;
                                                        if (!processedOpenRegisterRef.current.has(key)) {
                                                            processedOpenRegisterRef.current.add(key);
                                                            handleOpenRegister(first);
                                                        }
                                                    }
                                                }
                                            } catch (e) {
                                                console.error('Error opening register from preview fallback', e);
                                            }
                                        })();
                                    }
                                }}>Registrar Pago</Button>
                            </DialogActions>
                        </Dialog>

                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                            <TablePagination
                                component="div"
                                count={paginationCount}
                                page={page}
                                onPageChange={(e, newPage) => {
                                    // local pagination: just change page, data is already loaded client-side
                                    setPage(newPage);
                                }}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={(e) => {
                                    const rp = parseInt(e.target.value, 10);
                                    setRowsPerPage(rp);
                                    setPage(0);
                                }}
                                rowsPerPageOptions={[10,25,50]}
                            />
                        </Box>
                    </Paper>
                </Grid>
            </Grid>

            <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>{snackbar.message}</Alert>
            </Snackbar>
        </PageContainer>
    );
};

export default React.memo(SchoolPaymentsPage);
